import Phaser from "phaser";
import { avatarContents } from "../ui/avatar";

import { ARENA_ART, ARENA_PRELOAD_ASSETS, FACET_QUARTER_ART, PREMIUM_SHOP_ART } from "../config/artAssets";
import { arenaShotTiming } from "../config/arenaVisualConfig";
import { GAME_CONFIG } from "../config/gameConfig";
import { NullGameAnalytics, type GameAnalyticsEvent, type GameAnalyticsPort } from "../domain/analytics";
import { energyFillPercent, secondsUntilNextEnergy } from "../domain/energy";
import { energyPresentation } from "../domain/energyPresentation";
import { tapBoostAt } from "../domain/tapBoost";
import { renderTapBoostStatus } from "../ui/tapBoost";
import { isGameServiceError } from "../domain/errors";
import { browserDiagnosticsContext, type SeasonLeaderboard, type SupportReceipt, type SupportInbox, type SupportThread } from "../domain/community";
import { communityCopy, renderLeaderboard, renderSupport, renderSupportInbox, renderSupportThread } from "../ui/communitySheets";
import { amount, cloneState, multiplyAmount } from "../domain/gameMath";
import { dailyRouteReward, dailyTaskProgress, premiumOfferAvailability } from "../domain/liveOps";
import { isOnboardingActive } from "../domain/onboarding";
import type { GameServicePort } from "../domain/ports";
import { DEV_TOOLS_BUILD_ENABLED, RELIC_DUEL_PREVIEW_ENABLED, GRIM_TREASURY_PREVIEW_ENABLED } from "../config/buildFeatures";
import { TreasuryController } from "../ui/TreasuryController";
import { RewardTransferPanel } from "../ui/RewardTransferPanel";
import { installMarketNativeBack } from "../ui/marketNativeBack";
import { PreviewTreasuryService } from "../services/PreviewTreasuryService";
import { LiveTreasuryService } from "../services/LiveTreasuryService";
import type { TreasuryPort, TreasuryTab } from "../domain/treasury";
import { patchEnergySheet, patchUpgradeConfirmation } from "../ui/patchEnergySheet";
import { waitForTapDelivery, TapDeliveryStalledError } from "../domain/tapDeliveryBarrier";
import type { SpendFeedback } from "../ui/spendFeedback";
import {
  achievementProgress,
  dailyGoalsCompletedCount,
  getNextUpgradeLevel,
  readyRewardSummary,
} from "../domain/progression";
import type {
  DevScenarioId,
  GameState,
  LoadResult,
  Locale,
  ProductionUpgradeCode,
  ProgressSegment,
  SectionId,
  ServiceEnvelope,
  AchievementId,
  ArenaGameId,
  CollectibleId,
  DiamondSkinId,
  OnboardingAction,
  OnboardingStep,
  PremiumOfferId,
  SeasonTaskCode,
  UpgradeCode,
  UserSettings,
} from "../domain/types";
import { createTranslator, type TranslationKey, type Translator } from "../i18n/locales";
import {
  offlineRewardAssetWarmup,
  type OfflineRewardWarmupSnapshot,
} from "../phaser/OfflineRewardAssetWarmup";
import { createOfflineRewardGame, type OfflineRewardRuntime } from "../phaser/createOfflineRewardGame";
import {
  createEnergyMechanismGame,
  type EnergyMechanismRuntime,
} from "../phaser/createEnergyMechanismGame";
import { createWorkshopGame, type WorkshopRuntime } from "../phaser/createWorkshopGame";
import {
  createTrophyHallGame,
  type TrophyHallRuntime,
} from "../phaser/createTrophyHallGame";
import type { TrophyCelebration, TrophyHallVisualState } from "../phaser/TrophyHallScene";
import {
  createPremiumShopGame,
  type PremiumShopRuntime,
} from "../phaser/createPremiumShopGame";
import {
  createRewardVaultGame,
  type RewardVaultRuntime,
} from "../phaser/createRewardVaultGame";
import {
  premiumRelicForOfferKind,
  type PremiumShopRelic,
  type PremiumShopVisualState,
} from "../phaser/PremiumShopScene";
import {
  createMakersClubGame,
  type MakersClubRuntime,
} from "../phaser/createMakersClubGame";
import type { MakersClubCircle, MakersClubVisualState } from "../phaser/MakersClubScene";
import {
  createArenaCatalogGame,
  type ArenaCatalogRuntime,
} from "../phaser/createArenaCatalogGame";
import {
  createPenaltyArenaGame,
  warmPenaltyArenaAssets,
  type PenaltyArenaRuntime,
} from "../phaser/createPenaltyArenaGame";
import {
  createRelicDuelGame,
  type RelicDuelRuntime,
} from "../phaser/createRelicDuelGame";
import {
  createOnboardingGuideGame,
  type OnboardingGuideRuntime,
} from "../phaser/createOnboardingGuideGame";
import type { GuidePose } from "../phaser/OnboardingGuideScene";
import {
  createOnboardingPointerGame,
  type OnboardingPointerRuntime,
} from "../phaser/createOnboardingPointerGame";
import {
  createKingdomLoadingGame,
  type KingdomLoadingRuntime,
} from "../phaser/createKingdomLoadingGame";
import type { OnboardingPointerDirection } from "../phaser/OnboardingPointerScene";
import { commandId } from "../ui/commandId";
import { MarketController } from "../ui/MarketController";
import { renderProfilePreferences } from "../ui/profileTemplate";
import { createMarketGame, type MarketRuntime } from "../phaser/createMarketGame";
import { copyText } from "../ui/clipboard";
import {
  formatAmount,
  formatCurrencyAmount,
  formatCurrencyUnit,
  formatPassiveRate,
  formatProductionSpeed,
  formatSeasonCountdown,
  formatServerDuration,
  formatSharePpm,
  formatUsdtCents,
  getSeasonCountdownTarget,
  getSeasonCountdownParts,
} from "../ui/formatters";
import { Haptics } from "../ui/haptics";
import { currencyIcon, seasonIcon } from "../ui/icons";
import { onboardingRouteForStep } from "../ui/onboardingRoute";
import { renderQuarterMap } from "../ui/quarterMap";
import { onboardingUiModel, renderOnboardingLayer } from "../ui/onboardingTemplate";
import { seasonHeaderPresentation } from "../ui/seasonPresentation";
import {
  renderArenaRulesV2,
  renderBalanceV2,
  renderDevelopmentV2,
  renderEnergyV2,
  formatDailyResetLabel,
  dailyRemainingRewardHint,
  renderDailyStreakV2,
  renderFriendRosterV2,
  renderFriendsV2,
  renderOfflineRewardV2,
  renderPremiumShopContentV2,
  renderPremiumShopV2,
  renderProfileV2,
  renderRewardAccountV2,
  renderTonUsdtPrizeInfoV2,
  renderProgressV2,
  renderRewardRevealV2,
  renderCollectibleV2,
  renderSectionHelpV2,
  renderUpgradeConfirmationV2,
  renderUpgradeV2,
  type AchievementFilter,
  type FriendFilter,
} from "../ui/sectionTemplates";
import {
  escapeHtml,
  renderDevPanel,
  renderGames,
  type ArenaView,
} from "../ui/templates";

type SheetId =
  | "partner-transfer"
  | "profile"
  | "preferences"
  | "balance"
  | "upgrade"
  | "upgrade-confirm"
  | "energy"
  | "section-help"
  | "arena-rules"
  | "offline-reward"
  | "premium-shop"
  | "reward-account"
  | "reward-reveal"
  | "collectible"
  | "daily-streak"
  | "ton-usdt-prizes"
  | "leaderboard"
  | "support";
type ToastKind = "info" | "success" | "error";
type ToastMotif = "default" | "currency" | "mechanical" | "precision" | "energy";
type ToastOptions = { body?: string; motif?: ToastMotif };

const LOADING_STATUS_KEYS = [
  "loading.kingdom",
  "loading.diamond",
  "loading.workshop",
  "loading.trophies",
  "loading.arena",
] as const satisfies readonly TranslationKey[];

// Mobile WebViews may dispatch a compatibility mouse event after a touch that
// closes a sheet. Keep the workshop locked until that gesture has fully ended.
const SHEET_TAP_RELEASE_DELAY_MS = 360;

function projectionRevealTime(state: GameState, serverNow: string, t: Translator): string {
  return state.season.projection.revealsAt === null
    ? t("common.soon")
    : formatServerDuration(state.season.projection.revealsAt, serverNow, state.settings.locale);
}

function projectionRevealTimeShort(state: GameState, serverNow: string, t: Translator): string {
  if (state.season.projection.revealsAt === null) return t("common.soon");
  const seconds = Math.max(
    0,
    Math.ceil(
      (new Date(state.season.projection.revealsAt).getTime() - new Date(serverNow).getTime()) / 1_000,
    ),
  );
  if (seconds >= 86_400) return `${Math.ceil(seconds / 86_400)} ${state.settings.locale === "ru" ? "дн." : "d"}`;
  if (seconds >= 3_600) return `${Math.ceil(seconds / 3_600)} ${state.settings.locale === "ru" ? "ч." : "h"}`;
  return `${Math.max(1, Math.ceil(seconds / 60))} ${state.settings.locale === "ru" ? "мин." : "min"}`;
}

function browserEngineVersion(): string {
  const major = /\b(?:Chrome|CriOS)\/(\d{1,4})\b/.exec(navigator.userAgent)?.[1];
  return major === undefined ? "unknown" : `chromium-${major}`;
}

const detailNavigation: Record<Exclude<SectionId, "core">, { title: TranslationKey }> = {
  development: { title: "nav.development" },
  progress: { title: "nav.progress" },
  friends: { title: "nav.friends" },
  games: { title: "nav.games" },
};

type OnboardingFocusKind = "asset" | "content" | "landmark" | "panel";

type OnboardingTargetSpec = {
  actionSelector: string;
  focusSelector?: string;
  focusKind: OnboardingFocusKind;
  pointer: OnboardingPointerDirection | null;
  interactive?: boolean;
};

type OnboardingGuideSide = "left" | "right" | "center";

type OnboardingGuideStaging = {
  side: OnboardingGuideSide;
  mirror: boolean;
};

const ONBOARDING_TARGETS: Partial<Record<OnboardingStep, OnboardingTargetSpec>> = {
  tap: {
    actionSelector: ".keyboard-tap-target",
    focusKind: "asset",
    pointer: null,
  },
  map: {
    actionSelector: ".quarter-portal",
    focusKind: "content",
    pointer: "down",
  },
  workshop: {
    actionSelector: ".quarter-landmark[data-section='development']",
    focusKind: "landmark",
    pointer: "down",
  },
  upgrade: {
    actionSelector: "[data-testid='buy-cutter']",
    focusSelector: ".machine-module--mechanical",
    focusKind: "panel",
    pointer: "up",
  },
  trophy: {
    actionSelector: ".quarter-landmark[data-section='progress']",
    focusKind: "landmark",
    pointer: "down",
  },
  season: {
    actionSelector: "#progress-tab-season",
    focusSelector: ".trophy-hall-portals",
    focusKind: "content",
    pointer: "down",
  },
  "season-return": {
    actionSelector: ".map-return",
    focusKind: "content",
    pointer: "up",
  },
  club: {
    actionSelector: ".quarter-landmark[data-section='friends']",
    focusKind: "landmark",
    pointer: "down",
  },
  "club-link": {
    actionSelector: ".makers-club-gate [data-action='invite-friend'][data-mode='copy']",
    focusSelector: ".makers-club-gate",
    focusKind: "panel",
    pointer: "up",
  },
  "club-return": {
    actionSelector: ".map-return",
    focusKind: "content",
    pointer: "up",
  },
  arena: {
    actionSelector: ".quarter-landmark[data-section='games']",
    focusKind: "landmark",
    pointer: "down",
  },
  "arena-game": {
    actionSelector: ".arena-game-card.is-playable",
    focusKind: "content",
    pointer: "down",
    interactive: false,
  },
};

const ONBOARDING_GUIDE_STAGING: Record<OnboardingStep, OnboardingGuideStaging> = {
  welcome: { side: "center", mirror: false },
  tap: { side: "left", mirror: false },
  map: { side: "left", mirror: false },
  workshop: { side: "right", mirror: true },
  upgrade: { side: "left", mirror: false },
  passive: { side: "right", mirror: false },
  trophy: { side: "left", mirror: true },
  "trophy-overview": { side: "right", mirror: false },
  season: { side: "left", mirror: false },
  "season-overview": { side: "right", mirror: false },
  "season-return": { side: "right", mirror: false },
  club: { side: "right", mirror: false },
  "club-overview": { side: "right", mirror: false },
  "club-link": { side: "left", mirror: true },
  "club-return": { side: "right", mirror: true },
  arena: { side: "left", mirror: true },
  "arena-overview": { side: "right", mirror: true },
  "arena-game": { side: "left", mirror: true },
  "arena-return": { side: "center", mirror: false },
  complete: { side: "center", mirror: false },
};

const SMALL_TARGET_GUIDE_STEPS = new Set<OnboardingStep>([
  "season",
  "season-return",
  "club-link",
  "club-return",
  "arena-game",
]);

type ArenaResultExit = {
  destination: "catalog" | "replay";
  replayStake: string | null;
  lastDismissedResultKey: string | null;
  dismissCount: number;
};

export type DiamondTapperDebug = {
  isReady(): boolean;
  getState(): GameState | null;
  getRenderer(): "canvas" | "webgl" | "unknown";
  flushTaps(): Promise<void>;
  navigate(section: SectionId): void;
  seedScenario(scenario: DevScenarioId): Promise<void>;
  setOnline(online: boolean): Promise<void>;
  getArenaSnapshot(): Record<string, unknown> | null;
  getOnboardingSnapshot(): Record<string, unknown> | null;
  getOnboardingPointerSnapshot(): Record<string, unknown> | null;
  getPremiumShopSnapshot(): Record<string, unknown> | null;
  getMakersClubSnapshot(): Record<string, unknown> | null;
  getTrophySnapshot(): Record<string, unknown> | null;
  getWorkshopSnapshot(): Record<string, unknown> | null;
  getAnalyticsSnapshot(): readonly GameAnalyticsEvent[];
  previewTrophyDailyState(count: 0 | 1 | 2 | 3): void;
};

declare global {
  interface Window {
    __DIAMOND_TAPPER__?: DiamondTapperDebug;
  }
}

export class GameApp {
  readonly #root: HTMLElement;
  readonly #service: GameServicePort;
  readonly #analytics: GameAnalyticsPort;
  readonly #haptics = new Haptics();
  readonly #devEnabled: boolean;
  #state: GameState | null = null;
  #serverNow = new Date(0).toISOString();
  #translator: Translator = createTranslator("ru");
  #activeSection: SectionId = "core";
  #mapOpen = false;
  #marketOpen = false;
  #progressSegment: ProgressSegment = "today";
  #achievementFilter: AchievementFilter = "all";
  #friendFilter: FriendFilter = "all";
  #friendQuery = "";
  #friendPage = 1;
  #clubRosterRequestSequence = 0;
  #clubRosterSearchTimer = 0;
  #selectedUpgrade: ProductionUpgradeCode = "mechanicalCutter";
  #pendingUpgradeCode: UpgradeCode | null = null;
  #pendingUpgradeCommandId: string | null = null;
  #sheet: SheetId | null = null;
  #profileNativeBack: (() => void) | null = null;
  #marketController: MarketController | null = null;
  #treasuryController: TreasuryController | null = null;
  #treasuryService: TreasuryPort | null = null;
  #profileMarketRuntime: MarketRuntime | null = null;
  #profileRenderKey = "";
  #runtime: WorkshopRuntime | null = null;
  #energyRuntimes: EnergyMechanismRuntime[] = [];
  #energySigilRuntime: EnergyMechanismRuntime | null = null;
  #energyMountSequence = 0;
  #trophyRuntime: TrophyHallRuntime | null = null;
  #trophyMountSequence = 0;
  #trophyResizeObserver: ResizeObserver | null = null;
  #pendingTrophyCelebration: TrophyCelebration | null = null;
  #trophyPreviewCount: 0 | 1 | 2 | 3 | null = null;
  #rewardRevealRuntime: TrophyHallRuntime | null = null;
  #rewardRevealResizeObserver: ResizeObserver | null = null;
  #rewardRevealMountSequence = 0;
  #rewardRevealAchievement: AchievementId | null = null;
  #premiumRuntime: PremiumShopRuntime | null = null;
  #premiumResizeObserver: ResizeObserver | null = null;
  #premiumMountSequence = 0;
  #premiumRelic: PremiumShopRelic = "reserve";
  #premiumPreviewSkinId: DiamondSkinId | null = null;
  #rewardVaultRuntime: RewardVaultRuntime | null = null;
  #rewardVaultResizeObserver: ResizeObserver | null = null;
  #rewardVaultMountSequence = 0;
  #selectedCollectible: CollectibleId | null = null;
  #makersClubRuntime: MakersClubRuntime | null = null;
  #rewardTransferPanel: RewardTransferPanel | null = null;
  #transferReturnToRewards = false;
  #makersClubMountSequence = 0;
  #makersClubResizeObserver: ResizeObserver | null = null;
  #arenaView: ArenaView = "catalog";
  #arenaCatalogRuntime: ArenaCatalogRuntime | null = null;
  #arenaCatalogMountSequence = 0;
  #arenaCatalogResizeObserver: ResizeObserver | null = null;
  #arenaRuntime: PenaltyArenaRuntime | null = null;
  #relicDuelRuntime: RelicDuelRuntime | null = null;
  #relicDuelMountSequence = 0;
  #relicDuelResizeObserver: ResizeObserver | null = null;
  #arenaMountSequence = 0;
  #arenaClockFrame = 0;
  #arenaLastFrameAt = 0;
  #arenaRevealTimer = 0;
  #arenaImpactHoldTimer = 0;
  #reportedVisualIssues = new Set<string>();
  #serverAnchorPerformance = 0;
  #onboardingRuntime: OnboardingGuideRuntime | null = null;
  #onboardingResizeObserver: ResizeObserver | null = null;
  #onboardingPointerRuntime: OnboardingPointerRuntime | null = null;
  #onboardingPointerResizeObserver: ResizeObserver | null = null;
  #onboardingBusy = false;
  #onboardingTapConfirmationKey: string | null = null;
  #onboardingCompletionCelebrated = false;
  #onboardingPositionFrame = 0;
  #onboardingScrollTimer = 0;
  #onboardingFocusClone!: HTMLElement;
  #offlineRuntime: OfflineRewardRuntime | null = null;
  #offlineWarmupUnsubscribe: (() => void) | null = null;
  #offlineLaunchSequence = 0;
  #offlineReward: { gain: string; seconds: number } | null = null;
  #offlineCollecting = false;
  #loadingRuntime: KingdomLoadingRuntime | null = null;
  #loadingStatusTimer = 0;
  #loadingCleanupTimer = 0;
  #loadingStatusIndex = 0;
  #ready = false;
  #destroyed = false;
  #upgradeBusy = false;
  #spendWait: (SpendFeedback & { controller: AbortController }) | null = null;
  #rewardClaimBusy = false;
  readonly #rewardButtonStates = new WeakMap<HTMLButtonElement, boolean>();
  #pvpBusy = false;
  #arenaResultExit: ArenaResultExit | null = null;
  #arenaResultExitTimer = 0;
  #queuedPvpZone: number | null = null;
  #tapBatchCount = 0;
  #tapBatchCommandId = "";
  #tapBatchReward = 0n;
  #tapBatchTimer: number | null = null;
  #sheetTapReleaseTimer = 0;
  #workshopTapBlockedUntil = 0;
  #tapRequests = new Set<Promise<void>>();
  #pendingTapCount = 0;
  #networkRecoveryRunning = false;
  #networkRetryAt = 0;
  #pendingTapReward = 0n;
  #servicePendingTapCount = 0;
  #servicePendingTapReward = 0n;
  #displayedBalance = 0n;
  #balanceAnimation = 0;
  #syncTimer = 0;
  #toastTimer = 0;
  #mapTransitionTimer = 0;
  #viewTransitionSequence = 0;
  #workshopResizeFrame = 0;
  #workshopResizeObserver: ResizeObserver | null = null;
  #workshopWidth = 0;
  #workshopHeight = 0;
  #focusBeforeSheet: HTMLElement | null = null;
  #lastInteractionWasKeyboard = false;
  #sheetPointerY: number | null = null;
  #detailTouchY: number | null = null;
  #premiumShopTouchX: number | null = null;
  #premiumShopTouchY: number | null = null;
  #premiumShopTouchStartY: number | null = null;
  #premiumShopTouchMoved = false;
  #premiumShopSuppressClicksUntil = 0;
  #devCollapsed = false;
  #unsubscribeService: (() => void) | null = null;
  #lastOnboardingViewKey: string | null = null;
  #trackedArenaMatchId: string | null = null;
  #leaderboard: SeasonLeaderboard | null = null;
  #leaderboardLoading = false;
  #leaderboardError = false;
  #supportDraft = "";
  #supportRequestId = "";
  #supportReceipt: SupportReceipt | null = null;
  #supportBusy = false;
  #supportError = false;
  #supportView: "inbox" | "compose" | "thread" = "inbox";
  #supportInbox: SupportInbox | null = null;
  #supportThread: SupportThread | null = null;
  #supportReference = "";
  #supportLoading = false;
  #supportRefreshQueued = false;
  #supportLoadSequence = 0;
  #supportWatchStop: (() => void) | null = null;
  #transferBalanceWatchStop: (() => void) | null = null;
  #supportDrafts = new Map<string, string>();
  #pendingSupportReference = "";

  #shell!: HTMLElement;
  #coreScreen!: HTMLElement;
  #mapScreen!: HTMLElement;
  #marketScreen!: HTMLElement;
  #detailScreen!: HTMLElement;
  #balanceButton!: HTMLButtonElement;
  #balanceValue!: HTMLElement;
  #balanceEstimate!: HTMLElement;
  #balanceEstimateLabel!: HTMLElement;
  #passiveValue!: HTMLElement;
  #seasonValue!: HTMLElement;
  #seasonPrimaryValue!: HTMLElement;
  #seasonLabel!: HTMLElement;
  #reservedValue!: HTMLElement;
  #coreHint!: HTMLElement;
  #networkBanner!: HTMLElement;
  #sheetLayer!: HTMLElement;
  #sheetContent!: HTMLElement;
  #toast!: HTMLElement;
  #toastTitle!: HTMLElement;
  #toastBody!: HTMLElement;
  #liveRegion!: HTMLElement;
  #onboardingLayer!: HTMLElement;

  constructor(options: {
    root: HTMLElement;
    service: GameServicePort;
    analytics?: GameAnalyticsPort;
    enableDevTools?: boolean;
  }) {
    this.#root = options.root;
    this.#service = options.service;
    this.#analytics = options.analytics ?? new NullGameAnalytics();
    this.#devEnabled = DEV_TOOLS_BUILD_ENABLED && options.enableDevTools === true;
  }

  async start(): Promise<void> {
    if (this.#destroyed) return;
    this.#renderLoading();
    try {
      const loadGame = this.#service.load().then((result) => {
        const scenario = new URLSearchParams(window.location.search).get("scenario");
        if (
          (result.offlineGain !== "0" && !isOnboardingActive(result.state.onboarding)) ||
          (DEV_TOOLS_BUILD_ENABLED && this.#devEnabled && scenario === "offline-production")
        ) {
          void offlineRewardAssetWarmup.warm();
        }
        return result;
      });
      const [loadResult] = await Promise.all([
        loadGame,
        new Promise<void>((resolve) => window.setTimeout(resolve, 360)),
      ]);
      if (this.#destroyed) return;
      await this.#finishStart(loadResult);
    } catch (error) {
      if (this.#destroyed) return;
      console.error("Diamond Tapper failed to start", error);
      this.#renderLoadingError(isGameServiceError(error) && error.code === "SESSION_OPEN");
    }
  }

  destroy(): void {
    this.#rewardTransferPanel?.destroy();
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#spendWait?.controller.abort();
    this.#marketController?.destroy();
    this.#treasuryController?.destroy();
    this.#profileMarketRuntime?.destroy();
    this.#profileNativeBack?.(); this.#profileNativeBack = null;
    this.#disposeLoadingExperience();
    window.clearInterval(this.#syncTimer);
    window.clearTimeout(this.#tapBatchTimer ?? undefined);
    window.clearTimeout(this.#sheetTapReleaseTimer);
    window.clearTimeout(this.#toastTimer);
    window.clearTimeout(this.#mapTransitionTimer);
    window.clearTimeout(this.#arenaRevealTimer);
    window.clearTimeout(this.#arenaImpactHoldTimer);
    window.clearTimeout(this.#arenaResultExitTimer);
    window.clearTimeout(this.#onboardingScrollTimer);
    window.clearTimeout(this.#clubRosterSearchTimer);
    this.#clubRosterRequestSequence += 1;
    this.#viewTransitionSequence += 1;
    window.cancelAnimationFrame(this.#balanceAnimation);
    window.cancelAnimationFrame(this.#workshopResizeFrame);
    window.cancelAnimationFrame(this.#arenaClockFrame);
    window.cancelAnimationFrame(this.#onboardingPositionFrame);
    this.#workshopResizeObserver?.disconnect();
    this.#offlineLaunchSequence += 1;
    this.#offlineWarmupUnsubscribe?.();
    this.#offlineWarmupUnsubscribe = null;
    this.#offlineRuntime?.destroy();
    this.#disposeEnergyRuntime();
    this.#disposeTrophyRuntime();
    this.#disposeRewardRevealRuntime();
    this.#disposePremiumRuntime();
    this.#disposeRewardVaultRuntime();
    this.#disposeMakersClubRuntime();
    this.#disposeArenaCatalogRuntime();
    this.#disposeArenaRuntime();
    this.#disposeRelicDuelRuntime();
    this.#disposeOnboardingRuntime();
    this.#clearOnboardingCutout();
    this.#energySigilRuntime?.destroy();
    this.#energySigilRuntime = null;
    this.#runtime?.game.destroy(true);
    this.#unsubscribeService?.();
    this.#unsubscribeService = null;
    this.#supportWatchStop?.(); this.#supportWatchStop = null;
    this.#transferBalanceWatchStop?.(); this.#transferBalanceWatchStop = null;
    this.#service.destroy();
    void this.#analytics.flush().catch(() => undefined).finally(() => this.#analytics.destroy());
    if (DEV_TOOLS_BUILD_ENABLED) delete window.__DIAMOND_TAPPER__;
  }

  async #finishStart(loadResult: LoadResult): Promise<void> {
    this.#state = loadResult.state;
    this.#serverNow = loadResult.serverNow;
    this.#serverAnchorPerformance = performance.now();
    const pendingTaps = loadResult.pendingTaps ?? this.#service.getPendingTaps();
    this.#pendingTapCount = pendingTaps.count;
    this.#pendingTapReward = BigInt(pendingTaps.reward);
    this.#servicePendingTapCount = pendingTaps.count;
    this.#servicePendingTapReward = BigInt(pendingTaps.reward);
    this.#displayedBalance = BigInt(loadResult.state.account.available) + this.#pendingTapReward;
    this.#translator = createTranslator(loadResult.state.settings.locale);
    this.#haptics.setEnabled(loadResult.state.settings.vibration);
    document.documentElement.lang = loadResult.state.settings.locale;
    if (loadResult.state.season.status === "active") this.#restoreOnboardingContext();

    this.#buildShell();
    this.#mountCoreEnergyRuntime();
    this.#bindEvents();
    const workshopHost = this.#required<HTMLElement>("#workshop-root");
    this.#runtime = createWorkshopGame(
      workshopHost,
      {
        onTap: (point) => this.#handleTap(point.x, point.y, false),
        onEnergyDepleted: () => this.#showEnergyDepleted(),
        onMusicUnavailable: () => this.#showToast(this.#translator("status.musicUnavailable"), "error"),
      },
      {
        tapReward: tapBoostAt(loadResult.state, this.#estimatedServerNow()).reward,
        energyCurrent: loadResult.state.energy.current,
        energyCapacity: loadResult.state.energy.capacity,
        selectedSkinId: loadResult.state.collection.selectedSkinId,
        musicEnabled: loadResult.state.settings.music,
      },
    );
    await this.#runtime.ready;
    this.#observeWorkshopSize(workshopHost);
    this.#syncWorkshop(false);
    this.#updateView({ navigation: true, details: true, sheet: true });
    this.#unsubscribeService = this.#service.subscribe((envelope) => {
      if (this.#destroyed) return;
      if (this.#consumeArenaResultExitEnvelope(envelope)) return;
      // Arena events arrive over WebSocket. Rebuilding the detail screen here
      // destroys the active Phaser scene before playTurn() can present the
      // authoritative shot. Every arena state is already patched in place by
      // #syncArenaPresentation, so preserve the mounted runtime while a
      // penalty screen is open.
      const preserveArenaRuntime =
        this.#activeSection === "games" &&
        ((this.#arenaView === "penalty" && this.#arenaRuntime !== null) ||
          (this.#arenaView === "relic-duel" && this.#relicDuelRuntime !== null));
      const refresh = this.#liveRefreshBoundaries(this.#state, envelope.state);
      this.#applyEnvelope(envelope, {
        animateBalance: true,
        details: !preserveArenaRuntime && refresh.details,
        sheet: refresh.sheet,
      });
    });
    this.#service.resumePendingTaps();

    this.#syncTimer = window.setInterval(() => {
      this.#updateTapBoost();
      void this.#syncConfirmedState();
    }, 1_000);
    this.#ready = true;
    this.#transferBalanceWatchStop = this.#service.transfers?.watch?.(() => {
      this.#rewardTransferPanel?.refresh();
      if (this.#ready && !this.#destroyed) void this.#service.refreshState().catch(() => { /* The next SSE event or normal sync retries. */ });
    }, () => { /* Reconnection is automatic. */ }) ?? null;
    this.#shell.dataset.ready = "true";
    this.#installDebugApi();
    this.#scheduleLoadingDisposal();
    window.dispatchEvent(new Event("diamond-tapper-ready"));
    this.#analytics.track("screen_view", { screen: "core", entry: "bootstrap" });

    const scenario = DEV_TOOLS_BUILD_ENABLED
      ? (new URLSearchParams(window.location.search).get("scenario") as DevScenarioId | null)
      : null;
    const seededScenario = DEV_TOOLS_BUILD_ENABLED && this.#devEnabled && scenario !== null;
    if (seededScenario) await this.#applyScenario(scenario);
    if (
      !seededScenario &&
      loadResult.offlineGain !== "0" &&
      !isOnboardingActive(loadResult.state.onboarding)
    ) {
      this.#showOfflineReward(loadResult.offlineGain, loadResult.offlineSeconds);
    } else {
      this.#syncOnboarding();
      if (loadResult.state.season.status === "active") this.#restoreOnboardingViewport();
    }
    const supportReference = new URLSearchParams(window.location.search).get("support");
    if (supportReference && /^DK-[A-F0-9]{12}$/.test(supportReference)) this.#pendingSupportReference = supportReference;
    this.#consumeSupportLink();
  }

  #renderLoading(): void {
    const t = createTranslator("ru");
    this.#disposeLoadingExperience();
    this.#loadingStatusIndex = 0;
    const splash = this.#ensureLoadingSplash(t);
    const status = t(LOADING_STATUS_KEYS[0]);
    this.#root.innerHTML = `
      <main class="loading-screen" data-testid="loading-state" aria-busy="true">
        <p data-loading-status>${escapeHtml(status)}</p>
      </main>`;
    const host = splash.querySelector<HTMLElement>("[data-loading-phaser]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (host !== null) {
      try {
        const runtime = createKingdomLoadingGame(host, reducedMotion);
        this.#loadingRuntime = runtime;
        void runtime.ready.then(() => {
          if (this.#loadingRuntime === runtime) splash.dataset.phaserReady = "true";
        });
      } catch (error) {
        console.warn("Diamond Kingdom loading scene fell back to its static artwork", error);
      }
    }
    this.#loadingStatusTimer = window.setInterval(() => {
      this.#loadingStatusIndex = (this.#loadingStatusIndex + 1) % LOADING_STATUS_KEYS.length;
      const next = t(LOADING_STATUS_KEYS[this.#loadingStatusIndex] ?? LOADING_STATUS_KEYS[0]);
      document.querySelectorAll<HTMLElement>("[data-loading-status]").forEach((element) => {
        element.textContent = next;
        if (reducedMotion) return;
        element.animate(
          [
            { opacity: 0.32, transform: "translateY(3px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
          { duration: 280, easing: "ease-out" },
        );
      });
    }, 620);
  }

  #renderLoadingError(anotherWindow = false): void {
    const locale = document.documentElement.lang === "en" ? "en" : "ru";
    const t = createTranslator(locale);
    this.#scheduleLoadingDisposal();
    this.#root.innerHTML = `
      <main class="loading-screen loading-screen--error" data-testid="loading-error">
        <div class="loading-core" aria-hidden="true"><i></i></div>
        <h1>${escapeHtml(anotherWindow ? communityCopy(locale).anotherWindow : t("status.actionUnavailable"))}</h1>
        ${anotherWindow ? `<p>${escapeHtml(communityCopy(locale).anotherWindowHint)}</p>` : ""}
        <button class="primary-button" type="button" id="retry-start">${escapeHtml(t("loading.retry"))}</button>
      </main>`;
    this.#root.querySelector("#retry-start")?.addEventListener("click", () => void this.start(), { once: true });
    window.dispatchEvent(new Event("diamond-tapper-ready"));
  }

  #ensureLoadingSplash(t: Translator): HTMLElement {
    const current = document.querySelector<HTMLElement>("#boot-splash");
    if (current !== null) {
      current.classList.remove("is-done");
      current.dataset.phaserReady = "false";
      return current;
    }
    const splash = document.createElement("div");
    splash.id = "boot-splash";
    splash.setAttribute("role", "status");
    splash.setAttribute("aria-label", "Загрузка Diamond Kingdom");
    splash.dataset.phaserReady = "false";
    splash.innerHTML = `<div class="boot-stage"><img src="${escapeHtml(
      FACET_QUARTER_ART.diamond.base.url,
    )}" alt="" width="${FACET_QUARTER_ART.diamond.base.width}" height="${FACET_QUARTER_ART.diamond.base.height}" fetchpriority="high"/><div class="boot-phaser" data-loading-phaser></div></div><strong class="boot-brand">DIAMOND KINGDOM</strong><span class="boot-status" data-loading-status aria-hidden="true">${escapeHtml(
      t(LOADING_STATUS_KEYS[0]),
    )}</span>`;
    document.body.append(splash);
    return splash;
  }

  #scheduleLoadingDisposal(): void {
    window.clearInterval(this.#loadingStatusTimer);
    this.#loadingStatusTimer = 0;
    window.clearTimeout(this.#loadingCleanupTimer);
    const runtime = this.#loadingRuntime;
    this.#loadingCleanupTimer = window.setTimeout(() => {
      if (runtime !== null && this.#loadingRuntime === runtime) {
        runtime.destroy();
        this.#loadingRuntime = null;
      }
      this.#loadingCleanupTimer = 0;
    }, 380);
  }

  #disposeLoadingExperience(): void {
    window.clearInterval(this.#loadingStatusTimer);
    window.clearTimeout(this.#loadingCleanupTimer);
    this.#loadingStatusTimer = 0;
    this.#loadingCleanupTimer = 0;
    this.#loadingRuntime?.destroy();
    this.#loadingRuntime = null;
  }

  #buildShell(): void {
    const t = this.#translator;
    if (this.#state === null) throw new Error("Cannot build the game shell without state");
    const energyFill = energyFillPercent(this.#state.energy.current, this.#state.energy.capacity);
    const projectionVisible = this.#state.season.projection.visible;
    const projectionEstimate = projectionVisible
      ? formatUsdtCents(
          this.#state.season.projection.estimatedRewardUsdCents,
          this.#state.settings.locale,
          false,
        )
      : t("header.estimatePendingValue", {
          time: projectionRevealTimeShort(this.#state, this.#serverNow, t),
        });
    const seasonHeader = seasonHeaderPresentation(this.#state, this.#serverNow, t);
    this.#root.innerHTML = `
      <div class="ambient-world" aria-hidden="true"><i></i><i></i><i></i></div>
      <div class="game-shell" data-section="core" data-view="core" data-stage="${this.#state?.workshopStage ?? 1}" data-network="${
        this.#service.getOnline() ? "online" : "offline"
      }" data-reduced-motion="${this.#state?.settings.reducedMotion ?? false}" data-ready="false">
        <a class="skip-link" href="#game-main">${escapeHtml(t("common.skip"))}</a>
        <div class="network-banner" role="status" hidden>
          <div><strong data-network-title>${escapeHtml(t("status.networkTitle"))}</strong><span data-network-body>${escapeHtml(
            t("status.networkBody"),
          )}</span></div>
        </div>

        <header class="game-header">
          <button class="avatar-button" type="button" data-action="open-profile" aria-label="${escapeHtml(
            t("header.profile"),
          )}"><span class="avatar-frame"><span>A</span></span></button>
          <button class="balance-button" type="button" data-action="open-balance" aria-label="${escapeHtml(
            t("header.balanceDetails"),
          )}"><span class="balance-copy"><span class="currency-glyph">${currencyIcon()}</span><span class="balance-number-stack"><strong class="balance-value">0</strong><small class="balance-estimate${projectionVisible ? "" : " is-pending"}"><i class="balance-estimate__wing balance-estimate__wing--left" aria-hidden="true"></i><span data-balance-estimate>${escapeHtml(
            projectionEstimate,
          )}</span><em data-balance-estimate-label>${escapeHtml(
            t(projectionVisible ? "header.estimateNow" : "header.estimatePending"),
          )}</em><i class="balance-estimate__wing balance-estimate__wing--right" aria-hidden="true"></i></small></span></span></button>
          <button class="season-clock" type="button" data-action="open-season" data-season-mode="${seasonHeader.mode}" aria-label="${escapeHtml(
            seasonHeader.ariaLabel,
          )}"><span class="season-emblem">${seasonIcon()}</span><span class="season-copy"><small>${escapeHtml(
            seasonHeader.label,
          )}</small><strong>${escapeHtml(seasonHeader.value)}</strong><em class="season-countdown">${escapeHtml(
            seasonHeader.countdown,
          )}</em></span></button>
          <button class="premium-gate premium-gate--core" type="button" data-action="open-premium-shop" aria-label="${escapeHtml(
            t("shop.open"),
          )}"><img src="${escapeHtml(PREMIUM_SHOP_ART.seasonSignet.url)}" alt="" width="${PREMIUM_SHOP_ART.seasonSignet.width}" height="${PREMIUM_SHOP_ART.seasonSignet.height}"/><span>${escapeHtml(
            t("shop.openShort"),
          )}</span></button>
        </header>

        <main class="screen-stack" id="game-main" tabindex="-1">
          <section class="core-screen" data-testid="core-screen" aria-label="${escapeHtml(t("nav.core"))}">
            <div class="workshop-stage">
              <div id="workshop-root" aria-hidden="true"></div>
              <button class="keyboard-tap-target" type="button" data-action="keyboard-tap">
                <span class="visually-hidden">${escapeHtml(t("core.tapButton"))}</span>
              </button>
              <p class="tap-hint"><span aria-hidden="true">⌁</span>${escapeHtml(t("core.tapHint"))}</p>
            </div>
            <button class="core-tap-boost" type="button" data-action="open-premium-shop" data-shop-relic="booster" hidden></button>
            <button class="core-energy" type="button" data-action="open-energy" aria-label="${escapeHtml(
              t("energy.open"),
            )}">
              <span class="core-energy__sigil" aria-hidden="true"><span id="core-energy-sigil-root"></span></span>
              <span class="core-energy__copy"><small>${escapeHtml(t("energy.reserve"))}</small><strong><b data-live-energy-current>${formatAmount(
                String(this.#state.energy.current),
                this.#state.settings.locale,
              )}</b><i>/</i><em data-live-energy-capacity>${formatAmount(
                String(this.#state.energy.capacity),
                this.#state.settings.locale,
              )}</em></strong></span>
              <span class="core-energy__meter" role="progressbar" aria-label="${escapeHtml(
                t("energy.reserve"),
              )}" aria-valuemin="0" aria-valuemax="${this.#state.energy.capacity}" aria-valuenow="${this.#state.energy.current}" data-live-energy-meter><i style="--energy-fill:${energyFill}%"></i></span>
              <span class="core-energy__status" data-live-energy-status>${escapeHtml(
                this.#energyStatusText(this.#state),
              )}</span>
            </button>
            <div class="production-line" aria-label="${escapeHtml(t("header.passiveTitle"))}">
              <span class="production-line__sigil" aria-hidden="true">${currencyIcon()}</span>
              <span class="production-line__copy"><small>${escapeHtml(t("header.passiveTitle"))}</small><strong class="passive-value"></strong><em class="reserved-value"></em></span>
            </div>
            <button class="quarter-portal" type="button" data-action="open-map" aria-label="${escapeHtml(
              t("map.open"),
            )}">
              <span class="portal-art" aria-hidden="true"><i></i><i></i><i></i></span>
              <span class="portal-copy"><small>${escapeHtml(t("map.world"))}</small><strong>${escapeHtml(
                t("map.title"),
              )}</strong></span>
              <span class="portal-arrow" aria-hidden="true">↗</span>
              <span class="portal-reward-beacon" aria-hidden="true"><i>!</i><b data-global-reward-count>0</b></span>
            </button>
          </section>

          <section class="quarter-screen is-inactive" data-testid="quarter-screen" aria-label="${escapeHtml(
            t("map.title"),
          )}" aria-hidden="true" inert>
            ${renderQuarterMap(t)}
          </section>
          <section class="detail-screen is-inactive" data-testid="detail-screen" aria-hidden="true" inert></section>
          <section class="market-screen is-inactive" data-testid="market-screen" aria-hidden="true" inert></section>
        </main>

        <div class="sheet-layer" hidden>
          <button class="sheet-scrim" type="button" data-action="close-sheet" aria-label="${escapeHtml(
            t("sheet.close"),
          )}"></button>
          <div class="sheet-content"></div>
        </div>
        <div class="toast" role="status" aria-live="polite" aria-atomic="true" data-motif="default" hidden>
          <span class="toast__sigil" aria-hidden="true"><i></i></span>
          <span class="toast__copy"><strong data-toast-title></strong><small data-toast-body hidden></small></span>
        </div>
        <div class="visually-hidden" aria-live="polite" aria-atomic="true" data-live-region></div>
        ${DEV_TOOLS_BUILD_ENABLED && this.#devEnabled ? renderDevPanel(t) : ""}
        ${renderOnboardingLayer(this.#state, this.#serverNow, t)}
      </div>`;

    this.#shell = this.#required<HTMLElement>(".game-shell");
    this.#coreScreen = this.#required<HTMLElement>(".core-screen");
    this.#mapScreen = this.#required<HTMLElement>(".quarter-screen");
    this.#marketScreen = this.#required<HTMLElement>(".market-screen");
    this.#detailScreen = this.#required<HTMLElement>(".detail-screen");
    this.#balanceButton = this.#required<HTMLButtonElement>(".balance-button");
    this.#balanceValue = this.#required<HTMLElement>(".balance-value");
    this.#balanceEstimate = this.#required<HTMLElement>("[data-balance-estimate]");
    this.#balanceEstimateLabel = this.#required<HTMLElement>("[data-balance-estimate-label]");
    this.#passiveValue = this.#required<HTMLElement>(".passive-value");
    this.#seasonValue = this.#required<HTMLElement>(".season-countdown");
    this.#seasonPrimaryValue = this.#required<HTMLElement>(".season-clock strong");
    this.#seasonLabel = this.#required<HTMLElement>(".season-copy small");
    this.#reservedValue = this.#required<HTMLElement>(".reserved-value");
    this.#coreHint = this.#required<HTMLElement>(".tap-hint");
    this.#networkBanner = this.#required<HTMLElement>(".network-banner");
    this.#sheetLayer = this.#required<HTMLElement>(".sheet-layer");
    this.#sheetContent = this.#required<HTMLElement>(".sheet-content");
    this.#toast = this.#required<HTMLElement>(".toast");
    this.#toastTitle = this.#required<HTMLElement>("[data-toast-title]");
    this.#toastBody = this.#required<HTMLElement>("[data-toast-body]");
    this.#liveRegion = this.#required<HTMLElement>("[data-live-region]");
    this.#onboardingLayer = this.#required<HTMLElement>(".onboarding-layer");
    this.#onboardingFocusClone = this.#required<HTMLElement>(".onboarding-focus-clone");
  }

  #bindEvents(): void {
    this.#root.addEventListener("click", (event) => void this.#handleClick(event));
    this.#root.addEventListener("error", (event) => {
      if (event.target instanceof HTMLImageElement && event.target.matches("[data-player-avatar]")) event.target.hidden = true;
      if (event.target instanceof HTMLImageElement && event.target.dataset.visualAsset) {
        this.#trackVisualIssue("VISUAL_ASSET_LOAD", event.target.dataset.visualAsset,
          event.target.dataset.visualAsset === "quarter-map" ? "map" : "games");
      }
    }, true);
    this.#detailScreen.addEventListener(
      "scroll",
      () => {
        this.#shell.dataset.detailScrolled = String(this.#detailScreen.scrollTop > 12);
        this.#scheduleOnboardingPosition();
      },
      { passive: true },
    );
    this.#detailScreen.addEventListener(
      "touchstart",
      (event) => {
        this.#detailTouchY = event.touches.length === 1 ? (event.touches[0]?.clientY ?? null) : null;
      },
      { passive: true },
    );
    this.#detailScreen.addEventListener(
      "touchmove",
      (event) => {
        const touch = event.touches[0];
        if (touch === undefined || this.#detailTouchY === null) return;
        const deltaY = touch.clientY - this.#detailTouchY;
        const maxScroll = Math.max(0, this.#detailScreen.scrollHeight - this.#detailScreen.clientHeight);
        const pullingPastTop = this.#detailScreen.scrollTop <= 0 && deltaY > 0;
        const pullingPastBottom = this.#detailScreen.scrollTop >= maxScroll - 1 && deltaY < 0;
        if (pullingPastTop || pullingPastBottom) event.preventDefault();
        this.#detailTouchY = touch.clientY;
      },
      { passive: false },
    );
    const clearDetailTouch = () => {
      this.#detailTouchY = null;
    };
    this.#detailScreen.addEventListener("touchend", clearDetailTouch, { passive: true });
    this.#detailScreen.addEventListener("touchcancel", clearDetailTouch, { passive: true });
    this.#sheetContent.addEventListener(
      "touchstart",
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const dialog = target?.closest<HTMLElement>(".premium-shop-dialog") ?? null;
        const touch = dialog !== null && event.touches.length === 1 ? event.touches[0] : undefined;
        this.#premiumShopTouchX = touch?.clientX ?? null;
        this.#premiumShopTouchY = touch?.clientY ?? null;
        this.#premiumShopTouchStartY = touch?.clientY ?? null;
        this.#premiumShopTouchMoved = false;
      },
      { passive: true },
    );
    this.#sheetContent.addEventListener(
      "touchmove",
      (event) => {
        const dialog = this.#sheetContent.querySelector<HTMLElement>(".premium-shop-dialog");
        const touch = event.touches[0];
        if (dialog === null || touch === undefined || this.#premiumShopTouchY === null) return;
        if (
          this.#premiumShopTouchX !== null &&
          this.#premiumShopTouchStartY !== null &&
          Math.hypot(touch.clientX - this.#premiumShopTouchX, touch.clientY - this.#premiumShopTouchStartY) > 8
        ) {
          this.#premiumShopTouchMoved = true;
        }
        const deltaY = touch.clientY - this.#premiumShopTouchY;
        const maxScroll = Math.max(0, dialog.scrollHeight - dialog.clientHeight);
        const cannotScroll = maxScroll <= 1;
        const pullingPastTop = dialog.scrollTop <= 0 && deltaY > 0;
        const pullingPastBottom = dialog.scrollTop >= maxScroll - 1 && deltaY < 0;
        if (cannotScroll || pullingPastTop || pullingPastBottom) event.preventDefault();
        this.#premiumShopTouchY = touch.clientY;
      },
      { passive: false },
    );
    const clearPremiumShopTouch = (suppressClick: boolean) => {
      if (suppressClick && this.#premiumShopTouchMoved) this.#premiumShopSuppressClicksUntil = performance.now() + 420;
      this.#premiumShopTouchX = null;
      this.#premiumShopTouchY = null;
      this.#premiumShopTouchStartY = null;
      this.#premiumShopTouchMoved = false;
    };
    this.#sheetContent.addEventListener("touchend", () => clearPremiumShopTouch(true), { passive: true });
    this.#sheetContent.addEventListener("touchcancel", () => clearPremiumShopTouch(false), { passive: true });
    this.#root.addEventListener("pointerdown", (event) => {
      this.#lastInteractionWasKeyboard = false;
      this.#runtime?.scene.handleUserGesture();
      if ((event.target as Element).closest(".bottom-sheet, .profile-panel:not(.profile-panel--v3)") !== null) this.#sheetPointerY = event.clientY;
    }, { capture: true });
    this.#root.addEventListener("change", (event) => {
      const input = (event.target as Element).closest<HTMLInputElement>("input[data-setting]");
      if (input?.dataset.setting !== undefined) void this.#toggleSetting(input.dataset.setting as keyof UserSettings);
    });
    this.#root.addEventListener("input", (event) => {
      const message = (event.target as Element).closest<HTMLTextAreaElement>("[data-support-message]");
      if (message !== null && !this.#supportBusy) {
        this.#supportDraft = message.value;
        this.#supportRequestId = "";
        const send = this.#sheetContent.querySelector<HTMLButtonElement>('[data-action="send-support"]');
        if (send !== null) send.disabled = this.#supportDraft.trim().length < 10;
        return;
      }
      const input = (event.target as Element).closest<HTMLInputElement>('input[data-action="friend-search"]');
      if (input === null) return;
      this.#friendQuery = input.value;
      this.#friendPage = 1;
      this.#scheduleClubRosterLoad();
    });
    this.#root.addEventListener("pointerup", (event) => {
      if (this.#sheetPointerY !== null && event.clientY - this.#sheetPointerY > 86) this.#closeSheet();
      this.#sheetPointerY = null;
    });
    document.addEventListener("keydown", (event) => this.#handleKeydown(event));
    document.addEventListener("visibilitychange", () => void this.#handleVisibility());
    window.addEventListener("pagehide", () => {
      this.#spendWait?.controller.abort();
      void this.#flushTaps().catch(() => undefined);
    });
    window.addEventListener("resize", () => this.#scheduleOnboardingPosition(), { passive: true });
    window.addEventListener("offline", () => {
      this.#service.setOnline(false);
      this.#updateView({ details: false, navigation: false, sheet: false });
    });
    window.addEventListener("online", () => void this.#retryNetwork(false));
  }

  async #handleClick(event: MouseEvent): Promise<void> {
    const target = (event.target as Element).closest<HTMLElement>("[data-action]");
    if (target === null || this.#state === null) return;
    if (
      target.closest(".premium-shop-dialog") !== null &&
      performance.now() < this.#premiumShopSuppressClicksUntil
    ) {
      event.preventDefault();
      return;
    }
    const action = target.dataset.action;

    if (action === "open-leaderboard") {
      this.#openSheet("leaderboard", target);
      void this.#loadLeaderboard();
      return;
    }
    if (action === "refresh-leaderboard") { void this.#loadLeaderboard(); return; }
    if (action === "open-support") { this.#openSupport(target); return; }
    if (this.#supportBusy && ["new-support", "support-inbox", "support-ticket", "support-older"].includes(action ?? "")) return;
    if (action === "support-inbox") { this.#switchSupportView("inbox"); void this.#loadSupport(); return; }
    if (action === "support-ticket" && target.dataset.reference) { this.#switchSupportView("thread", target.dataset.reference); void this.#loadSupport(); return; }
    if (action === "support-retry") { void this.#loadSupport(); return; }
    if (action === "support-older") { void this.#loadSupport(true); return; }
    if (action === "new-support") {
      this.#switchSupportView("compose");
      this.#supportReceipt = null;
      this.#supportRequestId = "";
      this.#renderSupportSheet();
      return;
    }
    if (action === "send-support") { void this.#sendSupport(); return; }

    if (action === "onboarding-primary") {
      const command = target.dataset.onboardingCommand as OnboardingAction | undefined;
      if (command !== undefined) await this.#performOnboardingAction(command);
      return;
    }
    if (action === "onboarding-skip") {
      await this.#performOnboardingAction("skip");
      return;
    }
    if (action === "onboarding-target") {
      await this.#activateOnboardingTarget();
      return;
    }
    if (action === "onboarding-recover") {
      window.location.reload();
      return;
    }
    if (action === "open-arena-game") {
      const gameId = target.dataset.game as ArenaGameId | undefined;
      if (gameId === "crystal-penalty") {
        this.#arenaView = "penalty";
        void warmPenaltyArenaAssets(ARENA_PRELOAD_ASSETS.map((asset) => asset.url));
        this.#renderDetails();
        this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
      }
      if (gameId === "relic-duel" && RELIC_DUEL_PREVIEW_ENABLED) {
        this.#arenaView = "relic-duel";
        this.#renderDetails();
        this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
      }
      return;
    }
    if (action === "arena-catalog") {
      if (this.#state.pvp.status === "idle") {
        this.#arenaView = "catalog";
        this.#renderDetails();
        this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
      }
      return;
    }
    if (action === "arena-coming-soon") {
      this.#showToast(this.#translator("games.catalogComingSoon"), "info");
      return;
    }

    if (action === "navigate") {
      event.preventDefault();
      const section = target.dataset.section as SectionId | undefined;
      if (section !== undefined) {
        if (section === "progress" && target.dataset.rewardReady === "true") {
          const rewards = readyRewardSummary(this.#state);
          this.#progressSegment = rewards.season > 0 ? "season" : rewards.daily > 0 ? "today" : "achievements";
        }
        const fromLandmark = this.#mapOpen && target.classList.contains("quarter-landmark");
        if (fromLandmark && !this.#state.settings.reducedMotion) {
          window.clearTimeout(this.#mapTransitionTimer);
          target.classList.add("is-selected");
          this.#mapTransitionTimer = window.setTimeout(() => {
            this.#transitionView(() => {
              target.classList.remove("is-selected");
              this.#navigate(section);
            });
          }, GAME_CONFIG.motion.navigationMs);
        } else {
          this.#navigate(section);
        }
      }
      return;
    }
    if (action === "open-map") {
      this.#transitionView(() => this.#openMap());
      return;
    }
    if (action === "keyboard-tap") {
      this.#updateTapBoost();
      if (this.#runtime?.scene.playTapFeedback() === true) this.#handleTap(undefined, undefined, true);
      return;
    }
    if (action === "open-profile") this.#openSheet("profile", target);
    if (action === "open-preferences") this.#openSheet("preferences", target);
    if (action === "open-trade-quarter") this.#openTradeQuarter();
    if (action === "open-treasury") this.#openTreasury();
    if (action === "open-reward-account") this.#openSheet("reward-account", target);
    if (action === "open-partner-transfer" && this.#service.transfers) {
      const fromRewards = this.#sheet === "reward-account";
      this.#openSheet("partner-transfer", target); this.#transferReturnToRewards = fromRewards;
    }
    if (action === "open-ton-usdt-prizes") this.#openSheet("ton-usdt-prizes", target);
    if (action === "open-streak-calendar") this.#openSheet("daily-streak", target);
    if (action === "open-balance") this.#openSheet("balance", target);
    if (action === "open-energy") await this.#openEnergySheet(target);
    if (action === "open-premium-shop") this.#openPremiumShop(target);
    if (action === "open-upgrade-details") {
      const upgradeCode = this.#upgradeCodeFrom(target.dataset.upgrade);
      if (upgradeCode === "precisionCutter" || upgradeCode === "mechanicalCutter") {
        this.#selectedUpgrade = upgradeCode;
        this.#openSheet("upgrade", target);
      }
    }
    if (action === "open-section-help") this.#openSheet("section-help", target);
    if (action === "open-arena-rules" && this.#state?.pvp.status === "idle") this.#openSheet("arena-rules", target);
    if (action === "close-sheet") {
      if (this.#sheet === "partner-transfer") this.#leavePartnerTransfer();
      else this.#closeSheet();
    }
    if (action === "close-profile") this.#closeSheet();
    if (action === "retry-offline-reward") this.#retryOfflineReward();
    if (action === "collect-offline-reward") await this.#collectOfflineReward();
    if (action === "purchase-cutter") await this.#requestUpgradePurchase("mechanicalCutter", target);
    if (action === "buy-upgrade") {
      await this.#requestUpgradePurchase(this.#upgradeCodeFrom(target.dataset.upgrade), target);
    }
    if (action === "confirm-upgrade" && this.#pendingUpgradeCode === this.#upgradeCodeFrom(target.dataset.upgrade)) {
      await this.#purchaseUpgrade(this.#pendingUpgradeCode);
    }
    if (action === "refill-energy") await this.#refillEnergy();
    if (action === "open-season") {
      this.#progressSegment = "season";
      this.#navigate("progress");
      window.requestAnimationFrame(() =>
        this.#detailScreen.querySelector<HTMLElement>('[data-season-claim="true"], .trophy-season-chronicle')?.scrollIntoView({ block: "start", behavior: "auto" }),
      );
    }
    if (action === "progress-segment") {
      this.#progressSegment = target.dataset.segment as ProgressSegment;
      if (this.#progressSegment === "today") {
        this.#analytics.track("daily_route_view", { completed: dailyGoalsCompletedCount(this.#state) });
      }
      if (this.#progressSegment === "achievements") {
        this.#analytics.track("achievement_view", { source: "segment" });
      }
      this.#renderDetails();
      window.requestAnimationFrame(() => {
        if (this.#progressSegment === "season") {
          this.#detailScreen.querySelector<HTMLElement>('[data-season-claim="true"], .trophy-season-chronicle')?.scrollIntoView({ block: "start", behavior: "auto" });
        } else {
          this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
        }
      });
    }
    if (action === "achievement-filter") {
      this.#achievementFilter = target.dataset.filter as AchievementFilter;
      this.#renderDetails();
    }
    if (action === "friend-filter") {
      const filter = target.dataset.filter;
      this.#friendFilter = filter === "1" ? 1 : filter === "2" ? 2 : "all";
      this.#friendPage = 1;
      this.#haptics.selection();
      void this.#loadClubRoster();
    }
    if (action === "friend-page") {
      const page = Number(target.dataset.page);
      if (Number.isSafeInteger(page) && page > 0) {
        this.#friendPage = page;
        void this.#loadClubRoster();
        this.#detailScreen.querySelector<HTMLElement>(".makers-club-register")?.scrollIntoView({ block: "start" });
      }
    }
    if (action === "clear-friend-search") {
      this.#friendQuery = "";
      this.#friendFilter = "all";
      this.#friendPage = 1;
      const input = this.#detailScreen.querySelector<HTMLInputElement>('[data-action="friend-search"]');
      if (input !== null) input.value = "";
      void this.#loadClubRoster();
      window.requestAnimationFrame(() => input?.focus({ preventScroll: true }));
    }
    if (action === "show-unfinished-daily") {
      this.#detailScreen.querySelector<HTMLElement>(".trophy-trial:not(.is-complete)")?.scrollIntoView({
        block: "center", behavior: this.#state?.settings.reducedMotion ? "instant" : "smooth",
      });
      return;
    }
    if (action === "claim-daily-reward") await this.#claimDailyReward();
    if (action === "claim-achievement") {
      await this.#claimAchievement(target.dataset.achievement as AchievementId | undefined, target);
    }
    if (action === "claim-season-task" && target.dataset.task !== undefined) {
      await this.#claimSeasonTask(target.dataset.task);
    }
    if (
      action === "check-community" &&
      (target.dataset.community === "channel" || target.dataset.community === "chat")
    ) {
      await this.#checkCommunityMembership(target.dataset.community);
    }
    if (action === "select-shop-relic" && target.dataset.relic !== undefined) {
      this.#selectPremiumRelic(target.dataset.relic as PremiumShopRelic);
    }
    if (action === "purchase-premium-offer" && target.dataset.offer !== undefined) {
      await this.#purchasePremiumOffer(target.dataset.offer);
    }
    if (action === "preview-diamond-skin" && target.dataset.skin !== undefined) {
      this.#previewDiamondSkin(target.dataset.skin as DiamondSkinId);
    }
    if (action === "select-diamond-skin") {
      await this.#selectDiamondSkin(target.dataset.skin ? target.dataset.skin as DiamondSkinId : null);
    }
    if (action === "open-collectible" && target.dataset.collectible !== undefined) {
      this.#closeSheet();
      this.#selectedCollectible = target.dataset.collectible as CollectibleId;
      this.#openSheet("collectible", target);
    }
    if (action === "open-hall-of-fame") {
      this.#closeSheet();
      this.#progressSegment = "achievements";
      this.#navigate("progress");
    }
    if (action === "select-profile-title" && target.dataset.achievement !== undefined) {
      await this.#selectProfileTitle(target.dataset.achievement as AchievementId);
    }
    if (action === "claim-season-reward") await this.#claimSeasonReward(target.dataset.allocation);
    if (action === "invite-friend") {
      await this.#handleInviteFriend(target.dataset.mode === "copy" ? "copy" : "share", target);
    }
    if (action === "create-match" && target.dataset.stake !== undefined) {
      await this.#createMatch(target.dataset.stake);
    }
    if (action === "start-match") await this.#createMatch(GAME_CONFIG.pvp.stake);
    if (action === "pvp-zone" && target.dataset.zone !== undefined) {
      await this.#submitPvpZone(Number(target.dataset.zone));
    }
    if (action === "cancel-match") await this.#cancelMatch();
    if (action === "request-guardian") await this.#requestGuardianMatch();
    if (action === "return-arena") await this.#leaveMatchResult();
    if (action === "toggle-setting" && target.dataset.setting !== undefined) {
      await this.#toggleSetting(target.dataset.setting as keyof UserSettings);
    }
    if (action === "set-locale" && target.dataset.locale !== undefined) {
      await this.#setLocale(target.dataset.locale as Locale);
    }
    if (DEV_TOOLS_BUILD_ENABLED && action === "reset-demo") await this.#applyScenario("first-entry");
    if (action === "retry-network") await this.#retryNetwork();
    if (DEV_TOOLS_BUILD_ENABLED && action === "dev-scenario" && target.dataset.scenario !== undefined) {
      await this.#applyScenario(target.dataset.scenario as DevScenarioId);
    }
    if (DEV_TOOLS_BUILD_ENABLED && action === "seed-scenario" && target.dataset.scenario !== undefined) {
      await this.#applyScenario(target.dataset.scenario as DevScenarioId);
    }
    if (DEV_TOOLS_BUILD_ENABLED && action === "toggle-dev") {
      this.#devCollapsed = !this.#devCollapsed;
      const panel = this.#root.querySelector<HTMLElement>(".dev-panel, .dev-toggle");
      if (panel !== null) panel.outerHTML = renderDevPanel(this.#translator, this.#devCollapsed);
    }
  }

  #handleTap(_x?: number, _y?: number, fromKeyboard = false): void {
    if (this.#state === null) return;
    if (this.#marketOpen || this.#mapOpen || this.#activeSection !== "core") return;
    if (this.#sheet !== null || performance.now() < this.#workshopTapBlockedUntil) return;
    if (this.#state.season.status !== "active" || this.#estimatedServerNow() >= Date.parse(this.#state.season.endsAt)) {
      this.#showToast(this.#translator("status.seasonInactive"), "info");
      return;
    }
    const visibleEnergy = this.#visibleEnergy().current;
    if (visibleEnergy < GAME_CONFIG.energy.costPerTap) {
      this.#showEnergyDepleted();
      return;
    }
    if (!fromKeyboard) this.#haptics.tap();
    const boost = tapBoostAt(this.#state, this.#estimatedServerNow());
    const optimisticReward = BigInt(boost.reward);
    this.#runtime?.scene.setTapReward(boost.reward, boost.active !== undefined);
    this.#tapBatchCount += 1;
    this.#tapBatchCommandId ||= commandId("tap");
    this.#service.stageTaps(this.#tapBatchCommandId, this.#tapBatchCount);
    this.#tapBatchReward += optimisticReward;
    this.#pendingTapCount += 1;
    this.#pendingTapReward += optimisticReward;
    window.cancelAnimationFrame(this.#balanceAnimation);
    this.#displayedBalance += optimisticReward;
    this.#updateBalanceText();
    this.#updateEnergyPresentation();
    this.#updatePendingOnboardingTapProgress();
    const onboardingTapTargetReached =
      this.#state.onboarding.status === "in-progress" &&
      this.#state.onboarding.step === "tap" &&
      this.#state.progress.confirmedTaps + this.#pendingTapCount - this.#state.onboarding.tapBaseline >=
        this.#state.onboarding.tapTarget;
    if (
      onboardingTapTargetReached ||
      this.#tapBatchCount >= Math.min(
        GAME_CONFIG.economy.maxTapBatchSize,
        GAME_CONFIG.economy.maxTapBurstSize,
      )
    ) {
      if (this.#tapBatchTimer !== null) window.clearTimeout(this.#tapBatchTimer);
      this.#tapBatchTimer = null;
      void this.#submitTapBatch();
      return;
    }
    if (this.#tapBatchTimer !== null) return;
    this.#tapBatchTimer = window.setTimeout(() => {
      this.#tapBatchTimer = null;
      void this.#submitTapBatch();
    }, GAME_CONFIG.mock.tapBatchWindowMs);
  }

  async #submitTapBatch(): Promise<void> {
    const count = this.#tapBatchCount;
    const batchCommandId = this.#tapBatchCommandId || commandId("tap");
    this.#tapBatchCommandId = "";
    const optimisticReward = this.#tapBatchReward;
    this.#tapBatchCount = 0;
    this.#tapBatchReward = 0n;
    if (count === 0) return;
    this.#servicePendingTapCount += count;
    this.#servicePendingTapReward += optimisticReward;

    const request = this.#service
      .submitTaps(batchCommandId, count)
      .then((result) => {
        this.#synchronizePendingTaps(result.pendingTaps ?? this.#service.getPendingTaps());
        const reward = multiplyAmount(result.rewardPerTap, result.acceptedCount);
        // An outbox can finish long after the player leaves the diamond. Treat
        // its receipt like any other background update: remounting the open
        // screen here interrupts pressed buttons and destroys the Arena scene.
        const refresh = this.#liveRefreshBoundaries(this.#state, result.state);
        this.#applyEnvelope(result, { animateBalance: true, ...refresh });
        this.#liveRegion.textContent = this.#translator("status.tapsConfirmed", {
          value: formatCurrencyAmount(reward, result.state.settings.locale),
          balance: formatCurrencyAmount(result.state.account.available, result.state.settings.locale),
        });
        if (result.acceptedCount < result.requestedCount && result.state.energy.current === 0) {
          this.#showEnergyDepleted();
        }
      })
      .catch((error: unknown) => {
        this.#synchronizePendingTaps(this.#service.getPendingTaps());
        if (this.#state !== null) {
          this.#animateBalance(BigInt(this.#state.account.available) + this.#pendingTapReward);
          this.#syncWorkshop(false);
          this.#updatePendingOnboardingTapProgress();
        }
        this.#handleServiceError(error);
      })
      .finally(() => {
        this.#tapRequests.delete(request);
        this.#maybeConfirmOnboardingTaps();
      });
    this.#tapRequests.add(request);
    await request;
  }

  #synchronizePendingTaps(summary: NonNullable<ServiceEnvelope["pendingTaps"]>): void {
    const nextReward = BigInt(summary.reward);
    this.#pendingTapCount = Math.max(0, this.#pendingTapCount + summary.count - this.#servicePendingTapCount);
    const rewardDelta = nextReward - this.#servicePendingTapReward;
    this.#pendingTapReward = this.#pendingTapReward + rewardDelta > 0n ? this.#pendingTapReward + rewardDelta : 0n;
    this.#servicePendingTapCount = summary.count;
    this.#servicePendingTapReward = nextReward;
  }

  async #flushTaps(spend?: SpendFeedback & { controller: AbortController }): Promise<void> {
    if (this.#tapBatchTimer !== null) {
      window.clearTimeout(this.#tapBatchTimer);
      this.#tapBatchTimer = null;
      void this.#submitTapBatch();
    }
    this.#service.resumePendingTaps();
    await waitForTapDelivery({
      read: () => ({ count: this.#service.getPendingTaps().count, activeRequests: this.#tapRequests.size }),
      now: () => performance.now(),
      wait: (ms) => new Promise((resolve) => window.setTimeout(resolve, ms)),
      ...(spend ? { signal: spend.controller.signal } : {}),
      onProgress: (remaining) => {
        if (!spend || this.#spendWait !== spend || spend.remaining === remaining) return;
        spend.remaining = remaining;
        this.#renderSpendSheet();
      },
    });
  }

  #beginSpend(): SpendFeedback & { controller: AbortController } {
    const spend = { controller: new AbortController(), phase: "saving" as const, remaining: this.#service.getPendingTaps().count };
    this.#spendWait = spend;
    this.#upgradeBusy = true;
    this.#shell.dataset.upgradeBusy = "true";
    this.#renderSpendSheet();
    return spend;
  }

  #finishSpend(spend: SpendFeedback & { controller: AbortController }): void {
    if (this.#spendWait !== spend) return;
    this.#spendWait = null;
    this.#upgradeBusy = false;
    delete this.#shell.dataset.upgradeBusy;
    this.#renderSpendSheet();
  }

  #renderSpendSheet(): void {
    if (this.#destroyed) return;
    if (this.#sheet === "energy" || this.#sheet === "upgrade-confirm") this.#renderSheet();
  }

  async #purchaseUpgrade(upgradeCode: UpgradeCode): Promise<void> {
    if (this.#upgradeBusy || this.#state === null) return;
    const spend = this.#beginSpend();
    try {
      await this.#flushTaps(spend);
      if (spend.controller.signal.aborted) return;
      spend.phase = "sending";
      this.#renderSpendSheet();
      const purchaseCommandId = this.#pendingUpgradeCode === upgradeCode
        ? (this.#pendingUpgradeCommandId ??= commandId(`upgrade-${upgradeCode}`))
        : commandId(`upgrade-${upgradeCode}`);
      const result = await this.#service.purchaseUpgrade(purchaseCommandId, upgradeCode);
      if (result.purchased) {
        this.#applyEnvelope(result, { animateBalance: true, details: true });
        this.#haptics.success();
        if (!spend.controller.signal.aborted) this.#closeSheet();
        if (result.upgradeCode === "precisionCutter" || result.upgradeCode === "mechanicalCutter") {
          const precision = result.upgradeCode === "precisionCutter";
          this.#showToast(
            this.#translator(precision ? "status.precisionUpgraded" : "status.mechanicalUpgraded"),
            "success",
            4_000,
            {
              body: this.#translator(precision ? "status.precisionUpgradeBody" : "status.mechanicalUpgradeBody", {
                value: precision
                  ? formatCurrencyAmount(result.state.account.tapReward, result.state.settings.locale)
                  : formatProductionSpeed(result.state.account.passivePerHour, result.state.settings.locale),
              }),
              motif: precision ? "precision" : "mechanical",
            },
          );
        } else {
          const capacity = result.upgradeCode === "energyCapacity";
          this.#showToast(
            this.#translator(capacity ? "status.energyCapacityUpgraded" : "status.energyRecoveryUpgraded"),
            "success",
            4_000,
            {
              body: this.#translator(capacity ? "status.energyCapacityBody" : "status.energyRecoveryBody", {
                value: capacity ? result.state.energy.capacity : result.state.energy.recoverySecondsPerUnit,
              }),
              motif: "energy",
            },
          );
        }
      }
    } catch (error) {
      if (!spend.controller.signal.aborted) this.#handleServiceError(error);
    } finally {
      this.#finishSpend(spend);
    }
  }

  async #requestUpgradePurchase(upgradeCode: UpgradeCode, opener: HTMLElement): Promise<void> {
    if (this.#state === null) return;
    const onboardingPurchase =
      upgradeCode === "mechanicalCutter" &&
      this.#state.onboarding.status === "in-progress" &&
      this.#state.onboarding.step === "upgrade";
    if (onboardingPurchase) {
      await this.#purchaseUpgrade(upgradeCode);
      return;
    }
    if (this.#sheet !== null) this.#closeSheet();
    this.#pendingUpgradeCode = upgradeCode;
    this.#pendingUpgradeCommandId = commandId(`upgrade-${upgradeCode}`);
    this.#openSheet("upgrade-confirm", opener);
  }

  async #refillEnergy(): Promise<void> {
    if (this.#upgradeBusy || this.#state === null) return;
    const spend = this.#beginSpend();
    try {
      // A quick tap burst may still be moving through the ordered HTTP queue when
      // the user opens the energy sheet. Settle it first so the refill command is
      // calculated from the same confirmed energy and balance shown in the UI.
      await this.#flushTaps(spend);
      if (spend.controller.signal.aborted) return;
      spend.phase = "sending";
      this.#renderSpendSheet();
      const result = await this.#service.refillEnergy(commandId("energy-refill"));
      this.#applyEnvelope(result, { animateBalance: true, details: true, sheet: true });
      this.#haptics.success();
      if (!spend.controller.signal.aborted) this.#closeSheet();
      this.#showToast(this.#translator("status.energyRefilled"), "success", 4_000, {
        body: this.#translator("status.energyRefilledBody", {
          value: formatAmount(String(result.restored), result.state.settings.locale),
          count: Math.max(0, GAME_CONFIG.energy.dailyRefillLimit - result.state.energy.refillsUsedToday),
        }),
        motif: "energy",
      });
    } catch (error) {
      if (!spend.controller.signal.aborted) this.#handleServiceError(error);
    } finally {
      this.#finishSpend(spend);
    }
  }

  async #openEnergySheet(origin: HTMLElement): Promise<void> {
    if (this.#state === null) return;
    this.#openSheet("energy", origin);
    // Opening the quote is not consent to spend. Only Charge now creates a
    // cancellable action that follows the durable tap queue.
  }

  #showEnergyDepleted(): void {
    this.#haptics.error();
    this.#showToast(this.#translator("status.energyEmpty"), "info", 4_000, {
      body: this.#translator("status.energyEmptyBody"),
      motif: "energy",
    });
  }

  async #claimDailyReward(): Promise<void> {
    if (this.#rewardClaimBusy || this.#state?.progress.daily.rewardClaimed) return;
    this.#rewardClaimBusy = true; this.#syncRewardClaimButtons();
    try {
      const result = await this.#service.claimDailyReward(commandId("daily-reward"));
      this.#pendingTrophyCelebration = result.rewardClaim?.alreadyClaimed ? null : "daily";
      this.#applyEnvelope(result, { animateBalance: true, details: true });
      if (result.rewardClaim?.alreadyClaimed) {
        this.#showToast(this.#translator("status.rewardAlreadyClaimed"), "info");
        return;
      }
      this.#haptics.success();
      this.#showToast(
        this.#translator("status.dailyReward", {
          value: formatCurrencyAmount(result.state.progress.daily.claimedReward, result.state.settings.locale),
        }),
        "success",
      );
      this.#analytics.track("daily_route_claim", {
        streak: result.state.progress.dailyStreak.current,
        reward: result.state.progress.daily.claimedReward,
      });
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#rewardClaimBusy = false; this.#syncRewardClaimButtons();
    }
  }

  async #claimAchievement(
    achievementId: AchievementId = "living-facet",
    opener: HTMLElement,
  ): Promise<void> {
    if (this.#rewardClaimBusy || this.#state?.progress.claimedAchievementIds.includes(achievementId)) return;
    this.#rewardClaimBusy = true; this.#syncRewardClaimButtons();
    try {
      const result = await this.#service.claimAchievement(commandId(`achievement-${achievementId}`), achievementId);
      this.#pendingTrophyCelebration = result.rewardClaim?.alreadyClaimed ? null : "achievement";
      this.#applyEnvelope(result, { details: true, sheet: true });
      if (result.rewardClaim?.alreadyClaimed) {
        this.#showToast(this.#translator("status.rewardAlreadyClaimed"), "info");
        return;
      }
      this.#haptics.success();
      this.#closeSheet();
      this.#rewardRevealAchievement = achievementId;
      this.#openSheet("reward-reveal", opener);
      this.#analytics.track("achievement_claim", { achievementId });
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#rewardClaimBusy = false; this.#syncRewardClaimButtons();
    }
  }

  async #claimSeasonTask(taskCode: SeasonTaskCode): Promise<void> {
    if (this.#rewardClaimBusy || this.#state?.progress.seasonal.claimedCodes.includes(taskCode)) return;
    this.#rewardClaimBusy = true; this.#syncRewardClaimButtons();
    try {
      const result = await this.#service.claimSeasonTask(commandId(`season-task-${taskCode}`), taskCode);
      this.#applyEnvelope(result, { animateBalance: true, details: true });
      if (result.rewardClaim?.alreadyClaimed) {
        this.#showToast(this.#translator("status.rewardAlreadyClaimed"), "info");
        return;
      }
      this.#haptics.success();
      this.#showToast(this.#translator("status.seasonTaskClaimed"), "success");
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#rewardClaimBusy = false; this.#syncRewardClaimButtons();
    }
  }

  #syncRewardClaimButtons(): void {
    this.#root.querySelectorAll<HTMLButtonElement>('[data-action="claim-daily-reward"], [data-action="claim-achievement"], [data-action="claim-season-task"]').forEach(button => {
      if (this.#rewardClaimBusy) {
        if (!this.#rewardButtonStates.has(button)) this.#rewardButtonStates.set(button, button.disabled);
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      } else if (this.#rewardButtonStates.has(button)) {
        button.disabled = this.#rewardButtonStates.get(button) ?? true;
        button.removeAttribute("aria-busy");
        this.#rewardButtonStates.delete(button);
      }
    });
  }

  async #checkCommunityMembership(target: "channel" | "chat"): Promise<void> {
    try {
      const result = await this.#service.refreshCommunityMembership(target);
      this.#applyEnvelope(result, { details: true });
      const joined = result.state.telegram[target].joined;
      if (joined) this.#haptics.success();
      else this.#haptics.selection();
      this.#showToast(
        this.#translator(joined ? "status.membershipChecked" : "status.membershipMissing"),
        joined ? "success" : "info",
      );
    } catch (error) {
      this.#handleServiceError(error);
    }
  }

  #openPremiumShop(opener: HTMLElement): void {
    const requestedRelic = opener.dataset.shopRelic;
    this.#premiumRelic = requestedRelic === "booster" || requestedRelic === "skin" || requestedRelic === "signet"
      ? requestedRelic
      : "reserve";
    this.#premiumPreviewSkinId = null;
    this.#analytics.track("premium_offer_view", {
      relic: this.#premiumRelic,
      source: opener.dataset.shopRelic ?? "gate",
    });
    this.#openSheet("premium-shop", opener);
    void this.#service.refreshPremiumOffers?.().then(() => this.#refreshPremiumShopContent()).catch(() => undefined);
  }

  #selectPremiumRelic(relic: PremiumShopRelic): void {
    if (this.#sheet !== "premium-shop" || this.#premiumRelic === relic) return;
    this.#premiumRelic = relic;
    if (relic !== "skin") this.#premiumPreviewSkinId = null;
    this.#analytics.track("premium_offer_view", { relic, source: "tab" });
    this.#haptics.selection();
    this.#refreshPremiumShopContent();
  }

  async #purchasePremiumOffer(offerId: PremiumOfferId): Promise<void> {
    this.#analytics.track("premium_invoice_open", { offerId });
    try {
      const offer = GAME_CONFIG.premium.offers.find((candidate) => candidate.id === offerId);
      if (offer === undefined) return;
      const relic = premiumRelicForOfferKind(offer.kind);
      const result = await this.#service.purchasePremiumOffer(commandId(`premium-${offerId}`), offerId);
      this.#premiumRelic = relic;
      this.#premiumPreviewSkinId = null;
      this.#applyEnvelope(result);
      this.#refreshPremiumShopContent();
      this.#premiumRuntime?.scene.playPurchase(relic, offer.grantBasisPoints ?? 0);
      this.#haptics.success();
      this.#showToast(this.#translator("status.premiumPurchased"), "success");
      this.#analytics.track("premium_paid", { offerId });
    } catch (error) {
      this.#analytics.track("premium_cancelled", {
        offerId,
        reason: isGameServiceError(error) ? error.code : "unknown",
      });
      this.#handleServiceError(error);
    }
  }

  async #selectDiamondSkin(skinId: DiamondSkinId | null): Promise<void> {
    try {
      const result = await this.#service.selectDiamondSkin(skinId);
      this.#premiumRelic = "skin";
      this.#premiumPreviewSkinId = null;
      this.#applyEnvelope(result);
      this.#refreshPremiumShopContent();
      this.#premiumRuntime?.scene.playPurchase("skin");
      this.#haptics.selection();
      this.#showToast(this.#translator("status.skinSelected"), "success");
    } catch (error) {
      this.#handleServiceError(error);
    }
  }

  #previewDiamondSkin(skinId: DiamondSkinId): void {
    if (this.#sheet !== "premium-shop") return;
    const offer = GAME_CONFIG.premium.offers.find(
      (candidate) => candidate.kind === "cosmetic" && candidate.collectibleId === skinId,
    );
    if (offer === undefined) return;
    this.#premiumRelic = "skin";
    this.#premiumPreviewSkinId = this.#premiumPreviewSkinId === skinId ? null : skinId;
    this.#analytics.track("premium_offer_view", {
      offerId: offer.id,
      source: this.#premiumPreviewSkinId === null ? "preview-close" : "preview",
    });
    this.#haptics.selection();
    this.#refreshPremiumShopContent();
  }

  #refreshPremiumShopContent(): void {
    if (this.#state === null || this.#sheet !== "premium-shop") return;
    const content = this.#sheetContent.querySelector<HTMLElement>(".premium-shop-content");
    if (content !== null) {
      content.innerHTML = renderPremiumShopContentV2(
        this.#state,
        this.#serverNow,
        this.#translator,
        this.#premiumRelic,
        this.#premiumPreviewSkinId,
      );
    }
    this.#premiumRuntime?.scene.setVisualState(this.#premiumShopVisualState());
  }

  #premiumShopVisualState(): PremiumShopVisualState {
    if (this.#state === null) throw new Error("Premium shop cannot render without game state");
    const offer = GAME_CONFIG.premium.offers.find((candidate) => candidate.kind === "tap-production-grant");
    const now = new Date(this.#estimatedServerNow());
    const availability = offer === undefined
      ? { phase: "expired" as const, startsAt: null, endsAt: null }
      : premiumOfferAvailability(offer, now, this.#state.season.startsAt);
    const startsAt = availability.startsAt === null ? Number.NaN : Date.parse(availability.startsAt);
    const endsAt = availability.endsAt === null ? Number.NaN : Date.parse(availability.endsAt);
    const duration = endsAt - startsAt;
    const remainingBasisPoints = availability.phase === "upcoming"
      ? 10_000
      : availability.phase === "expired" || !Number.isFinite(duration) || duration <= 0
        ? 0
        : Math.round(Phaser.Math.Clamp((endsAt - now.getTime()) / duration, 0, 1) * 10_000);
    return {
      selectedRelic: this.#premiumRelic,
      offlineReserveSeconds: this.#state.premium.offlineReserveSeconds,
      queuedBoosts: this.#state.premium.passiveBoosts.length + this.#state.premium.tapBoosts.length,
      selectedSkinId: this.#state.collection.selectedSkinId,
      previewSkinId: this.#premiumPreviewSkinId,
      startWindowPhase: availability.phase,
      startWindowRemainingBasisPoints: remainingBasisPoints,
      reducedMotion: this.#state.settings.reducedMotion,
    };
  }

  async #selectProfileTitle(achievementId: AchievementId): Promise<void> {
    try {
      const result = await this.#service.selectProfileTitle(achievementId);
      this.#applyEnvelope(result, { sheet: true });
      const nameKey = `hall.achievement.${achievementId}.name` as Parameters<Translator>[0];
      this.#haptics.selection();
      this.#showToast(
        this.#translator("status.titleSelected", { name: this.#translator(nameKey) }),
        "success",
      );
    } catch (error) {
      this.#handleServiceError(error);
    }
  }

  async #claimSeasonReward(allocationId?: string): Promise<void> {
    try {
      const result = await this.#service.claimSeasonReward(commandId("season-reward"), allocationId);
      this.#pendingTrophyCelebration = "season";
      this.#applyEnvelope(result, { details: true });
      const opener = this.#root.querySelector<HTMLElement>('[data-action="open-profile"]');
      if (opener !== null) this.#openSheet("reward-account", opener);
      this.#haptics.success();
      this.#showToast(this.#translator("status.reward"), "success");
    } catch (error) {
      this.#handleServiceError(error);
    }
  }

  async #createMatch(stake: string): Promise<void> {
    if (this.#state?.pvp.status === "result") {
      this.#beginArenaResultExit("replay", stake);
      return;
    }
    await this.#queueMatch(stake);
  }

  async #queueMatch(stake: string): Promise<void> {
    if (this.#pvpBusy) return;
    this.#pvpBusy = true;
    this.#syncArenaPresentation(this.#state);
    this.#analytics.track("arena_queue_start", { gameId: "crystal-penalty", stake });
    try {
      const result = await this.#service.createPvpMatch(commandId("pvp-create"), stake, "crystal-penalty");
      this.#arenaView = "penalty";
      this.#applyEnvelope(result, { animateBalance: true });
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#pvpBusy = false;
      this.#syncArenaPresentation(this.#state);
    }
  }

  async #submitPvpZone(zone: number): Promise<void> {
    if (this.#pvpBusy) {
      if (this.#state?.pvp.status === "choosing" && this.#state.pvp.currentTurn?.playerZone === null) {
        this.#queuedPvpZone = zone;
      }
      return;
    }
    this.#pvpBusy = true;
    try {
      const accepted = await this.#service.submitPvpMove(commandId("pvp-move"), zone);
      this.#applyEnvelope(accepted);
      this.#haptics.selection();
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#pvpBusy = false;
    }

    const queuedZone = this.#queuedPvpZone;
    this.#queuedPvpZone = null;
    if (
      queuedZone !== null &&
      this.#state?.pvp.status === "choosing" &&
      this.#state.pvp.currentTurn?.playerZone === null
    ) {
      await this.#submitPvpZone(queuedZone);
    }
  }

  async #cancelMatch(): Promise<void> {
    if (this.#pvpBusy) return;
    this.#pvpBusy = true;
    try {
      const result = await this.#service.cancelPvpMatch(commandId("pvp-cancel"));
      this.#applyEnvelope(result, { animateBalance: true });
      this.#showToast(this.#translator("games.resultCancelled"), "info");
      this.#analytics.track("arena_queue_cancel", { gameId: "crystal-penalty" });
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#pvpBusy = false;
    }
  }

  async #requestGuardianMatch(): Promise<void> {
    if (this.#pvpBusy || !this.#service.canRequestGuardianMatch()) return;
    this.#pvpBusy = true;
    try {
      const result = await this.#service.requestGuardianMatch(commandId("pvp-guardian"));
      this.#applyEnvelope(result, { details: true });
      this.#haptics.selection();
    } catch (error) {
      this.#handleServiceError(error);
    } finally {
      this.#pvpBusy = false;
    }
  }

  async #leaveMatchResult(): Promise<void> {
    this.#beginArenaResultExit("catalog");
  }

  #beginArenaResultExit(destination: ArenaResultExit["destination"], replayStake: string | null = null): void {
    if (this.#state === null || this.#arenaResultExit !== null || this.#pvpBusy) return;
    this.#arenaResultExit = {
      destination,
      replayStake,
      lastDismissedResultKey: null,
      dismissCount: 0,
    };
    this.#pvpBusy = true;
    this.#shell.dataset.arenaExitBusy = "true";
    const actions = this.#detailScreen.querySelector<HTMLElement>(".arena-result-actions");
    actions?.setAttribute("aria-busy", "true");
    actions?.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      button.disabled = true;
    });
    void this.#dismissCurrentArenaResult();
  }

  #arenaResultKey(state: GameState): string {
    const match = state.pvp;
    return `${match.matchId ?? "cancelled"}:${match.sequence}:${match.eventSequence}`;
  }

  async #dismissCurrentArenaResult(): Promise<void> {
    const exit = this.#arenaResultExit;
    const state = this.#state;
    if (exit === null || state === null) return;
    if (state.pvp.status === "idle") {
      this.#completeArenaResultExit();
      return;
    }
    if (state.pvp.status !== "result") {
      this.#scheduleArenaResultExitRecovery();
      return;
    }
    const resultKey = this.#arenaResultKey(state);
    if (exit.lastDismissedResultKey === resultKey) {
      this.#scheduleArenaResultExitRecovery();
      return;
    }
    if (exit.dismissCount >= 64) {
      this.#abortArenaResultExit(new Error("Arena result chain exceeded the recovery limit"));
      return;
    }
    exit.lastDismissedResultKey = resultKey;
    exit.dismissCount += 1;
    try {
      const result = await this.#service.leavePvpResult(commandId("pvp-dismiss"));
      this.#consumeArenaResultExitEnvelope(result);
    } catch (error) {
      this.#abortArenaResultExit(error);
    }
  }

  #consumeArenaResultExitEnvelope(envelope: ServiceEnvelope): boolean {
    const exit = this.#arenaResultExit;
    if (exit === null) return false;
    window.clearTimeout(this.#arenaResultExitTimer);
    this.#arenaResultExitTimer = 0;
    this.#applyEnvelope(envelope, { animateBalance: true, details: false, sheet: true });
    if (envelope.state.pvp.status === "idle") {
      this.#completeArenaResultExit();
      return true;
    }
    if (envelope.state.pvp.status === "result") {
      const resultKey = this.#arenaResultKey(envelope.state);
      if (resultKey !== exit.lastDismissedResultKey) {
        this.#arenaResultExitTimer = window.setTimeout(() => {
          this.#arenaResultExitTimer = 0;
          void this.#dismissCurrentArenaResult();
        }, 90);
      } else {
        this.#scheduleArenaResultExitRecovery();
      }
      return true;
    }
    this.#scheduleArenaResultExitRecovery();
    return true;
  }

  #scheduleArenaResultExitRecovery(): void {
    if (this.#arenaResultExit === null || this.#arenaResultExitTimer !== 0) return;
    this.#arenaResultExitTimer = window.setTimeout(() => {
      this.#arenaResultExitTimer = 0;
      void this.#recoverArenaResultExit();
    }, 1_800);
  }

  async #recoverArenaResultExit(): Promise<void> {
    const exit = this.#arenaResultExit;
    if (exit === null) return;
    try {
      const refreshed = await this.#service.refreshState();
      if (this.#arenaResultExit !== exit) return;
      if (refreshed.state.pvp.status === "result") exit.lastDismissedResultKey = null;
      this.#consumeArenaResultExitEnvelope(refreshed);
      if (this.#arenaResultExit === exit && refreshed.state.pvp.status === "result") {
        void this.#dismissCurrentArenaResult();
      }
    } catch (error) {
      this.#abortArenaResultExit(error);
    }
  }

  #completeArenaResultExit(): void {
    const exit = this.#arenaResultExit;
    if (exit === null) return;
    window.clearTimeout(this.#arenaResultExitTimer);
    this.#arenaResultExitTimer = 0;
    this.#arenaResultExit = null;
    this.#pvpBusy = false;
    delete this.#shell.dataset.arenaExitBusy;
    this.#arenaView = exit.destination === "catalog" ? "catalog" : "penalty";
    this.#renderDetails();
    this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
    this.#haptics.selection();
    if (exit.destination === "replay" && exit.replayStake !== null) {
      void this.#queueMatch(exit.replayStake);
    }
  }

  #abortArenaResultExit(error: unknown): void {
    window.clearTimeout(this.#arenaResultExitTimer);
    this.#arenaResultExitTimer = 0;
    this.#arenaResultExit = null;
    this.#pvpBusy = false;
    delete this.#shell.dataset.arenaExitBusy;
    this.#syncArenaPresentation(null);
    this.#renderDetails();
    this.#handleServiceError(error);
  }

  async #inviteFriend(mode: "share" | "copy", origin: HTMLElement): Promise<boolean> {
    const url = this.#makersClubInviteUrl();
    const shareData = {
      title: this.#translator("friends.shareTitle"),
      text: this.#translator("friends.shareText"),
      url,
    };
    try {
      const shared = mode === "share" && typeof navigator.share === "function";
      if (shared) {
        await navigator.share(shareData);
      } else {
        if (!(await copyText(url))) throw new Error("Clipboard write failed");
      }
      this.#makersClubRuntime?.playInvitation();
      const feedbackSurface = origin.closest<HTMLElement>(".makers-club-hero, .makers-club-gate");
      feedbackSurface?.classList.remove("is-inviting");
      if (feedbackSurface !== null) {
        void feedbackSurface.offsetWidth;
        feedbackSurface.classList.add("is-inviting");
        window.setTimeout(() => feedbackSurface.classList.remove("is-inviting"), 920);
      }
      this.#haptics.success();
      const confirmation = this.#translator(shared ? "club.inviteShared" : "club.inviteCopied");
      const originalLabel = origin.textContent ?? "";
      origin.textContent = confirmation;
      origin.classList.add("is-confirmed");
      origin.setAttribute("aria-label", confirmation);
      origin.setAttribute("disabled", "");
      this.#liveRegion.textContent = this.#translator(shared ? "status.clubInviteShared" : "status.clubInviteCopied");
      window.setTimeout(() => {
        if (!origin.isConnected) return;
        origin.textContent = originalLabel;
        origin.classList.remove("is-confirmed");
        origin.removeAttribute("aria-label");
        origin.removeAttribute("disabled");
      }, 1_800);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return false;
      this.#showToast(
        this.#translator(mode === "copy" ? "status.clubInviteCopyFailed" : "status.actionUnavailable"),
        "error",
      );
      return false;
    }
  }

  async #handleInviteFriend(mode: "share" | "copy", origin: HTMLElement): Promise<void> {
    await this.#inviteFriend(mode, origin);
    // Copying is a client convenience, not a valid reason to trap a player in
    // onboarding. The URL remains visible and can still be shared manually.
    if (mode === "copy" && this.#state?.onboarding.step === "club-link") {
      await this.#performOnboardingAction("copy-invite");
    }
  }

  async #toggleSetting(setting: keyof UserSettings): Promise<void> {
    if (this.#state === null || setting === "locale") return;
    const current = this.#state.settings[setting];
    if (typeof current !== "boolean") return;
    const next = !current;
    const input = this.#sheetContent.querySelector<HTMLInputElement>(`input[data-setting="${setting}"]`);
    const row = input?.closest<HTMLElement>(".profile-setting") ?? null;
    if (input !== null) {
      input.checked = next;
      input.disabled = true;
      input.setAttribute("aria-busy", "true");
    }
    row?.classList.add("is-saving");

    // Music must be started synchronously inside the user's change gesture on
    // iOS. The confirmed service envelope reconciles the optimistic state.
    if (setting === "music") this.#runtime?.scene.setMusicEnabled(next);
    if (setting === "vibration") this.#haptics.setEnabled(next);

    try {
      const result = await this.#service.updateSettings({ [setting]: next });
      this.#applyEnvelope(result, { details: false, sheet: false });
      const confirmed = result.state.settings[setting];
      if (input !== null && typeof confirmed === "boolean") input.checked = confirmed;
      row?.classList.remove("is-saving");
      row?.classList.add("is-confirmed");
      window.setTimeout(() => row?.classList.remove("is-confirmed"), 420);
    } catch (error) {
      if (input !== null) input.checked = current;
      if (setting === "music") this.#runtime?.scene.setMusicEnabled(current);
      if (setting === "vibration") this.#haptics.setEnabled(current);
      row?.classList.remove("is-saving");
      this.#handleServiceError(error);
    } finally {
      if (input !== null) {
        input.disabled = false;
        input.removeAttribute("aria-busy");
      }
    }
  }

  async #setLocale(locale: Locale): Promise<void> {
    const result = await this.#service.updateSettings({ locale });
    this.#applyEnvelope(result, { navigation: true, details: true, sheet: true });
    this.#translator = createTranslator(locale);
    document.documentElement.lang = locale;
    this.#updateView({ navigation: true, details: true, sheet: true });
  }

  #applyPreferences(): void {
    if (this.#state === null) return;
    const reducedMotion = this.#state.settings.reducedMotion;
    this.#haptics.setEnabled(this.#state.settings.vibration);
    this.#runtime?.scene.setReducedMotion(reducedMotion);
    this.#runtime?.scene.setMusicEnabled(this.#state.settings.music);
    this.#runtime?.scene.setMusicSuspended(document.hidden);
    this.#energyRuntimes.forEach((runtime) => runtime.scene.setReducedMotion(reducedMotion));
    this.#energySigilRuntime?.scene.setReducedMotion(reducedMotion);
    if (this.#premiumRuntime !== null) {
      this.#premiumRuntime.scene.setVisualState(this.#premiumShopVisualState());
    }
    if (this.#makersClubRuntime !== null) {
      this.#makersClubRuntime.setVisualState(this.#makersClubVisualState());
    }
    this.#arenaCatalogRuntime?.setReducedMotion(reducedMotion);
    this.#arenaRuntime?.setMatch(this.#state.pvp, reducedMotion);
    this.#relicDuelRuntime?.setReducedMotion(reducedMotion);
    if (this.#state.onboarding.status === "not-started" || this.#state.onboarding.status === "in-progress") {
      const model = onboardingUiModel(this.#state, this.#serverNow, this.#translator);
      this.#onboardingRuntime?.setPresentation({
        pose: model.pose,
        mirror: ONBOARDING_GUIDE_STAGING[model.step].mirror,
        reducedMotion,
        compact: model.targetLabel !== null,
      });
    }
    const arenaHost = this.#detailScreen.querySelector<HTMLElement>("#arena-runtime");
    if (arenaHost !== null) arenaHost.dataset.phaserMotion = reducedMotion ? "reduced" : "full";
    this.#shell.dataset.reducedMotion = String(reducedMotion);
  }

  #navigate(section: SectionId): void {
    this.#closeTradeQuarter();
    const repeated = section === this.#activeSection;
    if (section !== "development") this.#disposeEnergyRuntime();
    if (section !== "progress") {
      this.#trophyPreviewCount = null;
      this.#disposeTrophyRuntime();
    }
    if (section !== "friends") this.#disposeMakersClubRuntime();
    if (section !== "games") {
      this.#disposeArenaCatalogRuntime();
      this.#disposeArenaRuntime();
      this.#disposeRelicDuelRuntime();
    }
    if (section === "games" && this.#state?.pvp.status === "idle") this.#arenaView = "catalog";
    this.#activeSection = section;
    this.#analytics.track("screen_view", section === "games"
      ? { screen: section, version: browserEngineVersion() }
      : { screen: section });
    if (section === "progress" && this.#progressSegment === "today") {
      this.#analytics.track("daily_route_view", {
        completed: this.#state === null ? 0 : dailyGoalsCompletedCount(this.#state),
      });
    }
    if (section === "progress" && this.#progressSegment === "achievements") {
      this.#analytics.track("achievement_view", { source: "navigation" });
    }
    this.#mapOpen = false;
    this.#haptics.selection();
    this.#shell.dataset.detailScrolled = "false";
    this.#updateView({ navigation: true, details: true, sheet: false });
    if (section === "friends") void this.#loadClubRoster();
    this.#detailScreen.scrollTo({
      top: 0,
      behavior: repeated && this.#state?.settings.reducedMotion !== true ? "smooth" : "auto",
    });
  }

  #openMap(): void {
    this.#closeTradeQuarter();
    this.#disposeEnergyRuntime();
    this.#disposeTrophyRuntime();
    this.#disposeMakersClubRuntime();
    this.#disposeArenaCatalogRuntime();
    this.#disposeArenaRuntime();
    this.#disposeRelicDuelRuntime();
    this.#mapOpen = true;
    this.#analytics.track("screen_view", { screen: "map", version: browserEngineVersion() });
    this.#mapScreen.querySelectorAll(".is-selected").forEach((element) => element.classList.remove("is-selected"));
    this.#closeSheet();
    this.#haptics.selection();
    this.#updateView({ navigation: false, details: false, sheet: false });
  }

  #openTradeQuarter(): void {
    if (!this.#state || !this.#service.market || this.#state.profile.marketEnabled === false) return;
    this.#closeSheet();
    this.#navigate("core");
    this.#marketOpen = true;
    this.#blockWorkshopTapInput();
    this.#updateView({ navigation: false, details: false, sheet: false });
    this.#marketController = new MarketController({ root: this.#marketScreen, api: this.#service.market, analytics: this.#analytics,
      locale: this.#state.settings.locale, reducedMotion: this.#state.settings.reducedMotion,
      onExit: () => { this.#navigate("core"); this.#required<HTMLElement>("#game-main").focus({ preventScroll: true }); },
      onReward: async () => { this.#applyEnvelope(await this.#service.refreshState(), { details: false, sheet: false }); },
      onTreasury: () => this.#openTreasury(),
    });
  }

  #closeTradeQuarter(): void {
    if (!this.#marketOpen) return;
    this.#marketOpen = false;
    this.#marketController?.destroy(); this.#marketController = null;
    this.#treasuryController?.destroy(); this.#treasuryController = null;
    this.#marketScreen.innerHTML = "";
    this.#releaseWorkshopTapInput();
  }

  #openTreasury(initialTab?: TreasuryTab): void {
    if (!GRIM_TREASURY_PREVIEW_ENABLED || !this.#state || !this.#service.market || this.#state.profile.marketEnabled === false) return;
    const returnTo = this.#marketOpen ? "market" : this.#sheet === "profile" ? "profile" : "core";
    this.#closeSheet(); this.#navigate("core");
    this.#marketOpen = true; this.#blockWorkshopTapInput();
    this.#updateView({ navigation: false, details: false, sheet: false });
    if (!this.#treasuryService) {
      if (this.#service.vault) this.#treasuryService = new LiveTreasuryService(this.#service.vault);
      else {
        let storage: Storage | undefined;
        try { storage = window.localStorage; } catch { /* Preview can run in memory. */ }
        this.#treasuryService = new PreviewTreasuryService({ ...(storage ? { storage } : {}), delay: () => new Promise(resolve => setTimeout(resolve, 180)) });
      }
    }
    this.#treasuryController = new TreasuryController({ root: this.#marketScreen, api: this.#treasuryService, market: this.#service.market,
      analytics: this.#analytics,
      locale: this.#state.settings.locale, reducedMotion: this.#state.settings.reducedMotion,
      ...(initialTab ? { initialTab } : {}), onExit: () => {
        if (returnTo === "market") { this.#openTradeQuarter(); return; }
        this.#navigate("core");
        const opener = this.#root.querySelector<HTMLElement>('[data-action="open-profile"]');
        if (returnTo === "profile" && opener) this.#openSheet("profile", opener);
      },
      onHome: () => this.#navigate("core"),
    });
  }

  #transitionView(update: () => void): void {
    if (this.#state?.settings.reducedMotion === true || typeof document.startViewTransition !== "function") {
      update();
      return;
    }

    const sequence = ++this.#viewTransitionSequence;
    this.#shell.dataset.viewTransition = "active";
    const transition = document.startViewTransition(update);
    void transition.finished
      .catch(() => undefined)
      .finally(() => {
        if (sequence === this.#viewTransitionSequence) delete this.#shell.dataset.viewTransition;
      });
  }

  #openSheet(sheet: SheetId, opener: HTMLElement): void {
    if (this.#sheet !== null) this.#closeSheet();
    this.#blockWorkshopTapInput();
    this.#focusBeforeSheet = opener;
    this.#sheet = sheet;
    if (sheet === "profile" || sheet === "partner-transfer") this.#profileNativeBack = installMarketNativeBack(() => {
      if (this.#sheet === "partner-transfer") this.#leavePartnerTransfer();
      else this.#closeSheet();
    });
    this.#renderSheet();
    this.#sheetLayer.hidden = false;
    document.body.classList.add("sheet-open");
    this.#syncCoreRendering();
    window.requestAnimationFrame(() =>
      this.#sheetContent.querySelector<HTMLElement>("[role='dialog'] button, [role='dialog'] [href], .profile-page button")?.focus(),
    );
  }

  #leavePartnerTransfer(): void {
    const returnToRewards = this.#transferReturnToRewards;
    this.#closeSheet();
    if (returnToRewards && this.#state) this.#openSheet("reward-account", this.#root.querySelector<HTMLElement>('[data-action="open-profile"]') ?? this.#root);
  }

  #closeSheet(): void {
    if (this.#sheet === null) return;
    this.#rewardTransferPanel?.destroy(); this.#rewardTransferPanel = null;
    this.#transferReturnToRewards = false;
    this.#profileNativeBack?.(); this.#profileNativeBack = null;
    this.#spendWait?.controller.abort();
    if (this.#sheet === "support") {
      this.#supportDrafts.set(this.#supportReference || "new", this.#supportDraft);
      this.#supportWatchStop?.(); this.#supportWatchStop = null;
      this.#supportLoadSequence++; this.#supportLoading = false;
      this.#supportRefreshQueued = false;
    }
    this.#profileMarketRuntime?.destroy(); this.#profileMarketRuntime = null;
    this.#profileRenderKey = "";
    const closingOfflineReward = this.#sheet === "offline-reward";
    this.#disposePremiumRuntime();
    this.#disposeRewardRevealRuntime();
    this.#disposeRewardVaultRuntime();
    this.#sheet = null;
    // A support notification must not discard the offline reward dialog on entry.
    queueMicrotask(() => this.#consumeSupportLink());
    this.#premiumShopTouchX = null;
    this.#premiumShopTouchY = null;
    this.#premiumShopTouchStartY = null;
    this.#premiumShopTouchMoved = false;
    this.#premiumShopSuppressClicksUntil = 0;
    this.#premiumPreviewSkinId = null;
    if (closingOfflineReward) {
      this.#offlineLaunchSequence += 1;
      this.#offlineWarmupUnsubscribe?.();
      this.#offlineWarmupUnsubscribe = null;
      this.#offlineRuntime?.destroy();
      this.#offlineRuntime = null;
      this.#offlineReward = null;
      this.#offlineCollecting = false;
      this.#shell.removeAttribute("data-offline-reward");
    }
    this.#sheetLayer.hidden = true;
    this.#sheetContent.innerHTML = "";
    document.body.classList.remove("sheet-open");
    this.#syncCoreRendering();
    this.#releaseWorkshopTapInput(this.#lastInteractionWasKeyboard);
    if (this.#lastInteractionWasKeyboard) {
      this.#focusBeforeSheet?.focus({ preventScroll: true });
    } else {
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
      this.#focusBeforeSheet?.blur();
    }
    this.#focusBeforeSheet = null;
    this.#rewardRevealAchievement = null;
    this.#selectedCollectible = null;
    this.#pendingUpgradeCode = null;
    this.#pendingUpgradeCommandId = null;
    if (closingOfflineReward) {
      if (this.#activeSection === "core" && !this.#mapOpen && !this.#marketOpen) this.#runtime?.scene.setVisualPaused(false);
      this.#syncOnboarding();
      this.#restoreOnboardingViewport();
    }
  }

  #showOfflineReward(gain: string, seconds: number): void {
    if (this.#state === null || gain === "0") return;
    if (this.#sheet !== null) this.#closeSheet();
    this.#blockWorkshopTapInput();
    this.#offlineReward = { gain, seconds };
    this.#offlineCollecting = false;
    this.#focusBeforeSheet = null;
    this.#sheet = "offline-reward";
    this.#renderSheet();
    this.#sheetLayer.hidden = false;
    this.#shell.dataset.offlineReward = "loading";
    document.body.classList.add("sheet-open");
    this.#runtime?.scene.setVisualPaused(true);
    this.#prepareOfflineRewardRuntime();
  }

  #blockWorkshopTapInput(): void {
    window.clearTimeout(this.#sheetTapReleaseTimer);
    this.#sheetTapReleaseTimer = 0;
    this.#workshopTapBlockedUntil = Number.POSITIVE_INFINITY;
    this.#runtime?.scene.setInteractionBlocked(true);
  }

  #releaseWorkshopTapInput(immediately = false): void {
    window.clearTimeout(this.#sheetTapReleaseTimer);
    if (this.#marketOpen) return;
    if (immediately) {
      this.#sheetTapReleaseTimer = 0;
      this.#workshopTapBlockedUntil = 0;
      this.#runtime?.scene.setInteractionBlocked(false);
      return;
    }
    this.#workshopTapBlockedUntil = performance.now() + SHEET_TAP_RELEASE_DELAY_MS;
    this.#sheetTapReleaseTimer = window.setTimeout(() => {
      this.#sheetTapReleaseTimer = 0;
      if (this.#sheet !== null || this.#marketOpen) return;
      this.#workshopTapBlockedUntil = 0;
      this.#runtime?.scene.setInteractionBlocked(false);
    }, SHEET_TAP_RELEASE_DELAY_MS);
  }

  #prepareOfflineRewardRuntime(): void {
    if (this.#sheet !== "offline-reward" || this.#state === null) return;
    const sequence = ++this.#offlineLaunchSequence;
    this.#offlineWarmupUnsubscribe?.();
    this.#offlineRuntime?.destroy();
    this.#offlineRuntime = null;
    this.#shell.dataset.offlineReward = "loading";

    const dialog = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-dialog");
    const loader = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader");
    const progress = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader__progress");
    const retry = this.#sheetContent.querySelector<HTMLButtonElement>("[data-action='retry-offline-reward']");
    const action = this.#sheetContent.querySelector<HTMLButtonElement>("[data-action='collect-offline-reward']");
    dialog?.setAttribute("data-reward-ready", "false");
    loader?.setAttribute("data-state", "loading");
    loader?.setAttribute("aria-hidden", "false");
    progress?.removeAttribute("hidden");
    if (retry !== null) retry.hidden = true;
    if (action !== null) action.disabled = true;
    const loadingTitle = loader?.querySelector<HTMLElement>("[data-offline-loading-title]");
    const loadingBody = loader?.querySelector<HTMLElement>("[data-offline-loading-body]");
    if (loadingTitle !== null && loadingTitle !== undefined) loadingTitle.textContent = this.#translator("offline.loadingTitle");
    if (loadingBody !== null && loadingBody !== undefined) loadingBody.textContent = this.#translator("offline.loadingBody");

    this.#offlineWarmupUnsubscribe = offlineRewardAssetWarmup.subscribe((state) => {
      if (sequence !== this.#offlineLaunchSequence || this.#sheet !== "offline-reward") return;
      this.#updateOfflineRewardLoadProgress(state, 0, 80);
    });
    const warmup = offlineRewardAssetWarmup.warm();
    window.requestAnimationFrame(() => {
      void warmup.then(() => this.#mountOfflineRewardRuntime(sequence));
    });
  }

  #mountOfflineRewardRuntime(sequence: number): void {
    if (sequence !== this.#offlineLaunchSequence || this.#sheet !== "offline-reward" || this.#state === null) return;
    try {
      const host = this.#sheetContent.querySelector<HTMLElement>("#offline-reward-stage");
      if (host === null) return;
      const runtime = createOfflineRewardGame(
        host,
        {
          onCollectRequested: () => void this.#collectOfflineReward(),
          onCollectComplete: () => this.#finishOfflineReward(),
          onLoadProgress: (progress) => this.#setOfflineRewardLoadProgress(80 + progress * 18),
          onLoadError: () => this.#showOfflineRewardLoadError(sequence),
        },
        this.#state.settings.reducedMotion,
      );
      this.#offlineRuntime = runtime;
      void runtime.ready.then(() => this.#markOfflineRewardReady(sequence, runtime));
    } catch (error) {
      console.error("Offline reward scene failed to start", error);
      this.#showOfflineRewardLoadError(sequence);
    }
  }

  #updateOfflineRewardLoadProgress(state: OfflineRewardWarmupSnapshot, from: number, span: number): void {
    this.#setOfflineRewardLoadProgress(from + state.progress * span);
  }

  #setOfflineRewardLoadProgress(value: number): void {
    const progress = Math.max(0, Math.min(100, Math.round(value)));
    const meter = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader__progress");
    meter?.style.setProperty("--offline-load-progress", `${progress}%`);
    meter?.setAttribute("aria-valuenow", String(progress));
  }

  #markOfflineRewardReady(sequence: number, runtime: OfflineRewardRuntime): void {
    if (sequence !== this.#offlineLaunchSequence || this.#sheet !== "offline-reward" || this.#offlineRuntime !== runtime) {
      return;
    }
    this.#offlineWarmupUnsubscribe?.();
    this.#offlineWarmupUnsubscribe = null;
    this.#setOfflineRewardLoadProgress(100);
    this.#shell.dataset.offlineReward = "open";
    const dialog = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-dialog");
    const loader = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader");
    const action = this.#sheetContent.querySelector<HTMLButtonElement>("[data-action='collect-offline-reward']");
    dialog?.setAttribute("data-reward-ready", "true");
    loader?.setAttribute("data-state", "ready");
    loader?.setAttribute("aria-hidden", "true");
    if (action !== null) {
      action.disabled = false;
      window.requestAnimationFrame(() => action.focus());
    }
  }

  #showOfflineRewardLoadError(sequence: number): void {
    if (sequence !== this.#offlineLaunchSequence || this.#sheet !== "offline-reward") return;
    this.#offlineWarmupUnsubscribe?.();
    this.#offlineWarmupUnsubscribe = null;
    this.#shell.dataset.offlineReward = "error";
    const loader = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader");
    const progress = this.#sheetContent.querySelector<HTMLElement>(".offline-reward-loader__progress");
    const retry = this.#sheetContent.querySelector<HTMLButtonElement>("[data-action='retry-offline-reward']");
    const title = loader?.querySelector<HTMLElement>("[data-offline-loading-title]");
    const body = loader?.querySelector<HTMLElement>("[data-offline-loading-body]");
    loader?.setAttribute("data-state", "error");
    progress?.setAttribute("hidden", "");
    if (title !== null && title !== undefined) title.textContent = this.#translator("offline.loadingError");
    if (body !== null && body !== undefined) body.textContent = this.#translator("offline.loadingErrorBody");
    if (retry !== null) {
      retry.hidden = false;
      retry.focus();
    }
  }

  #retryOfflineReward(): void {
    if (this.#sheet !== "offline-reward") return;
    offlineRewardAssetWarmup.reset();
    this.#prepareOfflineRewardRuntime();
  }

  async #collectOfflineReward(): Promise<void> {
    if (this.#sheet !== "offline-reward" || this.#offlineCollecting) return;
    this.#offlineCollecting = true;
    this.#shell.dataset.offlineReward = "collecting";
    const action = this.#sheetContent.querySelector<HTMLButtonElement>("[data-action='collect-offline-reward']");
    if (action !== null) action.disabled = true;
    this.#haptics.success();
    const runtime = this.#offlineRuntime;
    if (runtime === null) {
      window.setTimeout(() => this.#finishOfflineReward(), 120);
      return;
    }
    await runtime.ready;
    if (this.#sheet === "offline-reward" && this.#offlineRuntime === runtime) runtime.scene.collect();
  }

  #finishOfflineReward(): void {
    if (this.#sheet !== "offline-reward" || this.#offlineReward === null || this.#state === null) return;
    this.#liveRegion.textContent = this.#translator("status.offline", {
      value: formatCurrencyAmount(this.#offlineReward.gain, this.#state.settings.locale),
    });
    this.#closeSheet();
  }

  #handleKeydown(event: KeyboardEvent): void {
    if (
      event.key === "Tab" ||
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Escape" ||
      event.key.startsWith("Arrow")
    ) {
      this.#lastInteractionWasKeyboard = true;
    }
    const eventTarget = event.target instanceof Element ? event.target : null;
    const progressTab = eventTarget?.closest<HTMLElement>("[role='tab'][data-action='progress-segment']");
    if (progressTab != null && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const segments: readonly ProgressSegment[] = ["today", "achievements", "season"];
      const current = segments.indexOf(this.#progressSegment);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? segments.length - 1
            : (current + (event.key === "ArrowRight" ? 1 : -1) + segments.length) % segments.length;
      this.#progressSegment = segments[next] ?? "today";
      this.#renderDetails();
      window.requestAnimationFrame(() => this.#detailScreen.querySelector<HTMLElement>(`#progress-tab-${this.#progressSegment}`)?.focus());
      return;
    }
    const clubTab = eventTarget?.closest<HTMLElement>("[role='tab'][data-action='friend-filter']");
    if (clubTab != null && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const filters: readonly FriendFilter[] = ["all", 1, 2];
      const current = filters.indexOf(this.#friendFilter);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? filters.length - 1
            : (current + (event.key === "ArrowRight" ? 1 : -1) + filters.length) % filters.length;
      this.#friendFilter = filters[next] ?? "all";
      this.#friendPage = 1;
      void this.#loadClubRoster();
      window.requestAnimationFrame(() =>
        this.#detailScreen.querySelector<HTMLElement>(`#club-tab-${this.#friendFilter}`)?.focus(),
      );
      return;
    }
    // TON Connect owns its portal and keyboard handling while its wallet modal is above the game.
    if (this.#marketOpen && [...document.querySelectorAll<HTMLElement>("[data-tc-modal]")].some(modal => modal.getClientRects().length > 0)) return;
    if (this.#sheet === null) {
      if (this.#marketOpen && event.key === "Escape") { event.preventDefault(); if (this.#treasuryController) this.#treasuryController.back(); else this.#marketController?.back(); }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.#closeSheet();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...this.#sheetContent.querySelectorAll<HTMLElement>(
      ":is([role='dialog'], .profile-page) :is(button:not([disabled]), [href], input, textarea, select, summary)",
    )].filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first && last !== undefined) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last && first !== undefined) {
      event.preventDefault();
      first.focus();
    }
  }

  async #retryNetwork(showFeedback = true): Promise<void> {
    if (this.#networkRecoveryRunning) return;
    this.#networkRecoveryRunning = true;
    this.#service.resumePendingTaps();
    try {
      // Probe the API even when an outbox exists. Browser online events alone
      // cannot detect recovery from a server restart or a stalled connection.
      const result = await this.#service.refreshState();
      this.#service.setOnline(true);
      this.#applyEnvelope(result, { animateBalance: true, ...this.#liveRefreshBoundaries(this.#state, result.state) });
      if (showFeedback) this.#showToast(this.#translator("status.restored"), "success");
    } catch (error) {
      this.#service.setOnline(false);
      if (showFeedback) this.#handleServiceError(error);
      else this.#updateView({ details: false, navigation: false, sheet: false });
    } finally {
      this.#networkRecoveryRunning = false;
      this.#networkRetryAt = performance.now() + 5_000;
    }
  }

  async #applyScenario(scenario: DevScenarioId): Promise<void> {
    this.#closeTradeQuarter();
    window.clearTimeout(this.#arenaResultExitTimer);
    this.#arenaResultExitTimer = 0;
    this.#arenaResultExit = null;
    this.#pvpBusy = false;
    if (this.#shell !== undefined) delete this.#shell.dataset.arenaExitBusy;
    const result = await this.#service.seedScenario(scenario);
    this.#trophyPreviewCount = null;
    this.#state = result.state;
    this.#serverNow = result.serverNow;
    this.#serverAnchorPerformance = performance.now();
    this.#tapBatchCount = 0;
    this.#tapBatchReward = 0n;
    this.#pendingTapCount = 0;
    this.#pendingTapReward = 0n;
    this.#servicePendingTapCount = 0;
    this.#servicePendingTapReward = 0n;
    this.#displayedBalance = BigInt(result.state.account.available);
    this.#translator = createTranslator(result.state.settings.locale);
    this.#mapOpen = false;
    this.#achievementFilter = "all";
    this.#friendFilter = "all";
    this.#friendQuery = "";
    this.#friendPage = 1;
    this.#arenaView = scenario === "active-pvp" || scenario === "arena-result-stack" ? "penalty" : "catalog";
    this.#activeSection =
      scenario === "empty-friends"
        ? "friends"
        : scenario === "active-pvp" || scenario === "arena-ready" || scenario === "arena-result-stack"
          ? "games"
          : "core";
    if (scenario.startsWith("season-") || scenario.startsWith("reward-")) {
      this.#activeSection = "progress";
      this.#progressSegment = "season";
    }
    this.#restoreOnboardingContext();
    this.#closeSheet();
    this.#syncWorkshop(false);
    this.#updateView({ navigation: true, details: true, sheet: true });
    if (result.offlineGain !== "0") {
      this.#showOfflineReward(result.offlineGain, result.offlineSeconds);
    } else {
      this.#syncOnboarding();
      this.#restoreOnboardingViewport();
    }
  }

  async #syncConfirmedState(): Promise<void> {
    if (this.#destroyed || document.hidden) return;
    if (!this.#service.getOnline()) {
      if (performance.now() >= this.#networkRetryAt) await this.#retryNetwork(false);
      return;
    }
    try {
      const previous = this.#state;
      const result = await this.#service.syncProduction();
      const refresh = this.#liveRefreshBoundaries(previous, result.state);
      const seasonActivated = previous?.season.status !== "active" && result.state.season.status === "active";
      if (seasonActivated) {
        if (this.#sheet !== null) this.#closeSheet();
        this.#restoreOnboardingContext(result.state);
      }
      this.#applyEnvelope(result, {
        animateBalance: result.state.revision !== previous?.revision,
        navigation: seasonActivated,
        details: seasonActivated || refresh.details,
        sheet: seasonActivated || refresh.sheet,
      });
      if (seasonActivated) this.#restoreOnboardingViewport();
    } catch (error) {
      this.#handleServiceError(error);
    }
  }

  #liveRefreshBoundaries(previous: GameState | null, next: GameState): { details: boolean; sheet: boolean } {
    if (previous === null) return { details: false, sheet: false };
    const upgradeAvailabilityChanged = ([
      "mechanicalCutter",
      "precisionCutter",
      "energyCapacity",
      "energyRecovery",
    ] as const).some((code) => {
      const previousUpgrade = getNextUpgradeLevel(previous, code);
      const nextUpgrade = getNextUpgradeLevel(next, code);
      const previousAffordable =
        previousUpgrade?.costToReach != null && amount(previous.account.available) >= amount(previousUpgrade.costToReach);
      const nextAffordable =
        nextUpgrade?.costToReach != null && amount(next.account.available) >= amount(nextUpgrade.costToReach);
      return previousAffordable !== nextAffordable;
    });
    const dailyGoalCountChanged = dailyGoalsCompletedCount(previous) !== dailyGoalsCompletedCount(next);
    const dailyRouteChanged =
      previous.progress.daily.rewardClaimed !== next.progress.daily.rewardClaimed ||
      previous.progress.dailyStreak.current !== next.progress.dailyStreak.current ||
      previous.progress.daily.utcDay !== next.progress.daily.utcDay ||
      previous.progress.daily.assignmentCodes.length !== next.progress.daily.assignmentCodes.length ||
      previous.progress.daily.assignmentCodes.some(
        (code, index) => code !== next.progress.daily.assignmentCodes[index],
      );
    const projectionVisibilityChanged = previous.season.projection.visible !== next.season.projection.visible;
    const energyChanged =
      previous.energy.current !== next.energy.current ||
      previous.energy.capacity !== next.energy.capacity ||
      previous.energy.refillsUsedToday !== next.energy.refillsUsedToday;

    return {
      details:
        (this.#activeSection === "development" && upgradeAvailabilityChanged) ||
        (this.#activeSection === "progress" &&
          this.#progressSegment === "today" &&
          (dailyGoalCountChanged || dailyRouteChanged)) ||
        (this.#activeSection === "progress" && (
          previous.progress.claimedAchievementIds.join("|") !== next.progress.claimedAchievementIds.join("|") ||
          previous.progress.seasonal.claimedCodes.join("|") !== next.progress.seasonal.claimedCodes.join("|") ||
          (this.#progressSegment === "season" && projectionVisibilityChanged))),
      sheet:
        (this.#sheet === "upgrade" && upgradeAvailabilityChanged) ||
        (this.#sheet === "energy" && (energyChanged ||
          previous.account.available !== next.account.available || previous.account.tapReward !== next.account.tapReward)) ||
        (this.#sheet === "balance" && projectionVisibilityChanged),
    };
  }

  async #handleVisibility(): Promise<void> {
    this.#syncCoreRendering();
    if (document.hidden) {
      this.#runtime?.scene.setMusicSuspended(true);
      this.#runtime?.scene.setVisualPaused(true);
      this.#arenaRuntime?.setPaused(true);
      // Durable delivery continues on its own; a suspended WebView must not
      // report an unhandled rejection when its background timer resumes.
      await this.#flushTaps().catch(() => undefined);
      return;
    }
    this.#runtime?.scene.setMusicSuspended(false);
    if (this.#activeSection === "core" && !this.#mapOpen && !this.#marketOpen) this.#runtime?.scene.setVisualPaused(false);
    if (this.#activeSection === "games" && !this.#mapOpen && !this.#marketOpen) this.#arenaRuntime?.setPaused(false);
    this.#service.resumePendingTaps();
    this.#networkRetryAt = 0;
    await this.#syncConfirmedState();
  }

  #applyEnvelope(
    envelope: ServiceEnvelope,
    options: { animateBalance?: boolean; navigation?: boolean; details?: boolean; sheet?: boolean } = {},
  ): void {
    if (envelope.pendingTaps !== undefined) this.#synchronizePendingTaps(envelope.pendingTaps);
    if (this.#state !== null && envelope.state.revision < this.#state.revision) {
      this.#animateBalance(BigInt(this.#state.account.available) + this.#pendingTapReward);
      this.#syncWorkshop(false);
      return;
    }
    const previousState = this.#state;
    const previousStage = previousState?.workshopStage;
    const preserveCurrentClubPage = previousState !== null && this.#activeSection === "friends";
    const confirmedStateChanged = previousState !== null && envelope.state.revision !== previousState.revision;
    const nextState = preserveCurrentClubPage
      ? {
          ...envelope.state,
          friends: previousState.friends,
          clubRoster: previousState.clubRoster,
        }
      : envelope.state;
    this.#trackStateAnalytics(previousState, nextState);
    this.#state = nextState;
    this.#serverNow = envelope.serverNow;
    this.#serverAnchorPerformance = performance.now();
    this.#translator = createTranslator(nextState.settings.locale);
    this.#haptics.setEnabled(nextState.settings.vibration);
    this.#syncWorkshop(previousStage !== undefined && previousStage !== nextState.workshopStage);
    const visibleBalance = BigInt(nextState.account.available) + this.#pendingTapReward;
    if (options.animateBalance === true) this.#animateBalance(visibleBalance);
    else this.#displayedBalance = visibleBalance;
    this.#updateView({
      navigation: options.navigation ?? false,
      details: options.details ?? false,
      sheet: options.sheet ?? false,
    });
    this.#syncArenaPresentation(previousState);
    this.#syncOnboarding(previousState);
    if (preserveCurrentClubPage && confirmedStateChanged) void this.#loadClubRoster();
  }

  #trackStateAnalytics(previous: GameState | null, next: GameState): void {
    const previousDailyComplete = previous === null ? 0 : dailyGoalsCompletedCount(previous);
    const nextDailyComplete = dailyGoalsCompletedCount(next);
    if (nextDailyComplete === 3 && previousDailyComplete < 3 && !next.progress.daily.rewardClaimed) {
      this.#analytics.track("daily_route_complete", { utcDay: next.progress.daily.utcDay });
    }

    for (const achievement of GAME_CONFIG.progress.achievements) {
      if (next.progress.claimedAchievementIds.includes(achievement.id)) continue;
      const ready = achievementProgress(next, achievement).ready;
      const wasReady = previous === null
        ? false
        : previous.progress.claimedAchievementIds.includes(achievement.id) ||
          achievementProgress(previous, achievement).ready;
      if (ready && !wasReady) {
        this.#analytics.track("achievement_ready", { achievementId: achievement.id });
      }
    }

    if (next.pvp.status === "idle") this.#trackedArenaMatchId = null;
    if (
      next.pvp.matchId !== null &&
      next.pvp.opponentKind !== null &&
      next.pvp.matchId !== this.#trackedArenaMatchId
    ) {
      this.#trackedArenaMatchId = next.pvp.matchId;
      this.#analytics.track(
        next.pvp.opponentKind === "guardian" ? "arena_guardian_match" : "arena_pvp_match",
        { gameId: next.pvp.gameId ?? "unknown" },
      );
    }
  }

  #syncWorkshop(animate: boolean): void {
    if (this.#state === null || this.#runtime === null) return;
    const reducedMotion = this.#state.settings.reducedMotion;
    this.#runtime.scene.setWorkshopStage(this.#state.workshopStage);
    this.#runtime.scene.setSelectedSkin(this.#state.collection.selectedSkinId);
    if (animate && this.#state.workshopStage >= 2) this.#runtime.scene.playUpgrade();
    this.#runtime.scene.setReducedMotion(reducedMotion);
    this.#runtime.scene.setNetworkOnline(this.#service.getOnline());
    this.#runtime.scene.setEconomyActive(this.#state.season.status === "active");
    this.#runtime.scene.setEnergyState(
      this.#visibleEnergy().current,
      this.#state.energy.capacity,
    );
    const boost = tapBoostAt(this.#state, this.#estimatedServerNow());
    this.#runtime.scene.setTapReward(boost.reward, boost.active !== undefined);
    this.#energyRuntimes.forEach((runtime) => runtime.scene.setReducedMotion(reducedMotion));
    this.#energySigilRuntime?.scene.setCharge(this.#state.energy.current, this.#state.energy.capacity);
    this.#energySigilRuntime?.scene.setReducedMotion(reducedMotion);
  }

  #observeWorkshopSize(workshopHost: HTMLElement): void {
    if (typeof ResizeObserver === "undefined") return;

    this.#workshopWidth = 0;
    this.#workshopHeight = 0;
    this.#workshopResizeObserver = new ResizeObserver(([entry]) => {
      if (entry === undefined || this.#destroyed || this.#runtime === null) return;
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      if (width <= 0 || height <= 0 || (width === this.#workshopWidth && height === this.#workshopHeight)) return;

      this.#workshopWidth = width;
      this.#workshopHeight = height;
      window.cancelAnimationFrame(this.#workshopResizeFrame);
      this.#workshopResizeFrame = window.requestAnimationFrame(() => {
        if (this.#destroyed || this.#runtime === null) return;
        this.#runtime.resize(this.#workshopWidth, this.#workshopHeight);
      });
    });
    this.#workshopResizeObserver.observe(workshopHost);
  }

  #animateBalance(target: string | bigint): void {
    const from = this.#displayedBalance;
    const to = BigInt(target);
    window.cancelAnimationFrame(this.#balanceAnimation);
    if (from === to || this.#state?.settings.reducedMotion === true) {
      this.#displayedBalance = to;
      this.#updateBalanceText();
      return;
    }

    const started = performance.now();
    const duration = GAME_CONFIG.motion.balanceMs;
    const step = (now: number) => {
      const ratio = Math.min(1, (now - started) / duration);
      const eased = 1 - (1 - ratio) ** 3;
      const scaled = BigInt(Math.round(eased * 1_000));
      this.#displayedBalance = from + ((to - from) * scaled) / 1_000n;
      this.#updateBalanceText();
      if (ratio < 1) this.#balanceAnimation = window.requestAnimationFrame(step);
      else this.#displayedBalance = to;
    };
    this.#balanceAnimation = window.requestAnimationFrame(step);
  }

  #updateView(options: { navigation: boolean; details: boolean; sheet: boolean }): void {
    if (this.#state === null) return;
    const state = this.#state;
    const locale = state.settings.locale;
    this.#root.querySelector<HTMLElement>(".avatar-button")?.setAttribute("aria-label", this.#translator("header.profile"));
    const avatar = this.#root.querySelector<HTMLElement>(".avatar-frame");
    const avatarKey = JSON.stringify([state.profile.displayName, state.profile.photoUrl ?? null]);
    if (avatar !== null && avatar.dataset.avatarKey !== avatarKey) {
      avatar.dataset.avatarKey = avatarKey;
      avatar.innerHTML = avatarContents(state.profile);
    }
    this.#shell.dataset.section = this.#activeSection;
    this.#shell.dataset.view = this.#marketOpen ? "market" : this.#mapOpen ? "map" : this.#activeSection === "core" ? "core" : "detail";
    this.#shell.dataset.stage = String(state.workshopStage);
    this.#shell.dataset.network = this.#service.getOnline() ? "online" : "offline";
    this.#shell.dataset.reducedMotion = String(state.settings.reducedMotion);
    this.#shell.dataset.energy = state.energy.current === 0 ? "empty" : state.energy.current < state.energy.capacity * 0.2 ? "low" : "ready";
    const rewards = readyRewardSummary(state);
    const globalRewardCount = rewards.total;
    this.#shell.dataset.rewardAlert = globalRewardCount > 0 ? "ready" : "idle";
    this.#shell.dataset.dailyRewardAlert = rewards.daily > 0 ? "ready" : "idle";
    this.#shell.dataset.achievementRewardAlert = rewards.achievements > 0 ? "ready" : "idle";
    this.#shell.dataset.seasonRewardAlert = rewards.season > 0 ? "ready" : "idle";
    this.#root.querySelectorAll<HTMLElement>("[data-global-reward-count]").forEach((element) => {
      element.textContent = String(globalRewardCount);
    });
    const rewardAnnouncement = this.#translator("hall.globalRewardReady", { count: globalRewardCount });
    this.#root.querySelector<HTMLElement>(".quarter-portal")?.setAttribute(
      "aria-label",
      globalRewardCount > 0 ? `${this.#translator("map.open")}. ${rewardAnnouncement}` : this.#translator("map.open"),
    );
    const progressLandmark = this.#root.querySelector<HTMLElement>(".quarter-landmark[data-section='progress']");
    if (progressLandmark !== null) progressLandmark.dataset.rewardReady = String(globalRewardCount > 0);
    progressLandmark?.setAttribute(
      "aria-label",
      globalRewardCount > 0 ? `${this.#translator("map.openProgress")}. ${rewardAnnouncement}` : this.#translator("map.openProgress"),
    );
    document.documentElement.lang = locale;

    this.#updateBalanceText();
    const seasonHeader = seasonHeaderPresentation(state, this.#serverNow, this.#translator);
    const seasonClock = this.#seasonValue.closest<HTMLElement>(".season-clock");
    this.#seasonLabel.textContent = seasonHeader.label;
    this.#seasonPrimaryValue.textContent = seasonHeader.value;
    this.#seasonValue.textContent = seasonHeader.countdown;
    if (seasonClock !== null) {
      seasonClock.dataset.seasonMode = seasonHeader.mode;
      seasonClock.setAttribute("aria-label", seasonHeader.ariaLabel);
    }
    this.#passiveValue.textContent =
      state.account.passivePerHour === "0"
        ? this.#translator("header.noPassive")
        : this.#translator("header.passive", { value: formatPassiveRate(state.account.passivePerHour, locale) });
    this.#reservedValue.textContent =
      state.account.reserved === "0"
        ? ""
        : this.#translator("header.inGame", { value: formatCurrencyAmount(state.account.reserved, locale) });
    this.#balanceButton.setAttribute(
      "aria-label",
      `${this.#translator("header.balanceDetails")}. ${this.#translator(
        state.season.projection.visible ? "header.estimatedReward" : "balance.estimatePreparing",
      )}: ${this.#balanceEstimate.textContent ?? ""}`,
    );
    this.#coreHint.hidden = state.meta.firstTapHintSeen || state.season.status !== "active";

    const pendingTransport = this.#service.getPendingTaps();
    const tapSyncDelayed = pendingTransport.delayed && pendingTransport.count > 0;
    const networkTitle = this.#networkBanner.querySelector<HTMLElement>("[data-network-title]");
    const networkBody = this.#networkBanner.querySelector<HTMLElement>("[data-network-body]");
    if (networkTitle !== null) networkTitle.textContent = this.#translator(tapSyncDelayed ? "status.tapSyncTitle" : "status.networkTitle");
    if (networkBody !== null) networkBody.textContent = this.#translator(tapSyncDelayed ? "status.tapSyncBody" : "status.networkBody");
    // Healthy delivery is already indicated beside energy. A warning across
    // the balance (with a manual retry) turns normal background work into an alarm.
    this.#networkBanner.hidden = this.#service.getOnline();
    this.#runtime?.scene.setNetworkOnline(this.#service.getOnline());
    const mapActive = !this.#marketOpen && this.#mapOpen;
    const coreActive = !this.#marketOpen && !mapActive && this.#activeSection === "core";
    const detailActive = !this.#marketOpen && !mapActive && this.#activeSection !== "core";
    const arenaActive = detailActive && this.#activeSection === "games" && !document.hidden;
    if (!coreActive) this.#runtime?.scene.setVisualPaused(true);
    this.#arenaRuntime?.setPaused(!arenaActive);
    this.#coreScreen.classList.toggle("is-inactive", !coreActive);
    this.#coreScreen.toggleAttribute("inert", !coreActive);
    this.#coreScreen.setAttribute("aria-hidden", String(!coreActive));
    this.#mapScreen.classList.toggle("is-inactive", !mapActive);
    this.#mapScreen.toggleAttribute("inert", !mapActive);
    this.#mapScreen.setAttribute("aria-hidden", String(!mapActive));
    this.#detailScreen.classList.toggle("is-inactive", !detailActive);
    this.#detailScreen.toggleAttribute("inert", !detailActive);
    this.#detailScreen.setAttribute("aria-hidden", String(!detailActive));
    this.#marketScreen.classList.toggle("is-inactive", !this.#marketOpen);
    this.#marketScreen.toggleAttribute("inert", !this.#marketOpen);
    this.#marketScreen.setAttribute("aria-hidden", String(!this.#marketOpen));
    const header = this.#root.querySelector<HTMLElement>(".game-header");
    header?.toggleAttribute("inert", this.#marketOpen);
    header?.setAttribute("aria-hidden", String(this.#marketOpen));
    this.#syncCoreRendering();
    if (options.details && !this.#marketOpen) this.#renderDetails();
    if (options.sheet && this.#sheet !== null) this.#renderSheet();
    this.#updateLiveSectionValues();
    this.#syncRewardClaimButtons();
    this.#applyPreferences();
  }

  #updateTapBoost(): void {
    if (this.#state === null) return;
    const now = this.#estimatedServerNow();
    const boost = tapBoostAt(this.#state, now);
    this.#runtime?.scene.setTapReward(boost.reward, boost.active !== undefined);
    const badge = this.#root.querySelector<HTMLElement>(".core-tap-boost");
    const html = renderTapBoostStatus(this.#state, now, this.#translator);
    if (badge !== null) {
      badge.hidden = html === "";
      if (badge.innerHTML !== html) badge.innerHTML = html;
    }
    this.#coreHint.hidden = html !== "" || this.#state.meta.firstTapHintSeen || this.#state.season.status !== "active";
    this.#root.querySelectorAll<HTMLElement>("[data-tap-boost-status]").forEach((element) => {
      const content = renderTapBoostStatus(this.#state!, now, this.#translator, true);
      element.hidden = content === "";
      if (element.innerHTML !== content) element.innerHTML = content;
    });
  }

  #updateLiveSectionValues(): void {
    if (this.#state === null) return;
    const state = this.#state;
    const locale = state.settings.locale;
    const unit = formatCurrencyUnit(state.account.available, locale);

    this.#root.querySelectorAll<HTMLElement>('[data-live-value="available"]').forEach((element) => {
      element.textContent = formatAmount(state.account.available, locale, element.dataset.liveCompact === "true");
      element.dataset.digits = String(state.account.available.replace("-", "").length);
    });
    this.#root.querySelectorAll<HTMLElement>('[data-live-unit="available"]').forEach((element) => {
      element.textContent = ` ${unit}`;
    });
    this.#root.querySelectorAll<HTMLElement>('[data-live-exact="available"]').forEach((element) => {
      element.textContent = formatCurrencyAmount(state.account.available, locale);
    });

    const estimatedServerNow = new Date(this.#estimatedServerNow()).toISOString();
    this.#updateTapBoost();
    this.#root.querySelectorAll<HTMLElement>("[data-daily-reset-countdown]").forEach((element) => {
      element.textContent = formatDailyResetLabel(state, estimatedServerNow, this.#translator);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-premium-start-expires-at]").forEach((element) => {
      if (element.dataset.premiumStartPhase === "upcoming") return;
      const expiresAt = element.dataset.premiumStartExpiresAt;
      if (expiresAt === undefined) return;
      const expired = Date.parse(expiresAt) <= Date.parse(estimatedServerNow);
      element.dataset.premiumStartPhase = expired ? "expired" : "active";
      const countdown = element.querySelector<HTMLElement>("[data-premium-start-countdown]");
      if (countdown !== null) {
        countdown.textContent = expired
          ? this.#translator("shop.startWindow.expired")
          : this.#translator("shop.startWindow.active", {
              time: formatServerDuration(expiresAt, estimatedServerNow, locale),
            });
      }
    });
    this.#root.querySelectorAll<HTMLElement>("[data-premium-offer-expires-at]").forEach((offer) => {
      const expiresAt = offer.dataset.premiumOfferExpiresAt;
      if (expiresAt === undefined) return;
      const expired = Date.parse(expiresAt) <= Date.parse(estimatedServerNow);
      const timing = offer.querySelector<HTMLElement>(".premium-offer__body header > span");
      if (timing !== null) {
        timing.textContent = expired
          ? this.#translator("shop.finished")
          : this.#translator("shop.endsIn", { time: formatServerDuration(expiresAt, estimatedServerNow, locale) });
      }
      if (expired) offer.querySelector<HTMLButtonElement>("[data-action='purchase-premium-offer']")?.setAttribute("disabled", "");
    });

    this.#updateEnergyPresentation();

    const seasonCountdownTarget = getSeasonCountdownTarget(state.season);
    const countdown = getSeasonCountdownParts(seasonCountdownTarget, this.#serverNow);
    this.#root.querySelectorAll<HTMLElement>("[data-season-days]").forEach((element) => {
      element.textContent = String(countdown.days);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-hours]").forEach((element) => {
      element.textContent = String(countdown.hours);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-minutes]").forEach((element) => {
      element.textContent = String(countdown.minutes);
    });
    this.#root.querySelectorAll<HTMLElement>(".trophy-season-countdown").forEach((element) => {
      element.setAttribute("aria-label", formatSeasonCountdown(seasonCountdownTarget, this.#serverNow, locale));
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-fund]").forEach((element) => {
      element.textContent = formatUsdtCents(state.season.prizeFundUsdCents, locale);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-rank]").forEach((element) => {
      element.textContent = !state.season.projection.visible || state.season.projection.rank === null
        ? "—"
        : this.#translator("progress.place", { place: state.season.projection.rank });
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-rank-offset]").forEach((row) => {
      const place = row.querySelector<HTMLElement>(":scope > span");
      const offset = Number(row.dataset.seasonRankOffset ?? "0");
      if (place === null) return;
      place.textContent = !state.season.projection.visible || state.season.projection.rank === null || !Number.isFinite(offset)
        ? "—"
        : this.#translator("progress.place", { place: Math.max(1, state.season.projection.rank + offset) });
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-share]").forEach((element) => {
      element.textContent = state.season.projection.visible ? formatSharePpm(state.season.projection.sharePpm, locale) : "—";
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-estimate]").forEach((element) => {
      if (state.season.projection.visible) {
        element.textContent = formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true);
      }
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-estimate-leading]").forEach((element) => {
      if (state.season.projection.visible) {
        element.textContent = formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true);
      }
    });
    const revealTime = projectionRevealTime(state, this.#serverNow, this.#translator);
    this.#root.querySelectorAll<HTMLElement>("[data-season-projection-countdown]").forEach((element) => {
      element.textContent = element.dataset.seasonProjectionCountdown === "balance"
        ? this.#translator("balance.estimateRevealsIn", { time: revealTime })
        : this.#translator("hall.projectionPreparing", { time: revealTime });
    });
    this.#root.querySelectorAll<HTMLElement>("[data-season-fund-leading]").forEach((element) => {
      element.textContent = formatUsdtCents(state.season.prizeFundUsdCents, locale);
    });

    this.#root.querySelectorAll<HTMLElement>("[data-daily-task-code]").forEach((row) => {
      const code = row.dataset.dailyTaskCode;
      if (code === undefined) return;
      const task = dailyTaskProgress(state, code);
      const bounded = task.current > task.target ? task.target : task.current;
      const progress = task.target === 0n ? 100 : Number((bounded * 100n) / task.target);
      const value = row.querySelector<HTMLElement>("[data-daily-task-value]");
      if (value !== null) {
        value.textContent = task.complete
          ? this.#translator("progress.completed")
          : this.#translator("hall.progressValue", {
              current: formatAmount(bounded.toString(), locale),
              target: formatAmount(task.target.toString(), locale),
            });
      }
      const meter = row.querySelector<HTMLElement>("[data-daily-task-meter]");
      meter?.setAttribute("aria-valuenow", bounded.toString());
      meter?.querySelector<HTMLElement>("i")?.style.setProperty("--trophy-progress", String(progress / 100));
      row.closest(".trophy-trial")?.classList.toggle("is-complete", task.complete);
    });
    const dailyCompleteCount = dailyGoalsCompletedCount(state);
    const dailyReady = state.season.status === "active" && dailyCompleteCount === GAME_CONFIG.liveOps.daily.assignmentCount;
    const dailyReward = dailyRouteReward(state).toString();
    this.#root.querySelectorAll<HTMLElement>("[data-daily-hero-progress]").forEach((element) => {
      if (!state.progress.daily.rewardClaimed) {
        element.textContent = this.#translator("hall.heroDailyProgress", { current: dailyCompleteCount });
      }
    });
    this.#root.querySelectorAll<HTMLElement>(".trophy-daily").forEach((section) => {
      section.dataset.dailyCompleteCount = String(dailyCompleteCount);
      const reward = section.querySelector<HTMLElement>(".trophy-daily-reward");
      if (reward === null) return;
      const claimed = state.progress.daily.rewardClaimed;
      reward.classList.toggle("is-claimed", claimed);
      reward.classList.toggle("is-ready", dailyReady);
      const hint = reward.querySelector<HTMLElement>(".trophy-daily-reward__copy p");
      if (hint !== null) {
        hint.textContent = claimed ? this.#translator("hall.rewardStored") : dailyReady
          ? this.#translator("hall.rewardReady")
          : dailyRemainingRewardHint(state, this.#translator);
      }
      const action = reward.querySelector<HTMLButtonElement>("[data-action='claim-daily-reward']");
      const nextTask = reward.querySelector<HTMLButtonElement>("[data-action='show-unfinished-daily']");
      if (nextTask !== null) nextTask.hidden = dailyReady || claimed;
      if (action !== null) {
        action.disabled = claimed || !dailyReady || this.#rewardClaimBusy;
        if (claimed) this.#rewardButtonStates.set(action, true);
        const label = claimed ? this.#translator("hall.dailyClaimed") : this.#translator("hall.claimDaily", {
          value: formatCurrencyAmount(dailyReward, locale),
        });
        // Preserve the pressed label too: WebKit can cancel the click when its
        // text node is replaced between pointerdown and pointerup.
        if (action.textContent !== label) action.textContent = label;
      }
    });
    if (this.#activeSection === "progress" && this.#trophyRuntime !== null) {
      this.#trophyRuntime.scene.setVisualState(this.#trophyVisualState());
    }

    this.#root.querySelectorAll<HTMLElement>("[data-live-shortfall]").forEach((element) => {
      const code = this.#upgradeCodeFrom(element.dataset.liveShortfall);
      const nextUpgrade = getNextUpgradeLevel(state, code);
      if (nextUpgrade?.costToReach == null) {
        element.hidden = true;
        return;
      }
      const missing = amount(nextUpgrade.costToReach) - amount(state.account.available);
      element.hidden = missing <= 0n;
      if (missing > 0n) {
        const value = this.#translator("workshop.shortfall", {
          value: formatCurrencyAmount(missing.toString(), locale),
        });
        const valueElement = element.querySelector<HTMLElement>("[data-live-shortfall-value]");
        if (valueElement === null) element.textContent = value;
        else valueElement.textContent = value;
      }
    });
  }

  #visibleEnergy() {
    if (this.#state === null) throw new Error("Energy view requires a loaded state");
    return energyPresentation(this.#state, this.#serverNow, this.#pendingTapCount, GAME_CONFIG.energy.costPerTap);
  }

  #syncCoreRendering(): void {
    const active = !document.hidden && !this.#marketOpen && !this.#mapOpen && this.#activeSection === "core" && this.#sheet === null;
    this.#runtime?.scene.setVisualPaused(!active);
    for (const runtime of [this.#runtime, this.#energySigilRuntime]) {
      if (!runtime?.game.isBooted) continue;
      if (active && !runtime.game.loop.running) runtime.game.loop.wake();
      else if (!active && runtime.game.loop.running) runtime.game.loop.sleep();
    }
  }

  #updateEnergyPresentation(): void {
    if (this.#state === null) return;
    const state = this.#state;
    const locale = state.settings.locale;
    const current = this.#visibleEnergy().current;
    const energyFill = energyFillPercent(current, state.energy.capacity);
    this.#root.querySelectorAll<HTMLElement>("[data-live-energy-current]").forEach((element) => {
      element.textContent = formatAmount(String(current), locale);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-live-energy-capacity]").forEach((element) => {
      element.textContent = formatAmount(String(state.energy.capacity), locale);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-live-energy-meter]").forEach((element) => {
      element.setAttribute("aria-valuemax", String(state.energy.capacity));
      element.setAttribute("aria-valuenow", String(current));
      element.querySelector<HTMLElement>("i")?.style.setProperty("--energy-fill", `${energyFill}%`);
    });
    this.#root.querySelectorAll<HTMLElement>("[data-live-energy-status]").forEach((element) => {
      element.textContent = this.#energyStatusText(state, current);
    });
    this.#runtime?.scene.setEnergyState(current, state.energy.capacity);
    this.#energySigilRuntime?.scene.setCharge(current, state.energy.capacity);
    this.#renderSpendSheet();
  }

  #updatePendingOnboardingTapProgress(): void {
    if (
      this.#state === null ||
      this.#state.onboarding.status !== "in-progress" ||
      this.#state.onboarding.step !== "tap"
    ) {
      return;
    }
    const target = Math.max(1, this.#state.onboarding.tapTarget);
    const current = Math.min(
      target,
      Math.max(
        0,
        this.#state.progress.confirmedTaps + this.#pendingTapCount - this.#state.onboarding.tapBaseline,
      ),
    );
    const progress = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-progress]");
    const label = progress?.querySelector<HTMLElement>("[data-onboarding-progress-label]");
    const meter = progress?.querySelector<HTMLElement>("[role='progressbar']");
    if (label !== null && label !== undefined) {
      label.textContent = this.#translator("onboarding.tapProgress", { current, target });
    }
    meter?.setAttribute("aria-valuenow", String(Math.round((current / target) * 100)));
    meter?.querySelector<HTMLElement>("b")?.style.setProperty("--onboarding-progress", String(current / target));
  }

  #updateBalanceText(): void {
    if (this.#state === null) return;
    const exactBalance = this.#displayedBalance.toString();
    this.#balanceValue.textContent = formatAmount(exactBalance, this.#state.settings.locale);
    this.#balanceValue.dataset.digits = String(exactBalance.replace("-", "").length);
    const projectionVisible = this.#state.season.projection.visible;
    this.#balanceEstimate.textContent = projectionVisible
      ? formatUsdtCents(
          this.#state.season.projection.estimatedRewardUsdCents,
          this.#state.settings.locale,
          false,
        )
      : this.#translator("header.estimatePendingValue", {
          time: projectionRevealTimeShort(this.#state, this.#serverNow, this.#translator),
        });
    this.#balanceEstimateLabel.textContent = this.#translator(
      projectionVisible ? "header.estimateNow" : "header.estimatePending",
    );
    this.#balanceEstimate.closest(".balance-estimate")?.classList.toggle("is-pending", !projectionVisible);
  }

  #energyStatusText(state: GameState, current = state.energy.current): string {
    const pending = this.#service.getPendingTaps();
    if (pending.count > 0 && pending.delayed) return this.#translator("energy.synchronizing");
    if (state.season.status !== "active") return this.#translator(state.season.status === "scheduled" ? "header.seasonStarts" : "progress.seasonFrozenTitle");
    if (current >= state.energy.capacity) return this.#translator("energy.full");
    return this.#translator("energy.next", {
      seconds: secondsUntilNextEnergy(
        current,
        state.energy.capacity,
        state.energy.recoverySecondsPerUnit,
        this.#visibleEnergy().remainderMilliseconds,
        state.energy.recoveryMultiplierBasisPoints ?? 10000,
      ),
    });
  }

  #renderDetails(): void {
    if (this.#state === null || this.#activeSection === "core") return;
    this.#disposeEnergyRuntime();
    this.#disposeTrophyRuntime();
    this.#disposeMakersClubRuntime();
    this.#disposeArenaCatalogRuntime();
    this.#disposeArenaRuntime();
    this.#disposeRelicDuelRuntime();
    const state = this.#state;
    const locale = state.settings.locale;
    let content = "";
    if (this.#activeSection === "development") {
      content = renderDevelopmentV2(state, this.#translator);
    }
    if (this.#activeSection === "progress") {
      content = renderProgressV2(
        state,
        this.#progressSegment,
        this.#serverNow,
        this.#translator,
        this.#achievementFilter,
      );
    }
    if (this.#activeSection === "friends") {
      content = renderFriendsV2(
        state,
        this.#translator,
        this.#friendFilter,
        this.#friendQuery,
        this.#friendPage,
        this.#makersClubInviteUrl(),
      );
    }
    if (this.#activeSection === "games") {
      content = renderGames(
        state,
        this.#translator,
        locale,
        this.#arenaView,
        this.#service.canRequestGuardianMatch(),
      );
    }
    const navigation = detailNavigation[this.#activeSection];
    const relicDuelOpen = this.#activeSection === "games" && this.#arenaView === "relic-duel";
    const sectionBack = relicDuelOpen
      ? `<button class="map-return" type="button" data-action="arena-catalog" aria-label="${escapeHtml(
          this.#translator("games.catalogBack"),
        )}"><span aria-hidden="true"><i>←</i></span><strong>${escapeHtml(this.#translator("games.relicBackShort"))}</strong></button>`
      : `<button class="map-return" type="button" data-action="open-map" aria-label="${escapeHtml(
          this.#translator("map.backA11y"),
        )}"><span aria-hidden="true"><i>←</i></span><strong>${escapeHtml(this.#translator("map.back"))}</strong></button>`;
    const sectionHelp = relicDuelOpen
      ? `<span class="section-nav__preview">${escapeHtml(this.#translator("games.relicPreviewBadge"))}</span>`
      : `<button class="section-nav__help" type="button" data-action="open-section-help" aria-label="${escapeHtml(
          this.#translator("help.open", { section: this.#translator(navigation.title) }),
        )}"${this.#activeSection === "games" && state.pvp.status !== "idle" ? " hidden" : ""}><i aria-hidden="true">?</i></button>`;
    this.#detailScreen.innerHTML = `<header class="section-nav">
      ${sectionBack}
      <h1>${escapeHtml(this.#translator(navigation.title))}</h1>
      ${sectionHelp}
    </header>${content}`;
    if (this.#activeSection === "development") this.#mountEnergyRuntime();
    if (this.#activeSection === "progress") this.#mountTrophyRuntime();
    if (this.#activeSection === "friends") this.#mountMakersClubRuntime();
    if (this.#activeSection === "games") {
      if (this.#arenaView === "catalog" && state.pvp.status === "idle") this.#mountArenaCatalogRuntime();
      else if (this.#arenaView === "relic-duel" && RELIC_DUEL_PREVIEW_ENABLED) this.#mountRelicDuelRuntime();
      else this.#mountArenaRuntime();
    }
  }

  #trophyVisualState(): TrophyHallVisualState {
    if (this.#state === null) throw new Error("Trophy Hall cannot mount without game state");
    const state = this.#state;
    const dailyCompleteCount = this.#trophyPreviewCount ?? dailyGoalsCompletedCount(state);
    const visibleAchievements = GAME_CONFIG.progress.achievements.filter(
      (achievement) => this.#achievementFilter === "all" || achievement.category === this.#achievementFilter,
    );
    const ready = visibleAchievements.find(
      (achievement) =>
        !state.progress.claimedAchievementIds.includes(achievement.id) && achievementProgress(state, achievement).ready,
    );
    const selected = visibleAchievements.find((achievement) => achievement.id === state.profile.selectedTitle);
    const featured = ready ?? selected ?? visibleAchievements[0] ?? GAME_CONFIG.progress.achievements[0];
    if (featured === undefined) throw new Error("Trophy Hall requires at least one achievement");
    const featuredClaimed = state.progress.claimedAchievementIds.includes(featured.id);
    const featuredReady = achievementProgress(state, featured).ready;
    return {
      segment: this.#progressSegment,
      dailyCompleteCount,
      dailyClaimed: state.progress.daily.rewardClaimed,
      featuredAchievement: featured.id,
      featuredAchievementState: featuredClaimed ? "claimed" : featuredReady ? "ready" : "locked",
      seasonStatus: state.season.status,
      tonUsdtPrizeEnabled:
        state.tonUsdtPrize.campaign !== null &&
        state.tonUsdtPrize.campaign.seasonCode === state.season.code &&
        state.tonUsdtPrize.campaign.fundingStatus !== "draft",
      channelJoined: state.telegram.channel.joined,
      chatJoined: state.telegram.chat.joined,
      reducedMotion: state.settings.reducedMotion,
    };
  }

  #mountTrophyRuntime(): void {
    if (this.#state === null || this.#activeSection !== "progress") return;
    const host = this.#detailScreen.querySelector<HTMLElement>("#trophy-hall-runtime");
    if (host === null) return;
    const sequence = ++this.#trophyMountSequence;
    const runtime = createTrophyHallGame(host, this.#trophyVisualState());
    this.#trophyRuntime = runtime;
    const celebration = this.#pendingTrophyCelebration;
    this.#pendingTrophyCelebration = null;
    if (celebration !== null) runtime.playCelebration(celebration);

    const resize = (): void => {
      if (this.#trophyRuntime !== runtime || sequence !== this.#trophyMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#trophyResizeObserver = new ResizeObserver(resize);
      this.#trophyResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#trophyRuntime !== runtime || sequence !== this.#trophyMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      resize();
    });
  }

  #disposeTrophyRuntime(): void {
    this.#trophyMountSequence += 1;
    this.#trophyResizeObserver?.disconnect();
    this.#trophyResizeObserver = null;
    this.#trophyRuntime?.destroy();
    this.#trophyRuntime = null;
  }

  #mountRewardRevealRuntime(): void {
    if (this.#state === null || this.#sheet !== "reward-reveal" || this.#rewardRevealAchievement === null) return;
    const host = this.#sheetContent.querySelector<HTMLElement>("#reward-reveal-runtime");
    if (host === null) return;
    const sequence = ++this.#rewardRevealMountSequence;
    const runtime = createTrophyHallGame(host, {
      segment: "achievements",
      dailyCompleteCount: dailyGoalsCompletedCount(this.#state),
      dailyClaimed: this.#state.progress.daily.rewardClaimed,
      featuredAchievement: this.#rewardRevealAchievement,
      featuredAchievementState: "claimed",
      seasonStatus: this.#state.season.status,
      tonUsdtPrizeEnabled:
        this.#state.tonUsdtPrize.campaign !== null &&
        this.#state.tonUsdtPrize.campaign.seasonCode === this.#state.season.code &&
        this.#state.tonUsdtPrize.campaign.fundingStatus !== "draft",
      channelJoined: this.#state.telegram.channel.joined,
      chatJoined: this.#state.telegram.chat.joined,
      reducedMotion: this.#state.settings.reducedMotion,
    });
    this.#rewardRevealRuntime = runtime;
    const resize = (): void => {
      if (this.#rewardRevealRuntime !== runtime || sequence !== this.#rewardRevealMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#rewardRevealResizeObserver = new ResizeObserver(resize);
      this.#rewardRevealResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#rewardRevealRuntime !== runtime || sequence !== this.#rewardRevealMountSequence) return;
      host.dataset.phaserReady = "true";
      resize();
      runtime.playCelebration("achievement");
    });
  }

  #disposeRewardRevealRuntime(): void {
    this.#rewardRevealMountSequence += 1;
    this.#rewardRevealResizeObserver?.disconnect();
    this.#rewardRevealResizeObserver = null;
    this.#rewardRevealRuntime?.destroy();
    this.#rewardRevealRuntime = null;
  }

  #mountRewardVaultRuntime(): void {
    if (this.#state === null || this.#sheet !== "reward-account") return;
    const host = this.#sheetContent.querySelector<HTMLElement>("#reward-account-runtime");
    if (host === null) return;
    const sequence = ++this.#rewardVaultMountSequence;
    const runtime = createRewardVaultGame(host, {
      hasRewards: BigInt(this.#state.rewardAccount.referenceUsdtMicros) > 0n,
      ledgerCount: this.#state.rewardAccount.ledger.length,
      reducedMotion: this.#state.settings.reducedMotion,
    });
    this.#rewardVaultRuntime = runtime;
    const resize = (): void => {
      if (this.#rewardVaultRuntime !== runtime || sequence !== this.#rewardVaultMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#rewardVaultResizeObserver = new ResizeObserver(resize);
      this.#rewardVaultResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#rewardVaultRuntime !== runtime || sequence !== this.#rewardVaultMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      resize();
    });
  }

  #disposeRewardVaultRuntime(): void {
    this.#rewardVaultMountSequence += 1;
    this.#rewardVaultResizeObserver?.disconnect();
    this.#rewardVaultResizeObserver = null;
    this.#rewardVaultRuntime?.destroy();
    this.#rewardVaultRuntime = null;
  }

  #mountPremiumRuntime(): void {
    if (this.#state === null || this.#sheet !== "premium-shop") return;
    const host = this.#sheetContent.querySelector<HTMLElement>("#premium-shop-runtime");
    if (host === null) return;
    const sequence = ++this.#premiumMountSequence;
    const runtime = createPremiumShopGame(
      host,
      {
        onRelicSelected: (relic) => {
          window.requestAnimationFrame(() => this.#selectPremiumRelic(relic));
        },
      },
      this.#premiumShopVisualState(),
    );
    this.#premiumRuntime = runtime;
    const resize = (): void => {
      if (this.#premiumRuntime !== runtime || sequence !== this.#premiumMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#premiumResizeObserver = new ResizeObserver(resize);
      this.#premiumResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#premiumRuntime !== runtime || sequence !== this.#premiumMountSequence) return;
      host.dataset.phaserReady = "true";
      resize();
    });
  }

  #disposePremiumRuntime(): void {
    this.#premiumMountSequence += 1;
    this.#premiumResizeObserver?.disconnect();
    this.#premiumResizeObserver = null;
    this.#premiumRuntime?.destroy();
    this.#premiumRuntime = null;
  }

  #makersClubCircle(): MakersClubCircle {
    if (this.#friendFilter === 1) return "near";
    if (this.#friendFilter === 2) return "far";
    return "all";
  }

  #makersClubVisualState(): MakersClubVisualState {
    if (this.#state === null) throw new Error("Makers Club cannot mount without game state");
    return {
      selectedCircle: this.#makersClubCircle(),
      totalMembers: this.#state.clubRoster.networkCount,
      nearMembers: this.#state.clubRoster.nearCount,
      farMembers: this.#state.clubRoster.farCount,
      milestoneTarget: GAME_CONFIG.referrals.nextMilestoneMembers,
      reducedMotion: this.#state.settings.reducedMotion,
    };
  }

  #makersClubInviteUrl(): string {
    return this.#service.getInviteUrl();
  }

  #mountMakersClubRuntime(): void {
    if (this.#state === null || this.#activeSection !== "friends") return;
    const host = this.#detailScreen.querySelector<HTMLElement>("#makers-club-runtime");
    if (host === null) return;
    const sequence = ++this.#makersClubMountSequence;
    const runtime = createMakersClubGame(host, this.#makersClubVisualState());
    this.#makersClubRuntime = runtime;
    const resize = (): void => {
      if (this.#makersClubRuntime !== runtime || sequence !== this.#makersClubMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#makersClubResizeObserver = new ResizeObserver(resize);
      this.#makersClubResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#makersClubRuntime !== runtime || sequence !== this.#makersClubMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      resize();
    });
  }

  #disposeMakersClubRuntime(): void {
    this.#makersClubMountSequence += 1;
    this.#makersClubResizeObserver?.disconnect();
    this.#makersClubResizeObserver = null;
    this.#makersClubRuntime?.destroy();
    this.#makersClubRuntime = null;
  }

  #mountArenaCatalogRuntime(): void {
    if (this.#state === null || this.#activeSection !== "games" || this.#arenaView !== "catalog") return;
    const host = this.#detailScreen.querySelector<HTMLElement>("#arena-catalog-runtime");
    if (host === null) return;
    const sequence = ++this.#arenaCatalogMountSequence;
    const runtime = createArenaCatalogGame(host, this.#state.settings.reducedMotion);
    this.#arenaCatalogRuntime = runtime;
    const resize = (): void => {
      if (this.#arenaCatalogRuntime !== runtime || sequence !== this.#arenaCatalogMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#arenaCatalogResizeObserver = new ResizeObserver(resize);
      this.#arenaCatalogResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#arenaCatalogRuntime !== runtime || sequence !== this.#arenaCatalogMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      resize();
    });
  }

  #disposeArenaCatalogRuntime(): void {
    this.#arenaCatalogMountSequence += 1;
    this.#arenaCatalogResizeObserver?.disconnect();
    this.#arenaCatalogResizeObserver = null;
    this.#arenaCatalogRuntime?.destroy();
    this.#arenaCatalogRuntime = null;
  }

  #mountRelicDuelRuntime(): void {
    if (
      this.#state === null ||
      this.#activeSection !== "games" ||
      this.#arenaView !== "relic-duel" ||
      !RELIC_DUEL_PREVIEW_ENABLED
    ) {
      return;
    }
    const host = this.#detailScreen.querySelector<HTMLElement>("#relic-duel-runtime");
    if (host === null) return;
    const sequence = ++this.#relicDuelMountSequence;
    const t = this.#translator;
    const runtime = createRelicDuelGame(
      host,
      {
        onAnnouncement: (message) => {
          if (this.#relicDuelRuntime !== runtime || sequence !== this.#relicDuelMountSequence) return;
          const live = this.#detailScreen.querySelector<HTMLElement>("[data-relic-duel-announcement]");
          if (live !== null) live.textContent = message;
        },
        onHaptic: (kind) => {
          if (kind === "success") this.#haptics.success();
          else if (kind === "error") this.#haptics.error();
          else this.#haptics.selection();
        },
        onExitRequested: () => {
          if (this.#arenaView !== "relic-duel") return;
          this.#arenaView = "catalog";
          this.#renderDetails();
          this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
        },
      },
      GAME_CONFIG,
      {
        locale: this.#state.settings.locale,
        kicker: t("games.relicKicker"),
        title: t("games.relicTitle"),
        subtitle: t("games.relicSubtitle"),
        chooseTable: t("games.relicChooseTable"),
        tableHint: t("games.relicTableHint"),
        previewNotice: t("games.relicPreviewNotice"),
        available: t("games.available"),
        carats: t("currency.short"),
        opponent: t("games.relicOpponent"),
        you: t("games.relicYou"),
        versus: t("games.relicVersus"),
        round: t("games.relicRound"),
        roundPot: t("games.relicRoundPot"),
        chooseImpulse: t("games.relicChooseImpulse"),
        impulseHint: t("games.relicImpulseHint"),
        locked: t("games.relicLocked"),
        autoMove: t("games.relicAutoMove"),
        playerRoundWin: t("games.relicPlayerRoundWin"),
        opponentRoundWin: t("games.relicOpponentRoundWin"),
        tieRound: t("games.relicTieRound"),
        carryPot: t("games.relicCarryPot"),
        resultWin: t("games.relicResultWin"),
        resultLoss: t("games.relicResultLoss"),
        resultDraw: t("games.relicResultDraw"),
        resultWinBody: t("games.relicResultWinBody"),
        resultLossBody: t("games.relicResultLossBody"),
        resultDrawBody: t("games.relicResultDrawBody"),
        playAgain: t("games.relicPlayAgain"),
        chooseAnotherTable: t("games.relicChooseAnotherTable"),
        strength: t("games.relicStrength"),
        canvasLabel: t("games.relicCanvasLabel"),
      },
      this.#state.account.available,
      this.#state.settings.reducedMotion,
    );
    this.#relicDuelRuntime = runtime;
    const resize = (): void => {
      if (this.#relicDuelRuntime !== runtime || sequence !== this.#relicDuelMountSequence) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#relicDuelResizeObserver = new ResizeObserver(resize);
      this.#relicDuelResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#relicDuelRuntime !== runtime || sequence !== this.#relicDuelMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      resize();
      host.querySelector<HTMLCanvasElement>("canvas")?.focus({ preventScroll: true });
    });
  }

  #disposeRelicDuelRuntime(): void {
    this.#relicDuelMountSequence += 1;
    this.#relicDuelResizeObserver?.disconnect();
    this.#relicDuelResizeObserver = null;
    this.#relicDuelRuntime?.destroy();
    this.#relicDuelRuntime = null;
  }

  #mountArenaRuntime(): void {
    if (this.#state === null || this.#activeSection !== "games") return;
    const host = this.#detailScreen.querySelector<HTMLElement>("#arena-runtime");
    if (host === null) return;
    const sequence = ++this.#arenaMountSequence;
    const runtime = createPenaltyArenaGame(host, this.#state.pvp, this.#state.settings.reducedMotion, {
      onLoadProgress: ({ phase, progress, loaded, total }) => {
        if (sequence !== this.#arenaMountSequence) return;
        const page = host.closest<HTMLElement>(".arena-page");
        if (page === null) return;
        const percent = Math.round(Phaser.Math.Clamp(progress, 0, 1) * 100);
        page.dataset.arenaLoadPhase = phase;
        page.dataset.arenaLoadProgress = String(percent);
        page.dataset.arenaLoadCount = `${loaded}/${total}`;
        page.style.setProperty("--arena-load-progress", `${percent}%`);
        const phaseLabel = page.querySelector<HTMLElement>("[data-arena-loader-phase]");
        const percentLabel = page.querySelector<HTMLElement>("[data-arena-loader-percent]");
        const countLabel = page.querySelector<HTMLElement>("[data-arena-loader-count]");
        if (phaseLabel !== null) phaseLabel.textContent = this.#translator(phase === "scene" ? "games.loadingScene" : "games.loadingArena");
        if (percentLabel !== null) percentLabel.textContent = `${percent}%`;
        if (countLabel !== null) countLabel.textContent = this.#translator("games.loadingCount", { loaded, total });
      },
      onTargetLayout: ({ role, targets }) => {
        if (sequence !== this.#arenaMountSequence) return;
        const page = host.closest<HTMLElement>(".arena-page");
        if (page === null) return;
        page.dataset.arenaVisualRole = role;
        page.querySelectorAll<HTMLElement>("[data-action='pvp-zone']").forEach((button, index) => {
          const target = targets[index];
          if (target === undefined) return;
          button.style.setProperty("--zone-x", `${target.x}px`);
          button.style.setProperty("--zone-y", `${target.y}px`);
          button.style.setProperty("--zone-size", `${target.radius * 2}px`);
        });
      },
      onVisualIssue: (code, source) => {
        if (sequence !== this.#arenaMountSequence) return;
        if (code === "ARENA_FRAME_EMPTY" || (code === "ARENA_ASSET_LOAD" &&
          (source === ARENA_ART.background.key || source === ARENA_ART.defenseBackground.key))) {
          host.closest<HTMLElement>(".arena-page")?.classList.add("is-render-fallback");
        }
        this.#trackVisualIssue(code, source, "games");
      },
      onVisualRecovery: () => {
        if (sequence !== this.#arenaMountSequence) return;
        host.closest<HTMLElement>(".arena-page")?.classList.remove("is-render-fallback");
      },
    });
    this.#arenaRuntime = runtime;
    this.#arenaLastFrameAt = 0;
    this.#syncArenaPresentation(this.#state);
    void runtime.ready.then(() => {
      if (this.#arenaRuntime !== runtime || sequence !== this.#arenaMountSequence) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      host.closest<HTMLElement>(".arena-page")?.classList.add("is-phaser-ready");
      if (this.#state !== null) runtime.setMatch(this.#state.pvp, this.#state.settings.reducedMotion);
      this.#startArenaClock();
      this.#syncOnboarding();
    });
  }

  #disposeArenaRuntime(): void {
    this.#arenaMountSequence += 1;
    window.cancelAnimationFrame(this.#arenaClockFrame);
    window.clearTimeout(this.#arenaRevealTimer);
    window.clearTimeout(this.#arenaImpactHoldTimer);
    this.#arenaClockFrame = 0;
    this.#arenaLastFrameAt = 0;
    this.#arenaRuntime?.destroy();
    this.#arenaRuntime = null;
  }

  #trackVisualIssue(code: string, source: string, screen: "map" | "games"): void {
    const key = `${code}:${source}`;
    if (this.#reportedVisualIssues.has(key) || this.#reportedVisualIssues.size >= 8) return;
    this.#reportedVisualIssues.add(key);
    this.#analytics.track("client_error", { screen, code, source, reason: "render", version: browserEngineVersion() });
    void this.#analytics.flush().catch(() => undefined);
  }

  #startArenaClock(): void {
    window.cancelAnimationFrame(this.#arenaClockFrame);
    const tick = (frameTime: number): void => {
      if (this.#arenaRuntime === null || this.#destroyed) return;
      if (!document.hidden && frameTime - this.#arenaLastFrameAt >= 80) {
        this.#arenaLastFrameAt = frameTime;
        this.#updateArenaClock();
      }
      this.#arenaClockFrame = window.requestAnimationFrame(tick);
    };
    this.#arenaClockFrame = window.requestAnimationFrame(tick);
  }

  #estimatedServerNow(): number {
    const confirmed = Date.parse(this.#serverNow);
    if (!Number.isFinite(confirmed)) return Date.now();
    return confirmed + Math.max(0, performance.now() - this.#serverAnchorPerformance);
  }

  #arenaSeriesText(state: GameState): string {
    const lastTurn = state.pvp.history.at(-1);
    const series = state.pvp.currentTurn?.series ?? lastTurn?.series ?? 1;
    if (series <= GAME_CONFIG.pvp.regulationSeries) {
      return this.#translator("games.series", { current: series, total: GAME_CONFIG.pvp.regulationSeries });
    }
    return this.#translator("games.suddenDeath", { current: series - GAME_CONFIG.pvp.regulationSeries });
  }

  #updateArenaClock(): void {
    if (this.#state === null || this.#activeSection !== "games") return;
    const match = this.#state.pvp;
    const now = this.#estimatedServerNow();
    const searchCopy = this.#detailScreen.querySelector<HTMLElement>("[data-arena-search-copy]");
    if (searchCopy !== null && match.botFallbackAt !== null) {
      const remaining = Math.max(0, Date.parse(match.botFallbackAt) - now);
      searchCopy.textContent = this.#translator("games.searchBody", { seconds: Math.ceil(remaining / 1_000) });
    }
    const guardianChoice = this.#detailScreen.querySelector<HTMLButtonElement>("[data-action='request-guardian']");
    if (guardianChoice !== null) {
      const offerAt = match.searchStartedAt === null
        ? Number.POSITIVE_INFINITY
        : Date.parse(match.searchStartedAt) + GAME_CONFIG.pvp.manualGuardianOfferMs;
      guardianChoice.hidden = match.status !== "matchmaking" || now < offerAt;
    }

    const timer = this.#detailScreen.querySelector<HTMLElement>("[data-arena-timer]");
    const ring = this.#detailScreen.querySelector<SVGCircleElement>("[data-arena-timer-ring]");
    if (timer === null || ring === null || match.currentTurn === null) return;
    const remaining = Math.max(0, Date.parse(match.currentTurn.deadlineAt) - now);
    const ratio = Phaser.Math.Clamp(remaining / GAME_CONFIG.pvp.turnMs, 0, 1);
    const seconds = Math.ceil(remaining / 1_000);
    timer.textContent = String(seconds);
    timer.closest<HTMLElement>(".arena-timer")?.setAttribute(
      "aria-label",
      `${this.#translator("games.turnTime")}: ${seconds}`,
    );
    ring.style.strokeDashoffset = String(113.1 * (1 - ratio));
    ring.closest<HTMLElement>(".arena-timer")?.style.setProperty("--arena-time", String(ratio));
  }

  #syncArenaPresentation(previous: GameState | null): void {
    if (this.#state === null || this.#activeSection !== "games") return;
    const state = this.#state;
    const match = state.pvp;
    const page = this.#detailScreen.querySelector<HTMLElement>(".arena-page");
    if (page === null) return;
    const updateAttempts = (side: "player" | "opponent", history: typeof match.history): void => {
      const container = page.querySelector<HTMLElement>(`[data-arena-attempts='${side}']`);
      if (container === null) return;
      const attempts = history.filter((turn) => (side === "player" ? turn.role === "shooter" : turn.role === "keeper"));
      const slotCount = Math.max(GAME_CONFIG.pvp.regulationSeries, attempts.length);
      container.replaceChildren(
        ...Array.from({ length: slotCount }, (_, index) => {
          const marker = document.createElement("i");
          marker.className = "arena-attempt";
          const attempt = attempts[index];
          if (attempt === undefined) {
            marker.classList.add("is-pending");
            marker.setAttribute("aria-hidden", "true");
          } else {
            const scored = attempt.outcome === "goal";
            marker.classList.add(scored ? "is-goal" : "is-save");
            marker.title = this.#translator(scored ? "games.attemptGoal" : "games.attemptSave");
          }
          return marker;
        }),
      );
    };
    const presentationRole = match.currentTurn?.role ?? match.history.at(-1)?.role ?? null;
    page.dataset.arenaStatus = match.status;
    page.dataset.arenaResult = match.result ?? "none";
    page.dataset.arenaRole = presentationRole ?? "none";
    const sectionHelp = this.#detailScreen.querySelector<HTMLButtonElement>(".section-nav__help");
    if (sectionHelp !== null) sectionHelp.hidden = match.status !== "idle";
    if (match.status !== "idle" && (this.#sheet === "section-help" || this.#sheet === "arena-rules")) this.#closeSheet();

    this.#arenaRuntime?.setMatch(match, state.settings.reducedMotion);
    const foundOpponent = previous?.pvp.opponentKind == null && match.opponentKind !== null;
    if (foundOpponent) this.#arenaRuntime?.playMatchFound();
    const previousHistoryLength = previous?.pvp.history.length ?? match.history.length;
    if (match.history.length > previousHistoryLength) {
      const turn = match.history.at(-1);
      if (turn !== undefined) {
        const heldScore = previous?.pvp.score ?? match.score;
        const playbackMatchId = match.matchId;
        page.dataset.arenaStatus = "resolved";
        page.classList.remove("is-impact-revealed");
        page.dataset.arenaPlayback = `shot-${turn.index}`;
        page.dataset.arenaHeldScorePlayer = String(heldScore.player);
        page.dataset.arenaHeldScoreOpponent = String(heldScore.opponent);
        page.dataset.arenaHeldHistoryLength = String(previous?.pvp.history.length ?? Math.max(0, match.history.length - 1));
        window.clearTimeout(this.#arenaRevealTimer);
        window.clearTimeout(this.#arenaImpactHoldTimer);
        const timing = arenaShotTiming(turn.role, state.settings.reducedMotion);
        const impactAtMs = timing.impactAtMs;
        this.#arenaRevealTimer = window.setTimeout(() => {
          if (this.#state?.pvp.matchId !== playbackMatchId || this.#state.pvp.history.at(-1)?.index !== turn.index) return;
          page.dataset.arenaStatus = this.#state.pvp.status;
          page.classList.add("is-impact-revealed");
          page.dataset.arenaPlayback = `impact-${turn.index}`;
          delete page.dataset.arenaHeldHistoryLength;
          const latestScore = this.#state.pvp.score;
          const playerScore = page.querySelector<HTMLElement>("[data-arena-score-player]");
          const opponentScore = page.querySelector<HTMLElement>("[data-arena-score-opponent]");
          if (playerScore !== null) playerScore.textContent = String(latestScore.player);
          if (opponentScore !== null) opponentScore.textContent = String(latestScore.opponent);
          updateAttempts("player", this.#state.pvp.history);
          updateAttempts("opponent", this.#state.pvp.history);
          const playerSucceeded =
            (turn.role === "shooter" && turn.outcome === "goal") ||
            (turn.role === "keeper" && turn.outcome === "save");
          if (playerSucceeded) this.#haptics.success();
          else this.#haptics.error();
          this.#arenaImpactHoldTimer = window.setTimeout(() => {
            if (this.#state?.pvp.matchId !== playbackMatchId || this.#state.pvp.history.at(-1)?.index !== turn.index) return;
            page.classList.remove("is-impact-revealed");
            page.dataset.arenaPlayback = "idle";
            delete page.dataset.arenaHeldScorePlayer;
            delete page.dataset.arenaHeldScoreOpponent;
            delete page.dataset.arenaHeldHistoryLength;
            this.#syncArenaPresentation(this.#state);
          }, timing.impactHoldMs);
        }, impactAtMs);
        this.#arenaRuntime?.playTurn(turn);
      }
    } else if (page.dataset.arenaPlayback?.startsWith("shot-") === true) {
      page.dataset.arenaStatus = "resolved";
    } else if (page.dataset.arenaPlayback?.startsWith("impact-") === true) {
      page.dataset.arenaStatus = match.status;
    } else if (match.status === "resolved") {
      const currentIndex = match.history.at(-1)?.index;
      if (page.dataset.arenaPlayback !== `shot-${currentIndex ?? -1}`) {
        page.classList.add("is-impact-revealed");
      }
    } else {
      page.classList.remove("is-impact-revealed");
      page.dataset.arenaPlayback = "idle";
      delete page.dataset.arenaHeldScorePlayer;
      delete page.dataset.arenaHeldScoreOpponent;
      delete page.dataset.arenaHeldHistoryLength;
    }
    const reachedResult = previous?.pvp.status !== "result" && match.status === "result";
    if (reachedResult) {
      this.#arenaRuntime?.playResult(match.result);
      if (match.result === "win") this.#haptics.success();
      else if (match.result === "loss") this.#haptics.error();
    }

    const setText = (selector: string, value: string): void => {
      const element = page.querySelector<HTMLElement>(selector);
      if (element !== null) element.textContent = value;
    };
    setText("[data-arena-balance]", formatAmount(state.account.available, state.settings.locale, true));
    const holdScore = page.dataset.arenaPlayback?.startsWith("shot-") === true;
    setText(
      "[data-arena-score-player]",
      holdScore ? page.dataset.arenaHeldScorePlayer ?? String(match.score.player) : String(match.score.player),
    );
    setText(
      "[data-arena-score-opponent]",
      holdScore ? page.dataset.arenaHeldScoreOpponent ?? String(match.score.opponent) : String(match.score.opponent),
    );
    const visibleHistory = holdScore
      ? match.history.slice(0, Number(page.dataset.arenaHeldHistoryLength ?? Math.max(0, match.history.length - 1)))
      : match.history;
    updateAttempts("player", visibleHistory);
    updateAttempts("opponent", visibleHistory);
    setText(
      "[data-arena-opponent-name]",
      match.opponentKind === "guardian"
        ? this.#translator("games.scoreOpponent")
        : match.opponentName ?? this.#translator("games.scoreOpponent"),
    );
    setText("[data-arena-series]", this.#arenaSeriesText(state));
    setText("[data-arena-result-series]", this.#arenaSeriesText(state));
    setText("[data-arena-role]", presentationRole === "keeper" ? this.#translator("games.roleSave") : this.#translator("games.roleShot"));
    setText(
      "[data-arena-prompt]",
      match.status === "move-accepted"
        ? this.#translator("games.moveAccepted")
        : match.currentTurn?.role === "keeper"
          ? this.#translator("games.savePrompt")
          : this.#translator("games.zonePrompt"),
    );
    page.querySelector<HTMLElement>("[data-testid='penalty-goal']")?.setAttribute(
      "aria-label",
      match.currentTurn?.role === "keeper"
        ? this.#translator("games.savePrompt")
        : this.#translator("games.zonePrompt"),
    );
    const wait = page.querySelector<HTMLElement>("[data-arena-wait]");
    if (wait !== null) wait.hidden = match.status !== "move-accepted";

    page.querySelectorAll<HTMLButtonElement>("[data-action='pvp-zone']").forEach((button) => {
      const zone = Number(button.dataset.zone);
      const selected = match.currentTurn?.playerZone === zone;
      const zoneKey = `games.zone.${zone + 1}` as TranslationKey;
      const zoneName = this.#translator(zoneKey);
      button.disabled = match.status !== "choosing";
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
      button.setAttribute(
        "aria-label",
        this.#translator(presentationRole === "keeper" ? "games.saveZone" : "games.shotZone", { zone: zoneName }),
      );
    });

    const lastTurn = match.history.at(-1);
    const reveal = page.querySelector<HTMLElement>(".arena-reveal");
    if (reveal !== null) {
      const headline = reveal.querySelector<HTMLElement>("[data-arena-callout]");
      const detail = reveal.querySelector<HTMLElement>("[data-arena-callout-detail]");
      const playerSucceeded =
        lastTurn !== undefined &&
        ((lastTurn.role === "shooter" && lastTurn.outcome === "goal") ||
          (lastTurn.role === "keeper" && lastTurn.outcome === "save"));
      let detailKey: TranslationKey = "games.calloutYouScored";
      if (lastTurn?.role === "shooter" && lastTurn.outcome === "save") detailKey = "games.calloutOpponentSaved";
      else if (lastTurn?.role === "keeper" && lastTurn.outcome === "save") detailKey = "games.calloutYouSaved";
      else if (lastTurn?.role === "keeper" && lastTurn.outcome === "goal") detailKey = "games.calloutYouConceded";
      if (headline !== null) headline.textContent = lastTurn?.outcome === "save" ? this.#translator("games.save") : this.#translator("games.goal");
      if (detail !== null) detail.textContent = this.#translator(detailKey);
      reveal.dataset.arenaPlayerOutcome = playerSucceeded ? "success" : "failure";
    }
    const auto = page.querySelector<HTMLElement>("[data-arena-auto]");
    if (auto !== null) auto.hidden = lastTurn?.playerAuto !== true;

    let resultTitle = this.#translator("games.resultCancelled");
    let resultBody = this.#translator("games.resultCancelledBody");
    if (match.result === "win") {
      resultTitle = this.#translator("games.resultWin");
      resultBody = this.#translator("games.resultWinBody");
    } else if (match.result === "loss") {
      resultTitle = this.#translator("games.resultLoss");
      resultBody = this.#translator("games.resultLossBody");
    } else if (match.result === "draw") {
      resultTitle = this.#translator("games.resultDraw");
      resultBody = this.#translator("games.resultDrawBody");
    }
    setText("[data-arena-result-title]", resultTitle);
    setText("[data-arena-result-body]", resultBody);
    const payoutKey = match.result === "draw" || match.result === "cancelled" ? "games.resultRefund" : "games.resultPayout";
    setText(
      "[data-arena-result-bank]",
      this.#translator(payoutKey, { value: formatCurrencyAmount(match.payout, state.settings.locale) }),
    );

    const canStart =
      !this.#pvpBusy &&
      amount(state.account.available) >= amount(GAME_CONFIG.pvp.stake) &&
      state.season.status === "active";
    page.querySelectorAll<HTMLButtonElement>("[data-action='start-match']").forEach((button) => {
      button.disabled = !canStart || (match.status !== "idle" && match.status !== "result");
    });
    const shortfall = page.querySelector<HTMLElement>("[data-arena-shortfall]");
    if (shortfall !== null) {
      const missing = amount(GAME_CONFIG.pvp.stake) - amount(state.account.available);
      shortfall.hidden = missing <= 0n;
      if (missing > 0n) {
        shortfall.textContent = this.#translator("games.notEnough", {
          value: formatCurrencyAmount(missing.toString(), state.settings.locale),
        });
      }
    }
    this.#updateArenaClock();
  }

  #updateMakersClubRoster(): void {
    if (this.#state === null || this.#activeSection !== "friends") return;
    const results = this.#detailScreen.querySelector<HTMLElement>("[data-club-roster-results]");
    if (results === null) return;
    results.innerHTML = renderFriendRosterV2(
      this.#state,
      this.#translator,
      this.#friendFilter,
      this.#friendQuery,
      this.#friendPage,
    );
    this.#detailScreen.querySelectorAll<HTMLButtonElement>("[data-action='friend-filter']").forEach((button) => {
      const value = button.dataset.filter;
      const selected =
        (value === "all" && this.#friendFilter === "all") ||
        (value === "1" && this.#friendFilter === 1) ||
        (value === "2" && this.#friendFilter === 2);
      button.setAttribute("aria-selected", String(selected));
      button.setAttribute("aria-pressed", String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    const register = this.#detailScreen.querySelector<HTMLElement>("#makers-club-register");
    register?.setAttribute("aria-labelledby", `club-tab-${this.#friendFilter}`);
    this.#makersClubRuntime?.setVisualState(this.#makersClubVisualState());
  }

  #scheduleClubRosterLoad(): void {
    window.clearTimeout(this.#clubRosterSearchTimer);
    this.#clubRosterSearchTimer = window.setTimeout(() => void this.#loadClubRoster(), 220);
  }

  async #loadClubRoster(): Promise<void> {
    if (this.#state === null || this.#activeSection !== "friends") return;
    const sequence = ++this.#clubRosterRequestSequence;
    const results = this.#detailScreen.querySelector<HTMLElement>("[data-club-roster-results]");
    results?.setAttribute("aria-busy", "true");
    try {
      const roster = await this.#service.loadClubRoster({
        line: this.#friendFilter === "all" ? null : this.#friendFilter,
        query: this.#friendQuery,
        page: this.#friendPage,
      });
      if (sequence !== this.#clubRosterRequestSequence || this.#state === null || this.#activeSection !== "friends") return;
      this.#state.friends = roster.friends;
      this.#state.clubRoster = roster.clubRoster;
      this.#state.referrals = roster.referrals;
      this.#friendPage = roster.clubRoster.page;
      this.#serverNow = roster.serverNow;
      this.#updateMakersClubRoster();
    } catch (error) {
      if (sequence === this.#clubRosterRequestSequence) this.#handleServiceError(error);
    } finally {
      if (sequence === this.#clubRosterRequestSequence) {
        this.#detailScreen.querySelector<HTMLElement>("[data-club-roster-results]")?.removeAttribute("aria-busy");
      }
    }
  }

  #mountEnergyRuntime(): void {
    if (this.#state === null || this.#activeSection !== "development") return;
    const sequence = ++this.#energyMountSequence;
    const mechanisms = [
      { kind: "capacity" as const, host: this.#detailScreen.querySelector<HTMLElement>("#energy-capacity-root") },
      { kind: "recovery" as const, host: this.#detailScreen.querySelector<HTMLElement>("#energy-recovery-root") },
    ];
    for (const mechanism of mechanisms) {
      const host = mechanism.host;
      if (host === null) continue;
      const runtime = createEnergyMechanismGame(
        host,
        mechanism.kind,
        this.#state.settings.reducedMotion,
      );
      this.#energyRuntimes.push(runtime);
      void runtime.ready.then(() => {
        if (sequence !== this.#energyMountSequence || !this.#energyRuntimes.includes(runtime)) return;
        host.dataset.phaserReady = "true";
        host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
      });
    }
  }

  #disposeEnergyRuntime(): void {
    this.#energyMountSequence += 1;
    this.#energyRuntimes.forEach((runtime) => runtime.destroy());
    this.#energyRuntimes = [];
  }

  #mountCoreEnergyRuntime(): void {
    if (this.#state === null) return;
    const host = this.#root.querySelector<HTMLElement>("#core-energy-sigil-root");
    if (host === null) return;
    this.#energySigilRuntime?.destroy();
    const runtime = createEnergyMechanismGame(
      host,
      "sigil",
      this.#state.settings.reducedMotion,
      this.#state.energy.current / Math.max(1, this.#state.energy.capacity),
    );
    this.#energySigilRuntime = runtime;
    void runtime.ready.then(() => {
      if (this.#energySigilRuntime !== runtime) return;
      host.dataset.phaserReady = "true";
      host.dataset.phaserMotion = this.#state?.settings.reducedMotion === true ? "reduced" : "full";
    });
  }

  #renderLeaderboardSheet(): void {
    if (this.#sheet !== "leaderboard" || this.#state === null) return;
    const scroll = this.#sheetContent.querySelector(".kingdom-community-sheet")?.scrollTop ?? 0;
    this.#sheetContent.innerHTML = renderLeaderboard(this.#state.settings.locale, this.#leaderboard, this.#leaderboardLoading, this.#leaderboardError);
    this.#sheetContent.querySelector(".kingdom-community-sheet")?.scrollTo({ top: scroll });
  }

  async #loadLeaderboard(): Promise<void> {
    if (this.#leaderboardLoading) return;
    if (this.#leaderboard?.seasonId !== this.#state?.season.id) this.#leaderboard = null;
    this.#leaderboardLoading = true;
    this.#leaderboardError = false;
    this.#renderLeaderboardSheet();
    try { this.#leaderboard = await this.#service.loadLeaderboard(); }
    catch { this.#leaderboardError = true; }
    finally { this.#leaderboardLoading = false; this.#renderLeaderboardSheet(); }
  }

  #renderSupportSheet(): void {
    if (this.#sheet !== "support" || this.#state === null) return;
    const scroll = this.#sheetContent.querySelector(".kingdom-community-sheet")?.scrollTop ?? 0;
    const input = this.#sheetContent.querySelector<HTMLTextAreaElement>("[data-support-message]");
    const focused = input !== null && document.activeElement === input;
    const start = input?.selectionStart ?? 0, end = input?.selectionEnd ?? 0;
    const locale = this.#state.settings.locale;
    this.#sheetContent.innerHTML = this.#supportView === "inbox"
      ? renderSupportInbox(locale, this.#supportInbox, this.#supportLoading, this.#supportError)
      : this.#supportView === "thread"
        ? renderSupportThread(locale, this.#supportThread, this.#supportDraft, this.#supportBusy, this.#supportError)
        : renderSupport(locale, this.#supportDraft, this.#supportReceipt, this.#supportBusy, this.#supportError);
    if (focused) {
      const next = this.#sheetContent.querySelector<HTMLTextAreaElement>("[data-support-message]");
      next?.focus({ preventScroll: true }); next?.setSelectionRange(start, end);
    }
    this.#sheetContent.querySelector(".kingdom-community-sheet")?.scrollTo({ top: scroll });
  }

  #switchSupportView(view: "inbox" | "compose" | "thread", reference = ""): void {
    this.#supportDrafts.set(this.#supportReference || "new", this.#supportDraft);
    this.#supportView = view; this.#supportReference = reference;
    this.#supportDraft = this.#supportDrafts.get(reference || "new") ?? "";
    this.#supportThread = null; this.#supportError = false; this.#supportRequestId = "";
    this.#supportLoadSequence++; this.#supportLoading = false;
    this.#supportRefreshQueued = false;
    this.#renderSupportSheet();
  }

  #openSupport(opener: HTMLElement, reference = ""): void {
    this.#switchSupportView(reference ? "thread" : "inbox", reference);
    this.#openSheet("support", opener);
    void this.#loadSupport();
    this.#supportWatchStop = this.#service.watchSupport?.(() => { void this.#loadSupport(); }) ?? null;
  }

  #consumeSupportLink(): void {
    if (!this.#pendingSupportReference || this.#sheet !== null || !this.#state || this.#destroyed || isOnboardingActive(this.#state.onboarding)) return;
    const reference = this.#pendingSupportReference; this.#pendingSupportReference = "";
    this.#openSupport(this.#required<HTMLElement>("#game-main"), reference);
    const url = new URL(window.location.href); url.searchParams.delete("support"); window.history.replaceState(null, "", url);
  }

  async #loadSupport(older = false): Promise<void> {
    if (this.#sheet !== "support" || this.#supportBusy || this.#supportView === "compose") return;
    if (this.#supportLoading) { this.#supportRefreshQueued = true; return; }
    const sequence = ++this.#supportLoadSequence;
    this.#supportLoading = true; this.#supportError = false;
    if (!this.#supportInbox || (this.#supportView === "thread" && !this.#supportThread)) this.#renderSupportSheet();
    try {
      if (this.#supportView === "thread") {
        const thread = await this.#service.loadSupportThread(this.#supportReference);
        if (sequence !== this.#supportLoadSequence) return;
        this.#supportLoading = false;
        const hadThread = this.#supportThread !== null && this.#supportThread.canReply === thread.canReply;
        const changed = JSON.stringify(thread) !== JSON.stringify(this.#supportThread);
        this.#supportThread = thread;
        if (changed) {
          const conversation = this.#sheetContent.querySelector("[data-support-conversation]");
          if (hadThread && conversation && this.#state) {
            // Keep the actual textarea DOM node: refocusing a replacement flickers the mobile keyboard.
            const template = document.createElement("template");
            template.innerHTML = renderSupportThread(this.#state.settings.locale, thread, "", false, false);
            const next = template.content.querySelector("[data-support-conversation]");
            if (next) conversation.replaceChildren(...next.childNodes);
          } else this.#renderSupportSheet();
          void this.#service.markSupportRead(thread.reference).catch(() => undefined);
        }
      } else {
        const inbox = await this.#service.loadSupportInbox(older ? this.#supportInbox?.next ?? undefined : undefined);
        if (sequence !== this.#supportLoadSequence) return;
        this.#supportLoading = false;
        if (older && this.#supportInbox) {
          const prior = this.#supportInbox.tickets;
          this.#supportInbox = { tickets: [...prior, ...inbox.tickets.filter(t => !prior.some(p => p.reference === t.reference))], next: inbox.next };
        } else if (this.#supportInbox && this.#supportInbox.tickets.length > 20) {
          this.#supportInbox = { tickets: [...inbox.tickets, ...this.#supportInbox.tickets.filter(t => !inbox.tickets.some(p => p.reference === t.reference))], next: this.#supportInbox.next };
        } else this.#supportInbox = inbox;
        this.#renderSupportSheet();
      }
    } catch { if (sequence === this.#supportLoadSequence) { this.#supportLoading = false; this.#supportError = true; this.#renderSupportSheet(); } }
    finally {
      if (sequence === this.#supportLoadSequence) {
        this.#supportLoading = false;
        if (this.#supportRefreshQueued) { this.#supportRefreshQueued = false; void this.#loadSupport(); }
      }
    }
  }

  async #sendSupport(): Promise<void> {
    if (this.#supportBusy || this.#supportDraft.trim().length < 10) return;
    this.#supportBusy = true;
    this.#supportError = false;
    this.#supportRequestId ||= commandId("support");
    const button = this.#sheetContent.querySelector<HTMLButtonElement>('[data-action="send-support"]');
    if (button) { button.disabled = true; button.textContent = communityCopy(this.#state?.settings.locale ?? "ru").sending; button.setAttribute("aria-busy", "true"); }
    const input = this.#sheetContent.querySelector<HTMLTextAreaElement>("[data-support-message]");
    if (input) input.readOnly = true;
    try {
      // Diagnostics are useful but must never block a support report.
      void this.#analytics.flush().catch(() => undefined);
      const reference = this.#supportReference;
      if (this.#supportView === "thread" && reference) {
        await this.#service.replySupport(reference, this.#supportRequestId, this.#supportDraft);
        this.#supportDraft = ""; this.#supportDrafts.delete(reference); this.#supportRequestId = "";
      } else {
        this.#supportReceipt = await this.#service.submitSupport({ requestId: this.#supportRequestId,
          message: this.#supportDraft, screen: this.#activeSection, context: browserDiagnosticsContext() });
        this.#supportDraft = ""; this.#supportDrafts.delete("new");
        this.#supportReference = this.#supportReceipt.reference; this.#supportView = "thread"; this.#supportThread = null;
      }
    } catch { this.#supportError = true; }
    finally { this.#supportBusy = false; this.#renderSupportSheet(); if (!this.#supportError) void this.#loadSupport(); }
  }

  #renderSheet(): void {
    if (this.#state === null || this.#sheet === null) return;
    if (this.#sheet === "energy") {
      // The amount, price and energy meter must refer to the same visible
      // energy, including taps which are still being confirmed.
      const projected = this.#visibleEnergy();
      const energy = { ...this.#state.energy, current: projected.current };
      const meta = { ...this.#state.meta, energyRemainderMilliseconds: projected.remainderMilliseconds };
      patchEnergySheet(this.#sheetContent, renderEnergyV2({ ...this.#state, energy, meta }, this.#translator, this.#spendWait));
      const status = this.#sheetContent.querySelector<HTMLElement>("[data-live-energy-status]");
      if (status) status.textContent = this.#energyStatusText(this.#state, projected.current);
      return;
    }
    if (this.#sheet === "upgrade-confirm" && this.#pendingUpgradeCode !== null) {
      patchUpgradeConfirmation(this.#sheetContent, renderUpgradeConfirmationV2(this.#state, this.#translator, this.#pendingUpgradeCode, this.#spendWait));
      return;
    }
    if (this.#sheet === "preferences") {
      const key = `preferences:${this.#state.settings.locale}`;
      if (key !== this.#profileRenderKey) {
        this.#sheetContent.innerHTML = renderProfilePreferences(this.#state, this.#translator);
        this.#profileRenderKey = key;
      }
      return;
    }
    if (this.#sheet === "profile") {
      const key = JSON.stringify([this.#state.profile, this.#state.season.title, this.#state.collection.items.length, this.#state.rewardAccount.referenceUsdtMicros, this.#state.settings.locale]);
      if (this.#profileRenderKey === key) return;
      this.#profileRenderKey = key;
      this.#profileMarketRuntime?.destroy(); this.#profileMarketRuntime = null;
    }
    if (this.#sheet === "support") {
      if (!this.#sheetContent.querySelector('[data-testid="support-sheet"]')) this.#renderSupportSheet();
      return;
    }
    if (this.#sheet === "leaderboard") {
      if (!this.#sheetContent.querySelector('[data-testid="leaderboard-sheet"]')) this.#renderLeaderboardSheet();
      return;
    }
    this.#disposePremiumRuntime();
    this.#disposeRewardRevealRuntime();
    this.#disposeRewardVaultRuntime();
    if (this.#sheet === "profile") {
      this.#sheetContent.innerHTML = renderProfileV2(
        this.#state,
        this.#translator,
        DEV_TOOLS_BUILD_ENABLED && this.#devEnabled,
      );
      window.requestAnimationFrame(() => {
        if (this.#sheet !== "profile" || this.#state === null) return;
        const host = this.#sheetContent.querySelector<HTMLElement>("[data-profile-market-scene]");
        if (host && this.#profileMarketRuntime === null) this.#profileMarketRuntime = createMarketGame(host, "exchange", this.#state.settings.reducedMotion, true);
      });
    }
    if (this.#sheet === "reward-account") {
      this.#sheetContent.innerHTML = renderRewardAccountV2(this.#state, this.#translator);
      window.requestAnimationFrame(() => this.#mountRewardVaultRuntime());
    }
    if (this.#sheet === "partner-transfer" && this.#service.transfers) {
      if (this.#rewardTransferPanel === null) {
        this.#sheetContent.innerHTML = '<div class="partner-transfer-host"></div>';
        this.#rewardTransferPanel = new RewardTransferPanel(this.#sheetContent.firstElementChild as HTMLElement, this.#service.transfers,
          this.#state.settings.locale, this.#state.settings.reducedMotion, () => { void this.#service.refreshState().catch(() => { /* Normal sync will refresh the common balance. */ }); });
      }
    }
    if (this.#sheet === "ton-usdt-prizes") {
      this.#sheetContent.innerHTML = renderTonUsdtPrizeInfoV2(this.#state, this.#translator);
    }
    if (this.#sheet === "balance") {
      this.#sheetContent.innerHTML = renderBalanceV2(this.#state, this.#serverNow, this.#translator);
    }
    if (this.#sheet === "upgrade") {
      this.#sheetContent.innerHTML = renderUpgradeV2(this.#state, this.#translator, this.#selectedUpgrade);
    }
    if (this.#sheet === "daily-streak") {
      this.#sheetContent.innerHTML = renderDailyStreakV2(this.#state, this.#translator);
    }
    if (this.#sheet === "section-help" && this.#activeSection !== "core") {
      this.#sheetContent.innerHTML = renderSectionHelpV2(this.#state, this.#translator, this.#activeSection);
    }
    if (this.#sheet === "arena-rules" && this.#activeSection === "games" && this.#state.pvp.status === "idle") {
      this.#sheetContent.innerHTML = renderArenaRulesV2(this.#state, this.#translator);
    }
    if (this.#sheet === "offline-reward" && this.#offlineReward !== null && this.#offlineRuntime === null) {
      this.#sheetContent.innerHTML = renderOfflineRewardV2(
        this.#state,
        this.#translator,
        this.#offlineReward.gain,
        this.#offlineReward.seconds,
      );
    }
    if (this.#sheet === "premium-shop") {
      this.#sheetContent.innerHTML = renderPremiumShopV2(
        this.#state,
        this.#serverNow,
        this.#translator,
        this.#premiumRelic,
        this.#premiumPreviewSkinId,
      );
      window.requestAnimationFrame(() => this.#mountPremiumRuntime());
    }
    if (this.#sheet === "reward-reveal" && this.#rewardRevealAchievement !== null) {
      this.#sheetContent.innerHTML = renderRewardRevealV2(this.#translator, this.#rewardRevealAchievement, this.#state.settings.locale);
      window.requestAnimationFrame(() => this.#mountRewardRevealRuntime());
    }
    if (this.#sheet === "collectible" && this.#selectedCollectible !== null) {
      this.#sheetContent.innerHTML = renderCollectibleV2(
        this.#state,
        this.#translator,
        this.#selectedCollectible,
      );
    }
  }

  #handleServiceError(error: unknown): void {
    if (error instanceof TapDeliveryStalledError) {
      this.#analytics.track("client_error", { screen: this.#sheet ?? this.#activeSection,
        code: "TAP_DELIVERY_STALLED", pendingCount: this.#service.getPendingTaps().count });
      this.#showToast(this.#translator("spend.stalledTitle"), "info", 5_000, { body: this.#translator("spend.stalledBody"), motif: "energy" });
      return;
    }
    this.#analytics.track("client_error", {
      screen: this.#sheet ?? this.#activeSection,
      code: isGameServiceError(error) ? error.code : "unexpected",
      ...(isGameServiceError(error) && /^[A-Z_]{1,64}$/.test(error.message) ? { apiCode: error.message } : {}),
      pendingCount: this.#service.getPendingTaps().count,
    });
    if (!isGameServiceError(error)) {
      console.error(error);
      this.#showToast(this.#translator("status.actionUnavailable"), "error");
      return;
    }
    if (error.code === "NETWORK") {
      this.#service.setOnline(false);
      this.#runtime?.scene.setNetworkOnline(false);
      this.#networkBanner.hidden = false;
      return;
    }
    if (error.code === "INSUFFICIENT_FUNDS") {
      const missing = error.details.missing ?? "0";
      this.#showToast(
        this.#translator("development.notEnough", {
          value: formatCurrencyAmount(missing, this.#state?.settings.locale ?? "ru"),
        }),
        "info",
        4_000,
        { body: this.#translator("status.notEnoughHint"), motif: "currency" },
      );
      return;
    }
    if (error.code === "SEASON_INACTIVE") {
      this.#showToast(this.#translator("status.seasonInactive"), "info");
      return;
    }
    if (error.code === "ENERGY_FULL") {
      this.#showToast(this.#translator("energy.full"), "info", 3_200, {
        body: this.#translator("energy.fullBody"),
        motif: "energy",
      });
      return;
    }
    if (error.code === "REFILL_LIMIT") {
      this.#showToast(this.#translator("energy.limitTitle"), "info", 4_000, {
        body: this.#translator("energy.limitBody"),
        motif: "energy",
      });
      return;
    }
    if (error.code === "ARENA_SYNC") {
      this.#showToast(this.#translator("games.syncRestored"), "info", 4_000, {
        body: this.#translator("games.syncRestoredBody"),
      });
      return;
    }
    if (error.message === "TASK_NOT_COMPLETE") {
      this.#showToast(this.#translator(error.details.reason === "prerequisite_not_claimed"
        ? "status.previousTaskRequired" : "status.taskNotComplete"), "info", 4_500);
      return;
    }
    if (error.message === "SEQUENCE_CONFLICT") {
      this.#showToast(this.#translator("status.tapUnavailable"), "error", 5_000);
      return;
    }
    this.#showToast(this.#translator("status.actionUnavailable"), "error");
  }

  #showToast(message: string, kind: ToastKind, duration = 2_800, options: ToastOptions = {}): void {
    if (
      kind !== "error" &&
      this.#state !== null &&
      (this.#state.onboarding.status === "not-started" || this.#state.onboarding.status === "in-progress")
    ) {
      return;
    }
    window.clearTimeout(this.#toastTimer);
    this.#toastTitle.textContent = message;
    this.#toastBody.textContent = options.body ?? "";
    this.#toastBody.hidden = options.body === undefined;
    this.#toast.dataset.kind = kind;
    this.#toast.dataset.motif = options.motif ?? "default";
    this.#toast.hidden = false;
    this.#toastTimer = window.setTimeout(() => {
      this.#toast.hidden = true;
    }, duration);
  }

  async #activateOnboardingTarget(): Promise<void> {
    if (this.#state === null || this.#onboardingBusy) return;
    const step = this.#state.onboarding.step;
    if (step === "tap") {
      if (this.#runtime?.scene.playTapFeedback() === true) this.#handleTap();
      return;
    }
    if (step === "map") {
      await this.#performOnboardingAction("open-map");
      return;
    }
    if (step === "workshop") {
      await this.#performOnboardingAction("open-workshop");
      return;
    }
    if (step === "upgrade") {
      await this.#purchaseUpgrade("mechanicalCutter");
      if (this.#state?.onboarding.step === "upgrade" && this.#state.upgrades.mechanicalCutterLevel >= 1) {
        await this.#performOnboardingAction("purchase-contour");
      }
      return;
    }
    if (step === "trophy") {
      await this.#performOnboardingAction("open-trophies");
      return;
    }
    if (step === "season") {
      await this.#performOnboardingAction("view-season");
      return;
    }
    if (step === "season-return") {
      await this.#performOnboardingAction("return-from-season");
      return;
    }
    if (step === "club") {
      await this.#performOnboardingAction("open-club");
      return;
    }
    if (step === "club-link") {
      const copy = this.#detailScreen.querySelector<HTMLElement>(
        ".makers-club-gate [data-action='invite-friend'][data-mode='copy']",
      );
      if (copy !== null) await this.#handleInviteFriend("copy", copy);
      return;
    }
    if (step === "club-return") {
      await this.#performOnboardingAction("return-from-club");
      return;
    }
    if (step === "arena") {
      await this.#performOnboardingAction("open-arena");
      return;
    }
    if (step === "arena-game") await this.#performOnboardingAction("return-from-arena");
  }

  #restoreOnboardingContext(state: GameState | null = this.#state): boolean {
    if (
      state === null ||
      (state.onboarding.status !== "not-started" && state.onboarding.status !== "in-progress")
    ) {
      return false;
    }

    const route = onboardingRouteForStep(state.onboarding.step);
    const changed =
      this.#activeSection !== route.section ||
      this.#mapOpen !== route.mapOpen ||
      this.#progressSegment !== route.progressSegment ||
      this.#arenaView !== route.arenaView;

    this.#activeSection = route.section;
    this.#mapOpen = route.mapOpen;
    this.#progressSegment = route.progressSegment;
    this.#arenaView = route.arenaView;
    if (route.section === "friends") this.#friendFilter = "all";
    return changed;
  }

  #restoreOnboardingViewport(): void {
    if (
      this.#state === null ||
      (this.#state.onboarding.status !== "not-started" && this.#state.onboarding.status !== "in-progress")
    ) {
      return;
    }

    const step = this.#state.onboarding.step;
    const route = onboardingRouteForStep(step);
    if (!route.mapOpen && route.section !== "core") {
      this.#detailScreen.scrollTo({ top: 0, behavior: "auto" });
    }
    window.requestAnimationFrame(() => {
      if (this.#state?.onboarding.step !== step) return;
      if (route.scrollTarget !== null) {
        this.#scrollOnboardingTargetIntoView(
          route.scrollTarget,
          step === "upgrade" || step === "club-link" || step === "arena-game" ? "center" : "start",
        );
        return;
      }
      window.requestAnimationFrame(() => this.#scheduleOnboardingPosition());
    });
  }

  #scrollOnboardingTargetIntoView(selector: string, block: ScrollLogicalPosition): void {
    const target = this.#detailScreen.querySelector<HTMLElement>(selector);
    if (target === null || this.#state === null) {
      this.#scheduleOnboardingPosition();
      return;
    }

    window.clearTimeout(this.#onboardingScrollTimer);
    const smooth = !this.#state.settings.reducedMotion;
    this.#onboardingLayer.dataset.onboardingScrolling = String(smooth);
    target.scrollIntoView({ block, inline: "nearest", behavior: smooth ? "smooth" : "auto" });
    const finish = (): void => {
      delete this.#onboardingLayer.dataset.onboardingScrolling;
      this.#scheduleOnboardingPosition();
    };
    if (smooth) {
      this.#onboardingScrollTimer = window.setTimeout(finish, GAME_CONFIG.motion.onboardingScrollMs);
    } else {
      window.requestAnimationFrame(finish);
    }
  }

  async #performOnboardingAction(action: OnboardingAction): Promise<void> {
    if (this.#onboardingBusy || this.#state === null) return;
    this.#onboardingBusy = true;
    this.#onboardingLayer.dataset.onboardingBusy = "true";
    let recovered = false;
    let attempted: GameState["onboarding"] | null = null;
    try {
      await this.#flushTaps();
      if (this.#state === null) return;
      const checkpoint = structuredClone(this.#state.onboarding);
      attempted = checkpoint;
      const result = await this.#service.advanceOnboarding(commandId(`onboarding-${action}`), action);
      this.#analytics.track(action === "skip" ? "onboarding_skip" : "onboarding_step_complete", {
        action,
        step: checkpoint.step,
        version: checkpoint.version,
      });
      this.#applyEnvelope(result, { details: false, sheet: false });
      if (
        action === "open-map" ||
        action === "observe-passive" ||
        action === "claim-first-output" ||
        action === "return-from-club"
      ) {
        this.#transitionView(() => this.#openMap());
      } else if (action === "open-workshop") {
        this.#navigate("development");
        window.requestAnimationFrame(() =>
          this.#scrollOnboardingTargetIntoView("[data-testid='buy-cutter']", "center"),
        );
      } else if (action === "open-trophies") {
        this.#progressSegment = this.#state?.onboarding.step === "season-overview" ? "season" : "today";
        this.#navigate("progress");
      } else if (action === "explain-trophies") {
        this.#progressSegment = "today";
        this.#renderDetails();
      } else if (action === "view-season") {
        this.#progressSegment = "season";
        this.#renderDetails();
        window.requestAnimationFrame(() =>
          this.#detailScreen.querySelector<HTMLElement>(".trophy-season-chronicle")?.scrollIntoView({ block: "start", behavior: "auto" }),
        );
      } else if (action === "explain-season") {
        window.requestAnimationFrame(() => this.#scheduleOnboardingPosition());
      } else if (action === "return-from-season") {
        this.#transitionView(() => {
          if (this.#state?.onboarding.step === "complete") this.#navigate("core");
          else this.#openMap();
        });
      } else if (action === "open-club") {
        this.#friendFilter = "all";
        this.#navigate("friends");
      } else if (action === "explain-club") {
        window.requestAnimationFrame(() =>
          this.#scrollOnboardingTargetIntoView(".makers-club-gate", "center"),
        );
      } else if (action === "open-arena") {
        this.#arenaView = "catalog";
        this.#navigate("games");
      } else if (action === "explain-arena") {
        window.requestAnimationFrame(() =>
          this.#scrollOnboardingTargetIntoView(".arena-game-card.is-playable", "center"),
        );
      } else if (action === "return-from-arena") {
        this.#transitionView(() => this.#navigate("core"));
      } else if (action === "finish") {
        this.#transitionView(() => this.#navigate("core"));
      }
    } catch (error) {
      if (isGameServiceError(error) && error.code === "INVALID_ACTION" && this.#state !== null) {
        const checkpoint = attempted ?? structuredClone(this.#state.onboarding);
        try {
          const refreshed = await this.#service.refreshState();
          this.#applyEnvelope(refreshed, { details: true, sheet: true });
          const current = this.#state?.onboarding;
          recovered =
            current !== undefined &&
            (current.status === "completed" ||
              current.status === "skipped" ||
              current.version !== checkpoint.version ||
              current.step !== checkpoint.step ||
              current.status !== checkpoint.status);
          if (recovered && this.#state !== null) {
            if (isOnboardingActive(this.#state.onboarding)) {
              this.#restoreOnboardingContext();
              this.#updateView({ navigation: true, details: true, sheet: true });
              this.#restoreOnboardingViewport();
            } else {
              this.#transitionView(() => this.#navigate("core"));
            }
          }
        } catch (refreshError) {
          console.warn("Onboarding recovery sync failed", refreshError);
        }
      }
      if (!recovered) this.#handleServiceError(error);
    } finally {
      this.#onboardingBusy = false;
      delete this.#onboardingLayer.dataset.onboardingBusy;
      this.#syncOnboarding();
    }
  }

  #maybeConfirmOnboardingTaps(): void {
    if (this.#state === null || this.#onboardingBusy) return;
    const onboarding = this.#state.onboarding;
    if (onboarding.status !== "in-progress" || onboarding.step !== "tap") {
      this.#onboardingTapConfirmationKey = null;
      return;
    }
    const confirmed = this.#state.progress.confirmedTaps - onboarding.tapBaseline;
    if (confirmed < onboarding.tapTarget) {
      this.#onboardingTapConfirmationKey = null;
      return;
    }
    if (this.#tapBatchCount !== 0 || this.#pendingTapCount !== 0 || this.#tapRequests.size !== 0) return;
    const confirmationKey = `${onboarding.version}:${onboarding.tapBaseline}:${this.#state.progress.confirmedTaps}`;
    if (this.#onboardingTapConfirmationKey === confirmationKey) return;
    this.#onboardingTapConfirmationKey = confirmationKey;
    void this.#performOnboardingAction("confirm-taps");
  }

  #syncOnboarding(previousState: GameState | null = null): void {
    if (this.#state === null) return;
    const state = this.#state;
    const visible =
      state.season.status === "active" &&
      this.#sheet !== "offline-reward" &&
      (state.onboarding.status === "not-started" || state.onboarding.status === "in-progress");
    if (!visible) {
      window.clearTimeout(this.#onboardingScrollTimer);
      delete this.#onboardingLayer.dataset.onboardingScrolling;
      this.#runtime?.scene.setOnboardingFocus(false);
      delete this.#shell.dataset.onboardingStep;
      this.#onboardingLayer.hidden = true;
      this.#onboardingFocusClone.hidden = true;
      this.#onboardingFocusClone.replaceChildren();
      this.#clearOnboardingCutout();
      if (this.#runtime !== null) this.#runtime.scene.input.enabled = true;
      this.#disposeOnboardingRuntime();
      return;
    }

    const model = onboardingUiModel(state, this.#serverNow, this.#translator);
    const viewKey = `${state.onboarding.version}:${state.onboarding.step}`;
    if (this.#lastOnboardingViewKey !== viewKey) {
      this.#lastOnboardingViewKey = viewKey;
      this.#analytics.track("onboarding_step_view", {
        step: state.onboarding.step,
        version: state.onboarding.version,
      });
    }
    const previousStep = previousState?.onboarding.step;
    this.#runtime?.scene.setOnboardingFocus(model.step === "tap");
    if (this.#runtime !== null) this.#runtime.scene.input.enabled = false;
    this.#onboardingLayer.hidden = false;
    this.#onboardingLayer.dataset.onboardingStep = model.step;
    this.#shell.dataset.onboardingStep = model.step;
    if (previousStep !== undefined && previousStep !== model.step) {
      this.#onboardingLayer.dataset.onboardingTarget = "false";
      window.clearTimeout(this.#onboardingScrollTimer);
      delete this.#onboardingLayer.dataset.onboardingScrolling;
      this.#onboardingFocusClone.hidden = true;
      this.#clearOnboardingCutout();
    }
    const setText = (selector: string, value: string): void => {
      const element = this.#onboardingLayer.querySelector<HTMLElement>(selector);
      if (element !== null) element.textContent = value;
    };
    setText("[data-onboarding-kicker]", model.kicker);
    setText("[data-onboarding-title]", model.title);
    setText("[data-onboarding-body]", model.body);

    const seasonHero = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-season-hero]");
    if (seasonHero !== null) {
      seasonHero.hidden = model.seasonHero === null;
      setText("[data-onboarding-season-hero-label]", model.seasonHero?.label ?? "");
      setText("[data-onboarding-season-hero-value]", model.seasonHero?.value ?? "");
    }

    const season = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-season]");
    if (season !== null) {
      season.hidden = model.seasonSummary === null;
      setText("[data-onboarding-fund]", model.seasonSummary?.fund ?? "");
      setText("[data-onboarding-time]", model.seasonSummary?.time ?? "");
      setText("[data-onboarding-rule]", model.seasonSummary?.rule ?? "");
    }

    const seasonMetrics = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-season-metrics]");
    if (seasonMetrics !== null) {
      seasonMetrics.hidden = model.seasonMetrics === null;
      setText("[data-onboarding-place-label]", model.seasonMetrics?.placeLabel ?? "");
      setText("[data-onboarding-place]", model.seasonMetrics?.place ?? "");
      setText("[data-onboarding-share-label]", model.seasonMetrics?.shareLabel ?? "");
      setText("[data-onboarding-share]", model.seasonMetrics?.share ?? "");
      setText("[data-onboarding-reward-label]", model.seasonMetrics?.rewardLabel ?? "");
      setText("[data-onboarding-reward]", model.seasonMetrics?.reward ?? "");
    }

    const progress = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-progress]");
    if (progress !== null) {
      progress.hidden = model.progress === null;
      progress.dataset.emphasis = String(model.progressValue !== null);
      const progressValue = progress.querySelector<HTMLElement>("[data-onboarding-progress-value]");
      if (progressValue !== null) {
        progressValue.hidden = model.progressValue === null;
        progressValue.textContent = model.progressValue ?? "";
      }
      setText("[data-onboarding-progress-label]", model.progress ?? "");
      const meter = progress.querySelector<HTMLElement>("[role='progressbar']");
      meter?.setAttribute("aria-valuenow", String(Math.round(model.progressRatio * 100)));
      meter?.querySelector<HTMLElement>("b")?.style.setProperty("--onboarding-progress", String(model.progressRatio));
    }

    const actions = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-actions]");
    if (actions !== null) actions.hidden = model.primaryLabel === null;
    const primary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-primary");
    if (primary !== null) {
      primary.textContent = model.primaryLabel ?? "";
      primary.dataset.action = "onboarding-primary";
      primary.dataset.onboardingCommand = model.primaryAction ?? "";
    }
    const secondary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-skip");
    if (secondary !== null) {
      secondary.hidden = model.secondaryLabel === null;
      secondary.textContent = model.secondaryLabel ?? "";
    }
    const spotlight = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-spotlight");
    if (spotlight !== null) {
      spotlight.hidden = model.targetLabel === null;
      spotlight.setAttribute("aria-label", model.targetLabel ?? model.title);
    }

    this.#mountOnboardingRuntime(model.pose);
    this.#mountOnboardingPointerRuntime();
    this.#onboardingRuntime?.setPresentation({
      pose: model.pose,
      mirror: ONBOARDING_GUIDE_STAGING[model.step].mirror,
      reducedMotion: state.settings.reducedMotion,
      compact: model.targetLabel !== null,
    });
    this.#scheduleOnboardingPosition();

    if (model.step === "complete" && previousStep !== "complete" && !this.#onboardingCompletionCelebrated) {
      this.#onboardingCompletionCelebrated = true;
      void this.#onboardingRuntime?.ready.then(() => this.#onboardingRuntime?.celebrate());
    }
    if (model.step !== "complete") this.#onboardingCompletionCelebrated = false;

    if (model.step === "tap" && model.progressRatio >= 1 && !this.#onboardingBusy) {
      window.queueMicrotask(() => this.#maybeConfirmOnboardingTaps());
    } else if (model.primaryAction !== null && previousStep !== model.step) {
      window.requestAnimationFrame(() => primary?.focus({ preventScroll: true }));
    }
  }

  #mountOnboardingRuntime(pose: GuidePose): void {
    if (this.#state === null || this.#onboardingRuntime !== null) return;
    const host = this.#onboardingLayer.querySelector<HTMLElement>("#onboarding-guide-runtime");
    if (host === null) return;
    const runtime = createOnboardingGuideGame(host, {
      pose,
      mirror: false,
      reducedMotion: this.#state.settings.reducedMotion,
      compact: onboardingUiModel(this.#state, this.#serverNow, this.#translator).targetLabel !== null,
    });
    this.#onboardingRuntime = runtime;
    const resize = (): void => {
      if (this.#onboardingRuntime !== runtime) return;
      const width = Math.round(host.clientWidth);
      const height = Math.round(host.clientHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#onboardingResizeObserver = new ResizeObserver(resize);
      this.#onboardingResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#onboardingRuntime !== runtime) return;
      host.dataset.phaserReady = "true";
      resize();
    });
  }

  #disposeOnboardingRuntime(): void {
    this.#onboardingResizeObserver?.disconnect();
    this.#onboardingResizeObserver = null;
    this.#onboardingRuntime?.destroy();
    this.#onboardingRuntime = null;
    this.#onboardingPointerResizeObserver?.disconnect();
    this.#onboardingPointerResizeObserver = null;
    this.#onboardingPointerRuntime?.destroy();
    this.#onboardingPointerRuntime = null;
  }

  #mountOnboardingPointerRuntime(): void {
    if (this.#state === null || this.#onboardingPointerRuntime !== null) return;
    const host = this.#onboardingLayer.querySelector<HTMLElement>("#onboarding-pointer-runtime");
    if (host === null) return;
    const runtime = createOnboardingPointerGame(host, {
      visible: false,
      x: 0,
      y: 0,
      direction: "down",
      reducedMotion: this.#state.settings.reducedMotion,
    });
    this.#onboardingPointerRuntime = runtime;
    const resize = (): void => {
      if (this.#onboardingPointerRuntime !== runtime) return;
      const width = Math.round(host.clientWidth || window.innerWidth);
      const height = Math.round(host.clientHeight || window.innerHeight);
      if (width > 0 && height > 0) runtime.resize(width, height);
    };
    if (typeof ResizeObserver !== "undefined") {
      this.#onboardingPointerResizeObserver = new ResizeObserver(resize);
      this.#onboardingPointerResizeObserver.observe(host);
    }
    void runtime.ready.then(() => {
      if (this.#onboardingPointerRuntime !== runtime) return;
      host.dataset.phaserReady = "true";
      resize();
      this.#scheduleOnboardingPosition();
    });
  }

  #scheduleOnboardingPosition(): void {
    window.cancelAnimationFrame(this.#onboardingPositionFrame);
    this.#onboardingPositionFrame = window.requestAnimationFrame(() => this.#positionOnboardingTarget());
  }

  #positionOnboardingTarget(): void {
    if (this.#state === null || this.#onboardingLayer.hidden) {
      this.#positionOnboardingTargetLegacy();
      return;
    }

    this.#clearOnboardingCutout();
    const step = this.#state.onboarding.step;
    const spec = ONBOARDING_TARGETS[step];
    const spotlight = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-spotlight");
    if (spotlight === null) return;

    if (spec === undefined) {
      this.#hideOnboardingTarget(spotlight);
      this.#positionUntargetedGuide();
      return;
    }

    const actionTarget = this.#root.querySelector<HTMLElement>(spec.actionSelector);
    const focusTarget = this.#root.querySelector<HTMLElement>(spec.focusSelector ?? spec.actionSelector);
    if (actionTarget === null || focusTarget === null) {
      this.#hideOnboardingTarget(spotlight);
      if (this.#restoreOnboardingContext()) {
        this.#updateView({ navigation: true, details: true, sheet: false });
        this.#restoreOnboardingViewport();
      } else if (this.#restoreOnboardingDialogActions()) {
        this.#positionUntargetedGuide();
      } else {
        const actions = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-actions]");
        const primary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-primary");
        const secondary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-skip");
        if (actions !== null) actions.hidden = false;
        if (primary !== null) {
          primary.textContent = this.#translator("onboarding.recover");
          primary.dataset.action = "onboarding-recover";
          primary.dataset.onboardingCommand = "";
        }
        if (secondary !== null) secondary.hidden = true;
      }
      return;
    }

    let actionRect = actionTarget.getBoundingClientRect();
    let focusRect = focusTarget.getBoundingClientRect();
    let pointerRect = actionRect;
    let focusMask: string | null = null;
    let cutoutMask: string | null = null;

    if (step === "tap" && this.#runtime !== null) {
      const layout = this.#runtime.scene.getDiamondLayout();
      const stage = this.#root.querySelector<HTMLElement>("#workshop-root")?.getBoundingClientRect();
      if (stage !== undefined && stage !== null && layout.canvasWidth > 0 && layout.canvasHeight > 0) {
        const width = (layout.width / layout.canvasWidth) * stage.width;
        const height = (layout.height / layout.canvasHeight) * stage.height;
        const left = stage.left + (layout.left / layout.canvasWidth) * stage.width;
        const top = stage.top + (layout.top / layout.canvasHeight) * stage.height;
        focusRect = new DOMRect(left, top, width, height);
        actionRect = focusRect;
        pointerRect = focusRect;
        const asset = layout.upgraded ? FACET_QUARTER_ART.diamond.upgraded : FACET_QUARTER_ART.diamond.base;
        focusMask = `url(${JSON.stringify(asset.url)})`;
        cutoutMask = focusMask;
      }
    } else if (spec.focusKind === "landmark") {
      const landmark = this.#landmarkOnboardingMask(actionTarget);
      if (landmark !== null) {
        focusRect = landmark.mapRect;
        pointerRect = landmark.hitRect;
        focusMask = landmark.mask;
        cutoutMask = landmark.mask;
      }
    } else if (spec.focusKind === "panel") {
      focusRect = this.#visibleOnboardingRect(focusRect);
      const radius = Math.max(10, Number.parseFloat(getComputedStyle(focusTarget).borderRadius) || 18);
      cutoutMask = this.#roundedOnboardingMask(focusRect.width, focusRect.height, radius);
    }

    this.#onboardingFocusClone.dataset.kind = spec.focusKind;
    this.#onboardingFocusClone.style.left = `${focusRect.left}px`;
    this.#onboardingFocusClone.style.top = `${focusRect.top}px`;
    this.#onboardingFocusClone.style.width = `${focusRect.width}px`;
    this.#onboardingFocusClone.style.height = `${focusRect.height}px`;
    if (focusMask !== null) this.#onboardingFocusClone.style.setProperty("--onboarding-focus-mask", focusMask);
    if (spec.focusKind === "content") this.#cloneOnboardingContent(focusTarget);
    this.#onboardingFocusClone.hidden = false;

    if (cutoutMask !== null) this.#applyOnboardingCutout(cutoutMask, focusRect, spec.focusKind);

    const padding = spec.focusKind === "landmark" ? 5 : 3;
    const left = Math.max(2, actionRect.left - padding);
    const top = Math.max(2, actionRect.top - padding);
    const right = Math.min(window.innerWidth - 2, actionRect.right + padding);
    const bottom = Math.min(window.innerHeight - 2, actionRect.bottom + padding);
    spotlight.style.left = `${left}px`;
    spotlight.style.top = `${top}px`;
    spotlight.style.width = `${Math.max(44, right - left)}px`;
    spotlight.style.height = `${Math.max(44, bottom - top)}px`;
    spotlight.hidden = spec.interactive === false;
    spotlight.toggleAttribute("aria-hidden", spec.interactive === false);

    this.#onboardingLayer.dataset.onboardingTarget = "true";
    const placement = actionRect.top + actionRect.height / 2 > window.innerHeight * 0.56 ? "top" : "bottom";
    this.#onboardingLayer.dataset.onboardingPlacement = placement;
    const staging = ONBOARDING_GUIDE_STAGING[step];
    const fallbackSide = actionRect.left + actionRect.width / 2 >= window.innerWidth / 2 ? "left" : "right";
    const guideSide = staging.side === "center" ? fallbackSide : staging.side;
    this.#onboardingLayer.dataset.onboardingGuideSide = guideSide;
    this.#positionTargetedGuide(focusRect, actionRect, step, guideSide);

    const model = onboardingUiModel(this.#state, this.#serverNow, this.#translator);
    this.#onboardingRuntime?.setPresentation({
      pose: model.pose,
      mirror: staging.mirror,
      reducedMotion: this.#state.settings.reducedMotion,
      compact: true,
    });
    this.#showOnboardingPointer(spec.pointer, pointerRect);
  }

  #restoreOnboardingDialogActions(): boolean {
    if (this.#state === null) return false;
    const model = onboardingUiModel(this.#state, this.#serverNow, this.#translator);
    if (model.primaryLabel === null || model.primaryAction === null) return false;

    const actions = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-actions]");
    const primary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-primary");
    const secondary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-skip");
    if (actions !== null) actions.hidden = false;
    if (primary !== null) {
      primary.textContent = model.primaryLabel;
      primary.dataset.action = "onboarding-primary";
      primary.dataset.onboardingCommand = model.primaryAction;
    }
    if (secondary !== null) {
      secondary.hidden = model.secondaryLabel === null;
      secondary.textContent = model.secondaryLabel ?? "";
    }
    return true;
  }

  #hideOnboardingTarget(spotlight: HTMLButtonElement): void {
    this.#onboardingLayer.dataset.onboardingTarget = "false";
    delete this.#onboardingLayer.dataset.onboardingPlacement;
    delete this.#onboardingLayer.dataset.onboardingGuideSide;
    delete this.#onboardingLayer.dataset.onboardingPointer;
    spotlight.hidden = true;
    this.#onboardingFocusClone.hidden = true;
    this.#onboardingFocusClone.replaceChildren();
    this.#onboardingPointerRuntime?.setPresentation({
      visible: false,
      x: 0,
      y: 0,
      direction: "down",
      reducedMotion: this.#state?.settings.reducedMotion ?? false,
    });
  }

  #positionUntargetedGuide(): void {
    const guide = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-guide-stage");
    const dialog = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dialog");
    if (guide === null || dialog === null) return;
    this.#resetOnboardingGuidePosition(guide);
    const insets = this.#onboardingInsets();
    const dialogRect = dialog.getBoundingClientRect();
    const step = this.#state?.onboarding.step;
    const staging = step === undefined ? null : ONBOARDING_GUIDE_STAGING[step];
    if (staging !== null && staging.side !== "center") {
      this.#onboardingLayer.dataset.onboardingGuideSide = staging.side;
      const width = Math.round(Math.min(132, window.innerWidth * 0.35));
      // Compact Telegram viewports have less room above a tall explanatory
      // dialog. Let the side guide shrink instead of slipping behind it.
      const minimumHeight = window.innerHeight <= 620 ? 60 : 164;
      const height = Math.round(Math.min(232, Math.max(minimumHeight, dialogRect.top - insets.top - 20)));
      guide.style.width = `${width}px`;
      guide.style.height = `${height}px`;
      const landmarkTop =
        step === "trophy-overview"
          ? this.#detailScreen.querySelector<HTMLElement>(".trophy-hall-portals")?.getBoundingClientRect().top
          : undefined;
      const bottom = Math.min(dialogRect.top - 10, landmarkTop === undefined ? Number.POSITIVE_INFINITY : landmarkTop - 10);
      guide.style.top = `${Math.round(Math.max(insets.top, bottom - height))}px`;
      guide.style.bottom = "auto";
      const shellRect = this.#shell.getBoundingClientRect();
      const sideX = staging.side === "left" ? shellRect.left + 8 : shellRect.right - width - 8;
      guide.style.left = `${Math.round(Math.max(8, Math.min(window.innerWidth - width - 8, sideX)))}px`;
      guide.style.right = "auto";
      guide.style.transform = "none";
      return;
    }
    delete this.#onboardingLayer.dataset.onboardingGuideSide;
    const guideRect = guide.getBoundingClientRect();
    const dialogGap = 12;
    const availableHeight = Math.max(80, dialogRect.top - insets.top - dialogGap);
    const height = Math.min(guideRect.height, availableHeight);
    guide.style.top = `${Math.round(Math.max(insets.top, dialogRect.top - height - dialogGap))}px`;
    guide.style.bottom = "auto";
    guide.style.height = `${Math.round(height)}px`;
    guide.style.left = "50%";
    guide.style.right = "auto";
  }

  #positionTargetedGuide(
    focusRect: DOMRect,
    actionRect: DOMRect,
    step: OnboardingStep,
    guideSide: Exclude<OnboardingGuideSide, "center">,
  ): void {
    const guide = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-guide-stage");
    const dialog = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dialog");
    if (guide === null || dialog === null) return;
    this.#resetOnboardingGuidePosition(guide);
    const insets = this.#onboardingInsets();
    const dialogRect = dialog.getBoundingClientRect();
    if (SMALL_TARGET_GUIDE_STEPS.has(step)) {
      guide.style.width = `${Math.round(Math.min(116, window.innerWidth * 0.31))}px`;
      guide.style.height = `${Math.round(Math.min(194, window.innerHeight * 0.25))}px`;
    }
    const natural = guide.getBoundingClientRect();
    const placement = this.#onboardingLayer.dataset.onboardingPlacement;
    const zoneTop = placement === "top" ? dialogRect.bottom + 8 : insets.top;
    const zoneBottom = placement === "top" ? window.innerHeight - insets.bottom - 8 : dialogRect.top - 8;
    const zoneHeight = Math.max(96, zoneBottom - zoneTop);
    const height = Math.min(natural.height, zoneHeight);
    if (height < natural.height - 1) guide.style.height = `${Math.round(height)}px`;

    let desiredTop = focusRect.top - height + 8;
    if (step === "upgrade") desiredTop = actionRect.bottom + 8;
    if (desiredTop < zoneTop || desiredTop + height > zoneBottom) {
      const belowTarget = focusRect.bottom + 8;
      if (belowTarget + height <= zoneBottom) desiredTop = belowTarget;
    }
    const top = Math.max(zoneTop, Math.min(zoneBottom - height, desiredTop));
    guide.style.top = `${Math.round(top)}px`;
    guide.style.bottom = "auto";

    const shellRect = this.#shell.getBoundingClientRect();
    const width = guide.getBoundingClientRect().width || natural.width;
    const x = guideSide === "left" ? shellRect.left + 8 : shellRect.right - width - 8;
    guide.style.left = `${Math.round(Math.max(6, Math.min(window.innerWidth - width - 6, x)))}px`;
    guide.style.right = "auto";
  }

  #resetOnboardingGuidePosition(guide: HTMLElement): void {
    guide.style.removeProperty("top");
    guide.style.removeProperty("right");
    guide.style.removeProperty("bottom");
    guide.style.removeProperty("left");
    guide.style.removeProperty("width");
    guide.style.removeProperty("height");
    guide.style.removeProperty("transform");
  }

  #onboardingInsets(): { top: number; bottom: number } {
    const style = getComputedStyle(document.documentElement);
    const pixels = (name: string): number => Number.parseFloat(style.getPropertyValue(name)) || 0;
    return {
      top: pixels("--safe-top") + pixels("--app-top-extra") + 8,
      bottom: pixels("--safe-bottom"),
    };
  }

  #visibleOnboardingRect(rect: DOMRect): DOMRect {
    const insets = this.#onboardingInsets();
    const left = Math.max(6, rect.left);
    const top = Math.max(insets.top, rect.top);
    const right = Math.min(window.innerWidth - 6, rect.right);
    const bottom = Math.min(window.innerHeight - insets.bottom - 6, rect.bottom);
    return new DOMRect(left, top, Math.max(1, right - left), Math.max(1, bottom - top));
  }

  #landmarkOnboardingMask(target: HTMLElement): { mask: string; mapRect: DOMRect; hitRect: DOMRect } | null {
    const svg = target.closest<SVGSVGElement>("svg.quarter-map");
    const hit = target.querySelector<SVGPathElement>(".landmark-hit");
    const path = hit?.getAttribute("d");
    if (svg === null || hit === null || path === null) return null;

    const viewBox = svg.getAttribute("viewBox") ?? "0 0 941 1672";
    const plaque = target.querySelector<SVGImageElement>(".landmark-plaque");
    const plaqueGroup = target.querySelector<SVGGElement>(".landmark-label");
    let plaqueMask = "";
    if (plaque !== null) {
      const x = plaque.getAttribute("x") ?? "0";
      const y = plaque.getAttribute("y") ?? "0";
      const width = plaque.getAttribute("width") ?? "0";
      const height = plaque.getAttribute("height") ?? "0";
      const transform = plaqueGroup?.getAttribute("transform") ?? "";
      plaqueMask = `<g transform="${transform}"><rect x="${x}" y="${y}" width="${width}" height="${height}" rx="14" fill="#000"/></g>`;
    }
    const source = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}"><path d="${path}" fill="#000" stroke="#000" stroke-width="18" stroke-linejoin="round"/>${plaqueMask}</svg>`;
    return {
      mask: this.#onboardingMaskUrl(source),
      mapRect: svg.getBoundingClientRect(),
      hitRect: hit.getBoundingClientRect(),
    };
  }

  #roundedOnboardingMask(width: number, height: number, radius: number): string {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    const r = Math.max(0, Math.min(Math.round(radius), Math.floor(Math.min(w, h) / 2)));
    return this.#onboardingMaskUrl(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}"><rect width="${w}" height="${h}" rx="${r}" fill="#000"/></svg>`,
    );
  }

  #onboardingMaskUrl(source: string): string {
    return `url("data:image/svg+xml,${encodeURIComponent(source)}")`;
  }

  #cloneOnboardingContent(target: HTMLElement): void {
    const clone = target.cloneNode(true) as HTMLElement;
    const strip = (element: Element): void => {
      element.removeAttribute("id");
      element.removeAttribute("data-action");
      element.removeAttribute("tabindex");
      element.removeAttribute("href");
      element.setAttribute("aria-hidden", "true");
    };
    strip(clone);
    clone.querySelectorAll("*").forEach(strip);
    this.#onboardingFocusClone.replaceChildren(clone);
  }

  #applyOnboardingCutout(mask: string, rect: DOMRect, kind: OnboardingFocusKind): void {
    const dim = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dim");
    if (dim === null) return;
    dim.dataset.cutoutKind = kind;
    dim.style.setProperty("--onboarding-cutout-image", mask);
    dim.style.setProperty("--onboarding-cutout-left", `${rect.left}px`);
    dim.style.setProperty("--onboarding-cutout-top", `${rect.top}px`);
    dim.style.setProperty("--onboarding-cutout-width", `${rect.width}px`);
    dim.style.setProperty("--onboarding-cutout-height", `${rect.height}px`);
  }

  #showOnboardingPointer(direction: OnboardingPointerDirection | null, rect: DOMRect): void {
    if (direction === null || this.#state === null) {
      this.#onboardingPointerRuntime?.setPresentation({
        visible: false,
        x: 0,
        y: 0,
        direction: "down",
        reducedMotion: this.#state?.settings.reducedMotion ?? false,
      });
      delete this.#onboardingLayer.dataset.onboardingPointer;
      return;
    }
    const gap = 31;
    let x = rect.left + rect.width / 2;
    let y = rect.top - gap;
    if (direction === "up") y = rect.bottom + gap;
    if (direction === "left") {
      x = rect.right + gap;
      y = rect.top + rect.height / 2;
    }
    if (direction === "right") {
      x = rect.left - gap;
      y = rect.top + rect.height / 2;
    }
    this.#onboardingLayer.dataset.onboardingPointer = direction;
    this.#onboardingPointerRuntime?.setPresentation({
      visible: true,
      x,
      y,
      direction,
      reducedMotion: this.#state.settings.reducedMotion,
    });
  }

  #positionOnboardingTargetLegacy(): void {
    if (this.#state === null || this.#onboardingLayer.hidden) return;
    this.#clearOnboardingCutout();
    const selectors: Partial<Record<GameState["onboarding"]["step"], string>> = {
      tap: ".keyboard-tap-target",
      map: ".quarter-portal",
      workshop: ".quarter-landmark[data-section='development']",
      upgrade: "[data-testid='buy-cutter']",
      trophy: ".quarter-landmark[data-section='progress']",
      season: "#progress-tab-season",
      "season-return": ".map-return",
      club: ".quarter-landmark[data-section='friends']",
      "club-return": ".map-return",
      arena: ".quarter-landmark[data-section='games']",
    };
    const selector = selectors[this.#state.onboarding.step];
    const spotlight = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-spotlight");
    if (selector === undefined || spotlight === null) {
      this.#onboardingLayer.dataset.onboardingTarget = "false";
      delete this.#onboardingLayer.dataset.onboardingPlacement;
      delete this.#onboardingLayer.dataset.onboardingGuideSide;
      const guide = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-guide-stage");
      const dialog = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dialog");
      if (guide !== null) {
        guide.style.removeProperty("top");
        guide.style.removeProperty("bottom");
        guide.style.removeProperty("left");
        guide.style.removeProperty("right");
        guide.style.removeProperty("height");
        if (dialog !== null) {
          const naturalGuideRect = guide.getBoundingClientRect();
          const dialogRect = dialog.getBoundingClientRect();
          const anchorBottom = dialogRect.top - 6;
          const availableHeight = Math.max(120, anchorBottom - naturalGuideRect.top);
          const guideHeight = Math.min(naturalGuideRect.height, availableHeight);
          guide.style.top = `${Math.round(anchorBottom - guideHeight)}px`;
          guide.style.bottom = "auto";
          guide.style.height = `${Math.round(guideHeight)}px`;
          guide.style.left = "50%";
          guide.style.right = "auto";
        }
      }
      if (spotlight !== null) spotlight.hidden = true;
      this.#onboardingFocusClone.hidden = true;
      return;
    }
    const target = this.#root.querySelector<HTMLElement>(selector);
    if (target === null) {
      this.#onboardingLayer.dataset.onboardingTarget = "false";
      spotlight.hidden = true;
      this.#onboardingFocusClone.hidden = true;
      if (this.#restoreOnboardingContext()) {
        this.#updateView({ navigation: true, details: true, sheet: false });
        this.#restoreOnboardingViewport();
      } else if (this.#restoreOnboardingDialogActions()) {
        this.#positionUntargetedGuide();
      } else {
        const actions = this.#onboardingLayer.querySelector<HTMLElement>("[data-onboarding-actions]");
        const primary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-primary");
        const secondary = this.#onboardingLayer.querySelector<HTMLButtonElement>(".onboarding-skip");
        if (actions !== null) actions.hidden = false;
        if (primary !== null) {
          primary.textContent = this.#translator("onboarding.recover");
          primary.dataset.action = "onboarding-recover";
        }
        if (secondary !== null) secondary.hidden = true;
      }
      return;
    }

    const step = this.#state.onboarding.step;
    let rect = target.getBoundingClientRect();
    let focusRect: Pick<DOMRect, "left" | "top" | "right" | "bottom" | "width" | "height"> = rect;
    let focusAsset: { url: string } | null = null;
    let focusKind: "asset" | "control" | "landmark" = "control";

    if (step === "tap" && this.#runtime !== null) {
      const layout = this.#runtime.scene.getDiamondLayout();
      const stage = this.#root.querySelector<HTMLElement>("#workshop-root")?.getBoundingClientRect();
      if (stage !== undefined && stage !== null && layout.canvasWidth > 0 && layout.canvasHeight > 0) {
        const width = (layout.width / layout.canvasWidth) * stage.width;
        const height = (layout.height / layout.canvasHeight) * stage.height;
        const left = stage.left + (layout.left / layout.canvasWidth) * stage.width;
        const top = stage.top + (layout.top / layout.canvasHeight) * stage.height;
        focusRect = { left, top, right: left + width, bottom: top + height, width, height };
        rect = new DOMRect(left, top, width, height);
        focusAsset = {
          url: layout.upgraded ? FACET_QUARTER_ART.diamond.upgraded.url : FACET_QUARTER_ART.diamond.base.url,
        };
        focusKind = "asset";
      }
    } else if (target.classList.contains("quarter-landmark")) {
      const object = target.closest(".landmark-entry")?.querySelector<SVGImageElement>(".landmark-object");
      if (object !== null && object !== undefined) {
        const objectRect = object.getBoundingClientRect();
        const left = Math.max(6, objectRect.left);
        const top = Math.max(6, objectRect.top);
        const right = Math.min(window.innerWidth - 6, objectRect.right);
        const bottom = Math.min(window.innerHeight - 6, objectRect.bottom);
        focusRect = {
          left,
          top,
          right,
          bottom,
          width: Math.max(44, right - left),
          height: Math.max(44, bottom - top),
        };
        rect = new DOMRect(left, top, focusRect.width, focusRect.height);
        focusKind = "landmark";
      }
    }

    this.#onboardingFocusClone.replaceChildren();
    this.#onboardingFocusClone.dataset.kind = focusKind;
    this.#onboardingFocusClone.style.left = `${focusRect.left}px`;
    this.#onboardingFocusClone.style.top = `${focusRect.top}px`;
    this.#onboardingFocusClone.style.width = `${focusRect.width}px`;
    this.#onboardingFocusClone.style.height = `${focusRect.height}px`;
    if (focusKind === "asset" && focusAsset !== null) {
      const mask = `url(${JSON.stringify(focusAsset.url)})`;
      this.#onboardingFocusClone.style.setProperty("--onboarding-focus-mask", mask);
      this.#onboardingFocusClone.style.removeProperty("border-radius");
      this.#onboardingFocusClone.style.removeProperty("clip-path");
    } else if (focusKind === "landmark") {
      this.#onboardingFocusClone.style.removeProperty("--onboarding-focus-mask");
      this.#onboardingFocusClone.style.borderRadius = "14px";
      this.#onboardingFocusClone.style.clipPath = "none";
    } else {
      const targetStyle = getComputedStyle(target);
      this.#onboardingFocusClone.style.removeProperty("--onboarding-focus-mask");
      this.#onboardingFocusClone.style.borderRadius = targetStyle.borderRadius;
      this.#onboardingFocusClone.style.clipPath = targetStyle.clipPath;
    }
    this.#onboardingFocusClone.hidden = false;

    const dim = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dim");
    if (dim !== null) {
      dim.dataset.cutoutKind = focusKind === "asset" ? "asset" : "control";
      dim.style.setProperty(
        "--onboarding-cutout-image",
        focusKind === "asset" && focusAsset !== null
          ? `url(${JSON.stringify(focusAsset.url)})`
          : "linear-gradient(#000 0 0)",
      );
      dim.style.setProperty("--onboarding-cutout-left", `${focusRect.left}px`);
      dim.style.setProperty("--onboarding-cutout-top", `${focusRect.top}px`);
      dim.style.setProperty("--onboarding-cutout-width", `${focusRect.width}px`);
      dim.style.setProperty("--onboarding-cutout-height", `${focusRect.height}px`);
    }

    const padding = focusKind === "landmark" ? 8 : focusKind === "control" ? 5 : 2;
    const left = Math.max(6, focusRect.left - padding);
    const top = Math.max(6, focusRect.top - padding);
    const right = Math.min(window.innerWidth - 6, focusRect.right + padding);
    const bottom = Math.min(window.innerHeight - 6, focusRect.bottom + padding);
    spotlight.style.left = `${left}px`;
    spotlight.style.top = `${top}px`;
    spotlight.style.width = `${Math.max(44, right - left)}px`;
    spotlight.style.height = `${Math.max(44, bottom - top)}px`;
    spotlight.hidden = false;
    this.#onboardingLayer.dataset.onboardingTarget = "true";
    const placement = rect.top + rect.height / 2 > window.innerHeight * 0.56 ? "top" : "bottom";
    this.#onboardingLayer.dataset.onboardingPlacement = placement;
    const targetOnRight = rect.left + rect.width / 2 >= window.innerWidth / 2;
    this.#onboardingLayer.dataset.onboardingGuideSide = targetOnRight ? "left" : "right";
    const guide = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-guide-stage");
    const dialog = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dialog");
    if (guide !== null && dialog !== null) {
      guide.style.removeProperty("height");
      const guideRect = guide.getBoundingClientRect();
      const guideHeight = guideRect.height || Math.min(window.innerHeight * 0.3, 220);
      const guideWidth = guideRect.width || Math.min(window.innerWidth * 0.49, 184);
      const dialogRect = dialog.getBoundingClientRect();
      const shellRect = this.#shell.getBoundingClientRect();
      const safeTop = 8;
      const targetOverlap = focusKind === "asset" ? 12 : focusKind === "landmark" ? 4 : -8;
      let guideTop = focusRect.top - guideHeight + targetOverlap;
      if (guideTop < safeTop) guideTop = focusRect.bottom + 10;
      if (placement === "bottom") {
        guideTop = Math.min(guideTop, dialogRect.top - guideHeight - 8);
      } else {
        guideTop = Math.max(guideTop, dialogRect.bottom + 8);
      }
      guideTop = Math.max(safeTop, Math.min(window.innerHeight - guideHeight - 8, guideTop));
      guide.style.top = `${Math.round(guideTop)}px`;
      guide.style.bottom = "auto";
      guide.style.left = `${Math.round(
        targetOnRight ? shellRect.left + 8 : shellRect.right - guideWidth - 8,
      )}px`;
      guide.style.right = "auto";
    }
    const model = onboardingUiModel(this.#state, this.#serverNow, this.#translator);
    this.#onboardingRuntime?.setPresentation({
      pose: model.pose,
      mirror: ONBOARDING_GUIDE_STAGING[model.step].mirror,
      reducedMotion: this.#state.settings.reducedMotion,
      compact: true,
    });
  }

  #installDebugApi(): void {
    if (!DEV_TOOLS_BUILD_ENABLED) return;
    window.__DIAMOND_TAPPER__ = {
      isReady: () => this.#ready,
      getState: () => (this.#state === null ? null : cloneState(this.#state)),
      getRenderer: () => {
        if (this.#runtime?.game.renderer.type === Phaser.CANVAS) return "canvas";
        if (this.#runtime?.game.renderer.type === Phaser.WEBGL) return "webgl";
        return "unknown";
      },
      getArenaSnapshot: () =>
        this.#relicDuelRuntime?.scene.debugSnapshot() ?? this.#arenaRuntime?.scene.debugSnapshot() ?? null,
      getOnboardingSnapshot: () => this.#onboardingRuntime?.scene.debugSnapshot() ?? null,
      getOnboardingPointerSnapshot: () => this.#onboardingPointerRuntime?.scene.debugSnapshot() ?? null,
      getPremiumShopSnapshot: () => this.#premiumRuntime?.scene.debugSnapshot() ?? null,
      getMakersClubSnapshot: () => this.#makersClubRuntime?.scene.debugSnapshot() ?? null,
      getTrophySnapshot: () => this.#trophyRuntime?.scene.debugSnapshot() ?? null,
      getWorkshopSnapshot: () => this.#runtime?.scene.getDiamondMotionSnapshot() ?? null,
      getAnalyticsSnapshot: () => this.#analytics.snapshot(),
      previewTrophyDailyState: (count) => {
        if (this.#state === null || this.#trophyRuntime === null) return;
        this.#trophyPreviewCount = count;
        this.#trophyRuntime.scene.setVisualState({
          ...this.#trophyVisualState(),
          segment: "today",
          dailyCompleteCount: Math.max(0, Math.min(3, count)),
          dailyClaimed: false,
        });
      },
      flushTaps: () => this.#flushTaps(),
      navigate: (section) => this.#navigate(section),
      seedScenario: (scenario) => this.#applyScenario(scenario),
      setOnline: async (online) => {
        this.#service.setOnline(online);
        if (online) await this.#retryNetwork();
        else this.#updateView({ navigation: false, details: false, sheet: false });
      },
    };
  }

  #clearOnboardingCutout(): void {
    // Closing during bootstrap is valid: the shell may not have been created.
    if (!this.#onboardingLayer || !this.#onboardingFocusClone) return;
    const dim = this.#onboardingLayer.querySelector<HTMLElement>(".onboarding-dim");
    if (dim !== null) {
      delete dim.dataset.cutoutKind;
      dim.style.removeProperty("--onboarding-cutout-image");
      dim.style.removeProperty("--onboarding-cutout-left");
      dim.style.removeProperty("--onboarding-cutout-top");
      dim.style.removeProperty("--onboarding-cutout-width");
      dim.style.removeProperty("--onboarding-cutout-height");
    }
    this.#onboardingFocusClone.style.removeProperty("--onboarding-focus-mask");
    this.#onboardingFocusClone.style.removeProperty("border-radius");
    this.#onboardingFocusClone.style.removeProperty("clip-path");
    this.#onboardingFocusClone.replaceChildren();
    delete this.#onboardingFocusClone.dataset.kind;
  }

  #upgradeCodeFrom(value: string | undefined): UpgradeCode {
    if (value === "precisionCutter" || value === "energyCapacity" || value === "energyRecovery") return value;
    return "mechanicalCutter";
  }

  #required<T extends Element>(selector: string): T {
    const element = this.#root.querySelector<T>(selector);
    if (element === null) throw new Error(`Missing required element: ${selector}`);
    return element;
  }
}
